import React, { useEffect, useLayoutEffect, useState, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { format, addDays, isSameDay, startOfDay, endOfDay } from "date-fns";
import { SchedulingToolUtils } from "../../utils/schedulingToolConfig";
import { useLegacySchMapping } from "../../config";
import { RootState, useAppDispatch } from "../../redux/store";
import { useSelector } from "react-redux";
import { SchedulesV3GroupByType, PublishedShiftForAssign, AddScheduleV2Request, ScheduleV3Group } from "../../types";
import { ScheduleSidebarTag, ScheduleSidebarSite } from "../../types/response/scheduleV3";
import { getSchedulesV3, publishSchedule, publishOpenShift, getPublishedShiftsForAssign, assignOpenShiftToEmployee, copySchedule, addScheduleV2, getScheduleSidebarList } from "../../redux/features/scheduleV3";
import { DateTimeUtil } from "../../utils";
import { ChevronLeft, ChevronRight, Loader2, Filter, Calendar, Send, CheckCircle, X, Menu, Plus, ChevronDown, Users, Copy, Eye, Printer, Download, FileText, Tag, Check, Info, AlertTriangle } from "lucide-react";
import { buildConflictIndex, getConflictSeverity } from "./schedulingToolConflictUtils";
import { PublishScheduleModal } from "./components/PublishScheduleModal";
import { ScheduleAuditDrawer } from "./components/AuditLog";
import ConflictListModal from "./components/ConflictListModal/ConflictListModal";
import type { PublishScheduleV3Request } from "../../types";
import { useNavigate } from "react-router";
import { setTopNavNameTitle } from "../../redux/features/mobileLayout";
import { phpClient } from "../../network";
import { buildScheduleV3Request, GetSchedulesV3Response } from "../../types";
import { ScheduleSidebarSortBy } from "../../types/request/scheduleV3";
import SchedulingToolWeeklyGrid from "./schedulingToolWeeklyGrid";
import { IncludeDeptType, ModuleName } from "../../enums";
import { getSitesV2, fetchScheduleAttendanceTags, getDepartments, getJobCodes, getShifts, getGroups, searchEmployee, getEmployees } from "../../redux/features/common/api";
import { toast } from "react-toastify";
import { cleanErrorMessage } from "../../utils/helper";
import CreateOpenShiftModal from "../../components/OpenShifts/CreateOpenShiftModal";
import { createOpenShifts } from "../../redux/features/scheduleV3";
import { getOpenShiftStatusCounts } from "../../redux/features/openShift";
import AsyncSelect from "react-select/async";
import Select from "react-select";
import ColorPickerWithPalette from "../../components/common/ColorPickerWithPalette";
import { DayPicker } from "react-day-picker";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { getSchedulingToolShiftColor, setSchedulingToolShiftColor } from "../../utils";
import { showScheduleResultToast } from "./schedulingToolToastUtils";

type SchedulingViewType = 'daily' | 'weekly' | 'monthly';

// Event display configuration types
type EventDisplayField = 
    | 'employeeName'
    | 'site'
    | 'attendanceTag'
    | 'hours'
    | 'time'
    | 'scheduleType'
    | 'department'
    | 'jobCode'
    | 'status'
    | 'notes'
    | 'groupName';

interface EventDisplayConfig {
    schedule: {
        card: EventDisplayField[];
        hover: EventDisplayField[];
    };
    timeoff: {
        card: EventDisplayField[];
        hover: EventDisplayField[];
    };
    unavailability: {
        card: EventDisplayField[];
        hover: EventDisplayField[];
    };
    openShift: {
        card: EventDisplayField[];
        hover: EventDisplayField[];
    };
}

const DEFAULT_DISPLAY_CONFIG: EventDisplayConfig = {
    schedule: {
        card: ['time', 'employeeName', 'scheduleType'],
        hover: ['employeeName', 'scheduleType', 'time', 'hours', 'site', 'department', 'notes'],
    },
    timeoff: {
        card: ['time', 'employeeName', 'scheduleType'],
        hover: ['employeeName', 'scheduleType', 'time', 'hours', 'notes'],
    },
    unavailability: {
        card: ['time', 'employeeName'],
        hover: ['employeeName', 'time', 'notes'],
    },
    openShift: {
        card: ['time', 'scheduleType'],
        hover: ['scheduleType', 'time', 'status', 'site', 'department', 'notes'],
    },
};

const ALL_FIELDS: { key: EventDisplayField; label: string }[] = [
    { key: 'employeeName', label: 'Employee Name' },
    { key: 'site', label: 'Site' },
    { key: 'attendanceTag', label: 'Attendance Tag' },
    { key: 'hours', label: 'Hours' },
    { key: 'time', label: 'Time' },
    { key: 'scheduleType', label: 'Schedule Type' },
    { key: 'department', label: 'Department' },
    { key: 'jobCode', label: 'Job Code' },
    { key: 'status', label: 'Status' },
    { key: 'notes', label: 'Notes' },
    { key: 'groupName', label: 'Group Name' },
];

// Validate display config to ensure at least one field is selected
const validateDisplayConfig = (config: any): EventDisplayConfig => {
    const validated: EventDisplayConfig = { ...DEFAULT_DISPLAY_CONFIG };
    
    const eventTypes: Array<keyof EventDisplayConfig> = ['schedule', 'timeoff', 'unavailability', 'openShift'];
    
    eventTypes.forEach(eventType => {
        if (config[eventType]) {
            // Validate card fields
            if (Array.isArray(config[eventType].card) && config[eventType].card.length > 0) {
                validated[eventType].card = config[eventType].card.filter((f: EventDisplayField) => 
                    ALL_FIELDS.some(field => field.key === f)
                );
            }
            // Ensure at least one field
            if (validated[eventType].card.length === 0) {
                validated[eventType].card = [DEFAULT_DISPLAY_CONFIG[eventType].card[0]];
            }
            
            // Validate hover fields
            if (Array.isArray(config[eventType].hover) && config[eventType].hover.length > 0) {
                validated[eventType].hover = config[eventType].hover.filter((f: EventDisplayField) => 
                    ALL_FIELDS.some(field => field.key === f)
                );
            }
            // Ensure at least one field
            if (validated[eventType].hover.length === 0) {
                validated[eventType].hover = [DEFAULT_DISPLAY_CONFIG[eventType].hover[0]];
            }
        }
    });
    
    return validated;
};

const SchedulingToolMain: React.FC = () => {
    const dispatch = useAppDispatch();
    const { groups, isLoading, filters, ui, statistics, sidebarLayout } = useSelector((state: RootState) => state.scheduleV3);
    const { attendanceTags, departments, jobCodes, sitesV2, shifts: allShifts, groups: groupsList, employeeList, isLoadingAttendanceTags } = useSelector((state: RootState) => state.common);
    
    // Debug: Log groups from Redux to see if ungrouped_dept is present
    useEffect(() => {
        console.log('Redux groups updated:', {
            count: groups.length,
            groupIds: groups.map(g => ({ id: g.group_id, name: g.group_name })),
            ungroupedDept: groups.find(g => g.group_id === 'ungrouped_dept')
        });
    }, [groups]);
    
    const [currentMonth, setCurrentMonth] = useState(() => new Date());
    const [currentWeek, setCurrentWeek] = useState(() => new Date());
    const [currentDay, setCurrentDay] = useState(() => new Date());
    const [viewType, setViewType] = useState<SchedulingViewType>(() => {
        const savedView = localStorage.getItem("scheduling_tool_view_type");
        return (savedView as SchedulingViewType) || 'weekly';
    });
    const [groupingType, setGroupingType] = useState<SchedulesV3GroupByType>(() => {
        const savedGrouping = localStorage.getItem("scheduling_tool_grouping");
        return (savedGrouping as SchedulesV3GroupByType) || 'employee';
    });
    const [sidebarSortBy, setSidebarSortBy] = useState<ScheduleSidebarSortBy>(() => {
        const saved = localStorage.getItem("scheduling_tool_sidebar_sort") as ScheduleSidebarSortBy | null;
        return saved || 'first_name_asc';
    });
    const [activeFilters, setActiveFilters] = useState(0);
    const [isOptionsOpen, setIsOptionsOpen] = useState(false);
    const [showSchedule, setShowSchedule] = useState(() => {
        const saved = localStorage.getItem("scheduling_tool_show_schedule");
        return saved !== null ? saved === 'true' : true;
    });
    const [showTimeoff, setShowTimeoff] = useState(() => {
        const saved = localStorage.getItem("scheduling_tool_show_timeoff");
        return saved !== null ? saved === 'true' : true;
    });
    const [showHoliday, setShowHoliday] = useState(() => {
        const saved = localStorage.getItem("scheduling_tool_show_holiday");
        return saved !== null ? saved === 'true' : true;
    });
    const [showUnavailability, setShowUnavailability] = useState(() => {
        const saved = localStorage.getItem("scheduling_tool_show_unavailability");
        return saved !== null ? saved === 'true' : true;
    });
    const [showOpenShifts, setShowOpenShifts] = useState(() => {
        const saved = localStorage.getItem("scheduling_tool_show_open_shifts");
        return saved !== null ? saved === 'true' : true;
    });
    const [includeDept, setIncludeDept] = useState<IncludeDeptType>(() => {
        const savedDeptView = localStorage.getItem("scheduling_tool_include_dept");
        return (savedDeptView as IncludeDeptType) || IncludeDeptType.MY_SCHEDULE;
    });
    const [showPublishConfirmation, setShowPublishConfirmation] = useState(false);
    const [publishType, setPublishType] = useState<'schedules' | 'open_shifts'>('schedules');
    const [isPublishing, setIsPublishing] = useState(false);
    const [showPublishSuccess, setShowPublishSuccess] = useState(false);
    const [publishMessage, setPublishMessage] = useState('');
    const [isDownloadingPDF, setIsDownloadingPDF] = useState(false);
    const [publishDateRange, setPublishDateRange] = useState({
        startDate: '',
        endDate: '',
    });
    const [showCreateOpenShiftModal, setShowCreateOpenShiftModal] = useState(false);
    const [isCreatingOpenShifts, setIsCreatingOpenShifts] = useState(false);
    const [isPrintDropdownOpen, setIsPrintDropdownOpen] = useState(false);
    const [showEmployeeMonthPicker, setShowEmployeeMonthPicker] = useState(false);
    const [selectedEmployeeForPDF, setSelectedEmployeeForPDF] = useState<string>('');
    const [selectedMonthForEmployeePDF, setSelectedMonthForEmployeePDF] = useState<Date>(new Date());
    const [showAssignShiftsModal, setShowAssignShiftsModal] = useState(false);
    const [publishedShifts, setPublishedShifts] = useState<PublishedShiftForAssign[]>([]);
    const [isLoadingShifts, setIsLoadingShifts] = useState(false);
    const [isAssigningShifts, setIsAssigningShifts] = useState(false);
    const [selectedAssignments, setSelectedAssignments] = useState<{ [shiftId: string]: string }>({});
    const [showFilterModal, setShowFilterModal] = useState(false);
    const [showConflictModal, setShowConflictModal] = useState(false);
    const [filterValues, setFilterValues] = useState({
        employeeIds: [] as string[],
        departmentCodes: [] as string[],
        siteIds: [] as string[],
        shiftIds: [] as string[],
        groupCodes: [] as string[],
        scheduleStartDate: null as Date | null,
        scheduleEndDate: null as Date | null,
    });
    // Applied filters - only updated when Apply button is clicked
    const [appliedFilters, setAppliedFilters] = useState({
        employeeIds: [] as string[],
        departmentCodes: [] as string[],
        siteIds: [] as string[],
        shiftIds: [] as string[],
        groupCodes: [] as string[],
        scheduleStartDate: null as Date | null,
        scheduleEndDate: null as Date | null,
    });
    const [selectedEmployees, setSelectedEmployees] = useState<any[]>([]);
    const [showCopyScheduleModal, setShowCopyScheduleModal] = useState(false);
    const [isCopyingSchedule, setIsCopyingSchedule] = useState(false);
    const [copyScheduleData, setCopyScheduleData] = useState({
        sourceType: 'current_view' as 'date_range' | 'current_view',
        sourceStartDate: '',
        sourceEndDate: '',
        destStartDate: '',
        clearExisting: false,
        selectedDepartments: [] as string[],
        selectedGroups: [] as string[],
    });
    const [isOpenShiftDropdownOpen, setIsOpenShiftDropdownOpen] = useState(false);
    const [isManageDropdownOpen, setIsManageDropdownOpen] = useState(false);
    const [showMassAssignmentModal, setShowMassAssignmentModal] = useState(false);
    const [isMassAssigning, setIsMassAssigning] = useState(false);
    const [showMobileActionsMenu, setShowMobileActionsMenu] = useState(false);
    const [showAuditLogsModal, setShowAuditLogsModal] = useState(false);
    const [showEventDisplayConfig, setShowEventDisplayConfig] = useState(false);
    const [eventDisplayConfig, setEventDisplayConfig] = useState<EventDisplayConfig>(() => {
        const saved = localStorage.getItem("scheduling_tool_event_display_config");
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                // Validate that at least one field is selected for each card
                const validated = validateDisplayConfig(parsed);
                return validated;
            } catch {
                return DEFAULT_DISPLAY_CONFIG;
            }
        }
        return DEFAULT_DISPLAY_CONFIG;
    });
    // Load saved color preference from shift_planner_settings
    const savedColor = getSchedulingToolShiftColor("");
    
    const [massAssignmentData, setMassAssignmentData] = useState({
        selectedEmployees: [] as any[],
        selectedGroup: '',
        selectedDepartment: '',
        onlyNotes: false,
        selectedShift: '',
        startDateTime: '',
        endDateTime: '',
        repeatFor: 'Days',
        numberOfDays: 1,
        showOptionalFields: false,
        site: '',
        department: '',
        jobCode: null as number | null,
        notes: '',
        shift_color: savedColor,
        attendanceTags: {} as Record<string, { value_code: string; value_text: string }[]>,
    });

    const navigate = useNavigate();
    const user = useSelector((state: RootState) => state.user.user);
    const { openShiftStatusCounts, isLoadingOpenShiftStatusCounts } = useSelector(
        (state: RootState) => state.openShift
    );
    const datePickerContainerRef = useRef<HTMLDivElement>(null);
    const dailyDatePickerAnchorRef = useRef<HTMLElement | null>(null);
    const [showDailyDatePicker, setShowDailyDatePicker] = useState(false);
    const [dailyDatePickerPosition, setDailyDatePickerPosition] = useState({ top: 0, left: 0 });
    const [dailyDatePickerMonth, setDailyDatePickerMonth] = useState<Date>(() => new Date());
    const [isAttendanceTagsDropdownOpen, setIsAttendanceTagsDropdownOpen] = useState(false);
    const [selectedTagCode, setSelectedTagCode] = useState<string | null>(() => {
        const saved = localStorage.getItem("scheduling_tool_selected_tag_code");
        return saved || null;
    });
    
    // Get scheduling configuration from user
    const schedulingConfig = SchedulingToolUtils.getSchedulingConfig(user);
    const allowCustomShift = !schedulingConfig.RESTRICT_TO_PREDEFINED_SHIFTS;
    const hasScheduleToolPrivilege = SchedulingToolUtils.hasScheduleToolPrivilege(user);

    // Custom setter that also saves to localStorage; normalizes sidebar sort when grouping changes
    const setGroupingTypeWithPersistence = (value: SchedulesV3GroupByType) => {
        const isEmployee = value === 'employee';
        const currentSortValidForEmployee = ['first_name_asc', 'first_name_desc', 'last_name_asc', 'last_name_desc'].includes(sidebarSortBy);
        const currentSortValidForOther = sidebarSortBy === 'asc' || sidebarSortBy === 'desc';
        if (isEmployee && !currentSortValidForEmployee) {
            setSidebarSortBy('first_name_asc');
            localStorage.setItem("scheduling_tool_sidebar_sort", 'first_name_asc');
        } else if (!isEmployee && !currentSortValidForOther) {
            setSidebarSortBy('asc');
            localStorage.setItem("scheduling_tool_sidebar_sort", 'asc');
        }
        if (value === 'tags') {
            setGroupingType(value);
            localStorage.setItem("scheduling_tool_grouping", value);
            if (!attendanceTags || attendanceTags.length === 0) {
                dispatch(fetchScheduleAttendanceTags());
            }
        } else {
            setGroupingType(value);
            localStorage.setItem("scheduling_tool_grouping", value);
            if (groupingType === 'tags') {
                setSelectedTagCode(null);
                localStorage.removeItem("scheduling_tool_selected_tag_code");
            }
        }
    };

    const setSidebarSortWithPersistence = (sortBy: ScheduleSidebarSortBy) => {
        setSidebarSortBy(sortBy);
        localStorage.setItem("scheduling_tool_sidebar_sort", sortBy);
    };

    // Handle tag selection (radio button - single selection)
    const handleTagToggle = (tagCode: string) => {
        console.log('handleTagToggle called with:', tagCode);
        setSelectedTagCode((prev) => {
            // If clicking the same tag, deselect it (set to null)
            const newSelection = prev === tagCode ? null : tagCode;
            console.log('New selectedTagCode:', newSelection);
            if (newSelection) {
                localStorage.setItem("scheduling_tool_selected_tag_code", newSelection);
            } else {
                localStorage.removeItem("scheduling_tool_selected_tag_code");
            }
            return newSelection;
        });
    };

    // Custom setter for view type that also saves to localStorage
    const setViewTypeWithPersistence = (value: SchedulingViewType) => {
        setViewType(value);
        localStorage.setItem("scheduling_tool_view_type", value);
    };

    // Custom setter for include dept that also saves to localStorage
    const setIncludeDeptWithPersistence = (value: IncludeDeptType) => {
        setIncludeDept(value);
        localStorage.setItem("scheduling_tool_include_dept", value);
    };

    // Save event display config to localStorage
    const saveEventDisplayConfig = (config: EventDisplayConfig) => {
        const validated = validateDisplayConfig(config);
        setEventDisplayConfig(validated);
        localStorage.setItem("scheduling_tool_event_display_config", JSON.stringify(validated));
    };

    // Update a specific event type's display config
    const updateEventDisplayConfig = (
        eventType: keyof EventDisplayConfig,
        displayType: 'card' | 'hover',
        field: EventDisplayField,
        checked: boolean
    ) => {
        const newConfig = { ...eventDisplayConfig };
        const currentFields = [...newConfig[eventType][displayType]];
        
        if (checked) {
            if (!currentFields.includes(field)) {
                currentFields.push(field);
            }
        } else {
            // Don't allow unchecking if it's the last field
            if (currentFields.length > 1) {
                newConfig[eventType][displayType] = currentFields.filter(f => f !== field);
            } else {
                toast.error("At least one field must be selected");
                return;
            }
        }
        
        newConfig[eventType][displayType] = currentFields;
        saveEventDisplayConfig(newConfig);
    };

    // Close dropdown when clicking outside (only for desktop dropdown, not mobile bottom sheet)
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            
            // Check if we're on mobile (window width < 768px)
            const isMobile = window.innerWidth < 768;
            
            // Check if click is inside mobile options menu (bottom sheet)
            const mobileOptionsMenu = document.querySelector('[data-mobile-options-menu]');
            if (mobileOptionsMenu && (mobileOptionsMenu.contains(target) || isMobile)) {
                // On mobile, only close via backdrop click (handled separately)
                return;
            }
            
            // Check if click is inside desktop options dropdown
            const desktopOptionsMenu = document.querySelector('[data-desktop-options-menu]');
            if (isOptionsOpen && desktopOptionsMenu && !desktopOptionsMenu.contains(target) && !isMobile) {
                setIsOptionsOpen(false);
            }
            
            // Check if click is inside open shift dropdown
            if (isOpenShiftDropdownOpen && !isMobile) {
                const openShiftDropdown = document.querySelector('[data-open-shift-dropdown]');
                const openShiftButton = document.querySelector('[data-open-shift-button]');
                // Don't close if clicking inside dropdown or on the button that opened it
                if (openShiftDropdown && !openShiftDropdown.contains(target) && 
                    openShiftButton && !openShiftButton.contains(target)) {
                    setIsOpenShiftDropdownOpen(false);
                }
            }
            
            // Check if click is inside manage dropdown
            if (isManageDropdownOpen && !isMobile) {
                const manageDropdown = document.querySelector('[data-manage-dropdown]');
                const manageButton = document.querySelector('[data-manage-button]');
                // Don't close if clicking inside dropdown or on the button that opened it
                if (manageDropdown && !manageDropdown.contains(target) && 
                    manageButton && !manageButton.contains(target)) {
                    setIsManageDropdownOpen(false);
                }
            }
            
            // Check if click is inside attendance tags dropdown
            if (isAttendanceTagsDropdownOpen) {
                const attendanceTagsDropdown = document.querySelector('[data-attendance-tags-dropdown]');
                const attendanceTagsButton = document.querySelector('[data-attendance-tags-button]');
                // Don't close if clicking inside dropdown or on the button that opened it
                if (attendanceTagsDropdown && !attendanceTagsDropdown.contains(target) && 
                    attendanceTagsButton && !attendanceTagsButton.contains(target)) {
                    setIsAttendanceTagsDropdownOpen(false);
                }
            }
            
            // Check if click is inside print dropdown
            if (isPrintDropdownOpen) {
                const printDropdown = document.querySelector('[data-print-dropdown]');
                const printButton = document.querySelector('[data-print-button]');
                // Don't close if clicking inside dropdown or on the button that opened it
                if (printDropdown && !printDropdown.contains(target) && 
                    printButton && !printButton.contains(target)) {
                    setIsPrintDropdownOpen(false);
                }
            }
        };

        // Only add listener for desktop, or use a delay for mobile to avoid immediate closure
        if (isOptionsOpen || isOpenShiftDropdownOpen || isManageDropdownOpen || isPrintDropdownOpen || isAttendanceTagsDropdownOpen) {
            const isMobile = window.innerWidth < 768;
            const delay = isMobile ? 300 : 0; // Longer delay for mobile to allow menu to render
            
            const timeoutId = setTimeout(() => {
                document.addEventListener("click", handleClickOutside, true);
            }, delay);

            return () => {
                clearTimeout(timeoutId);
                document.removeEventListener("click", handleClickOutside, true);
            };
        }
    }, [isOptionsOpen, isOpenShiftDropdownOpen, isManageDropdownOpen, isPrintDropdownOpen, isAttendanceTagsDropdownOpen]);

    // Close mobile menu on escape key
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && showMobileActionsMenu) {
                setShowMobileActionsMenu(false);
            }
        };

        if (showMobileActionsMenu) {
            document.addEventListener('keydown', handleEscape);
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [showMobileActionsMenu]);

    // Calculate active filter count
    useEffect(() => {
        let count = 0;
        if (appliedFilters.employeeIds.length > 0) count++;
        if (appliedFilters.departmentCodes.length > 0) count++;
        if (appliedFilters.siteIds.length > 0) count++;
        if (appliedFilters.shiftIds.length > 0) count++;
        if (appliedFilters.groupCodes.length > 0) count++;
        if (appliedFilters.scheduleStartDate || appliedFilters.scheduleEndDate) count++;
        setActiveFilters(count);
    }, [appliedFilters]);

    // Sync filterValues with appliedFilters when modal opens
    useEffect(() => {
        if (showFilterModal) {
            setFilterValues({
                employeeIds: [...appliedFilters.employeeIds],
                departmentCodes: [...appliedFilters.departmentCodes],
                siteIds: [...appliedFilters.siteIds],
                shiftIds: [...appliedFilters.shiftIds],
                groupCodes: [...appliedFilters.groupCodes],
                scheduleStartDate: appliedFilters.scheduleStartDate ? new Date(appliedFilters.scheduleStartDate) : null,
                scheduleEndDate: appliedFilters.scheduleEndDate ? new Date(appliedFilters.scheduleEndDate) : null,
            });
        }
    }, [showFilterModal, appliedFilters]);

    useLayoutEffect(() => {
        dispatch(setTopNavNameTitle("Scheduling Tool"));
    }, [dispatch]);

    // Fetch all common data when component mounts (departments, job codes, shifts, attendance tags)
    useEffect(() => {
        // Fetch attendance tags on mount so they're available for the Tags grouping view
        dispatch(fetchScheduleAttendanceTags());
        
        // Skip fetching other data if grouping by tags and no tag is selected
        // (no modals can be opened, so sites/departments/etc are not needed)
        if (groupingType === 'tags' && !selectedTagCode) {
            return;
        }
        
        // Fetch departments, job codes, shifts, groups
        dispatch(getDepartments());
        dispatch(getJobCodes());
        dispatch(getShifts());
        dispatch(getGroups());
    }, [dispatch, groupingType, selectedTagCode]);

    // Fetch schedule attendance tags when optional fields are opened in mass assignment modal
    useEffect(() => {
        if (massAssignmentData.showOptionalFields) {
            dispatch(fetchScheduleAttendanceTags());
        }
    }, [massAssignmentData.showOptionalFields, dispatch]);

    // Fetch employees ONCE when grouping by employee (before schedules are fetched)
    // This should happen only once per grouping session, not on every includeDept change
    const hasEmployeesFetchedRef = useRef<{ grouping: SchedulesV3GroupByType | null, includeDept: IncludeDeptType | null }>({
        grouping: null,
        includeDept: null
    });
    
    useEffect(() => {
        // Only fetch employees when grouping by employee
        if (groupingType === 'employee') {
            // When using API-driven sidebar layout, avoid legacy employee list fetch for weekly view
            const isSidebarLayoutEnabled = !useLegacySchMapping && viewType === 'weekly';
            if (isSidebarLayoutEnabled) {
                return;
            }

            // Determine if we need to fetch based on current state
            const currentKey = `${groupingType}-${includeDept}`;
            const lastKey = hasEmployeesFetchedRef.current.grouping && hasEmployeesFetchedRef.current.includeDept 
                ? `${hasEmployeesFetchedRef.current.grouping}-${hasEmployeesFetchedRef.current.includeDept}`
                : null;
            
            // Only fetch if this is a new combination of grouping + includeDept
            if (currentKey !== lastKey) {
                // Pass co_worker: true when viewing "My Department" to fetch only department colleagues
                const shouldFetchCoWorkers = includeDept === IncludeDeptType.MY_DEPARTMENT;
                
                console.log('Scheduling Tool: Fetching employees for employee grouping', {
                    groupingType,
                    includeDept,
                    co_worker: shouldFetchCoWorkers,
                    l_i_m_i_t: "500",
                    currentKey,
                    lastKey,
                    isFirstFetch: !hasEmployeesFetchedRef.current.grouping
                });
                
                dispatch(getEmployees({ co_worker: shouldFetchCoWorkers, l_i_m_i_t: "500", module_name: ModuleName.EMPLOYEE_PRIV_SCHEDULE_TOOL }));
                
                // Update the ref to track this fetch
                hasEmployeesFetchedRef.current = {
                    grouping: groupingType,
                    includeDept: includeDept
                };
            }
        } else {
            // Reset when switching away from employee grouping
            if (hasEmployeesFetchedRef.current.grouping === 'employee') {
                console.log('Scheduling Tool: Resetting employee fetch tracking (switched away from employee grouping)');
                hasEmployeesFetchedRef.current = {
                    grouping: null,
                    includeDept: null
                };
            }
        }
    }, [dispatch, groupingType, includeDept, viewType, useLegacySchMapping]);

    // Fetch sitesV2 when employeeId is available
    // Skip if grouping by tags and no tag is selected (no modals can be opened)
    const employeeId = localStorage.getItem("employee_id") || user?.os_emp_account_id?.toString() || "";
    useEffect(() => {
        // Skip fetching sites if grouping by tags and no tag is selected
        if (groupingType === 'tags' && !selectedTagCode) {
            return;
        }
        
        console.log('Scheduling Tool: Fetching sitesV2 for employee_id:', employeeId);
        
        if (employeeId) {
            dispatch(getSitesV2({ employee_id: employeeId }));
        }
    }, [dispatch, employeeId, groupingType, selectedTagCode]);

    const shouldUseSidebarLayout =
        !useLegacySchMapping &&
        viewType === 'weekly' &&
        (groupingType === 'employee' || groupingType === 'department' || groupingType === 'site' || groupingType === 'tags');
    
    console.log('shouldUseSidebarLayout calculation:', {
        useLegacySchMapping,
        viewType,
        groupingType,
        result: shouldUseSidebarLayout
    });
    
    const isSidebarLayoutLoading = shouldUseSidebarLayout && sidebarLayout?.isLoading;
    const hasSidebarLayout = shouldUseSidebarLayout && sidebarLayout?.items?.length > 0;
    const isSidebarLayoutBlocking = shouldUseSidebarLayout && !hasSidebarLayout && sidebarLayout?.isLoading;
    
    // Debug: Log sidebar layout status
    useEffect(() => {
        console.log('Sidebar layout status:', {
            shouldUseSidebarLayout,
            sidebarItemsCount: sidebarLayout?.items?.length || 0,
            sidebarGroupBy: sidebarLayout?.groupBy,
            sidebarIsLoading: sidebarLayout?.isLoading,
            hasSidebarLayout,
            currentGroupingType: groupingType
        });
    }, [shouldUseSidebarLayout, sidebarLayout, groupingType]);

    // Merge all employees/departments with schedule groups when grouping by employee/department
    // This ensures we show all employees/departments, even those without schedules
    const mergedGroups = useMemo(() => {
        console.log('=== mergedGroups calculation ===', {
            viewType,
            groupingType,
            totalGroups: groups.length,
            groupIds: groups.map(g => g.group_id),
            useLegacySchMapping,
        });
        
        // Extract holidays group to preserve it
        const holidaysGroup = groups.find((g) => g.group_id === "global_holiday");
        
        // For monthly view, skip employee/department mapping - just show data directly in calendar
        if (viewType === 'monthly') {
            return groups;
        }

        // If legacy mapping is disabled, rely on sidebar_list_v4 API for weekly layout
        if (
            !useLegacySchMapping &&
            viewType === 'weekly' &&
            (groupingType === 'employee' || groupingType === 'department' || groupingType === 'site' || groupingType === 'tags')
        ) {
            const sidebarItems = sidebarLayout?.items || [];
            const sidebarGroupBy = sidebarLayout?.groupBy;

            console.log('Sidebar layout check:', {
                sidebarItemsLength: sidebarItems.length,
                sidebarGroupBy,
                currentGroupingType: groupingType,
                matches: sidebarGroupBy === groupingType,
                willUseSidebarPath: sidebarItems.length > 0 && sidebarGroupBy === groupingType
            });

            // ALWAYS use sidebar path when sidebar layout is enabled (don't fall back to legacy)
            const scheduleGroupsMap = new Map(groups.map((group) => [group.group_id, group]));

            const layoutGroups = sidebarItems
                .flatMap((item) => {
                    // Handle employee items
                    if ('employee_id' in item) {
                        const groupId = item.employee_id?.toString();
                        if (!groupId) return [];

                        return [{
                            group_id: groupId,
                            group_name:
                                item.full_name ||
                                `${item.first_name || ''} ${item.last_name || ''}`.trim() ||
                                groupId,
                            dates: {},
                        }];
                    }

                    // Handle department items
                    if ('department_code' in item) {
                        const departmentCode = item.department_code?.toString();
                        if (!departmentCode) return [];

                        return [{
                            group_id: departmentCode,
                            group_name: item.department_name || departmentCode,
                            dates: {},
                        }];
                    }

                    // Handle site items
                    if ('site_id' in item) {
                        const siteItem = item as ScheduleSidebarSite;
                        // Schedules API uses site_code as group_id, not site_id
                        // Use site_code if available, otherwise fallback to site_id
                        const groupId = siteItem.site_code || siteItem.site_id?.toString();
                        if (!groupId) return [];

                        return [{
                            group_id: groupId,
                            group_name: siteItem.site_name || groupId,
                            dates: {},
                        }];
                    }

                    // Handle tag items
                    if ('tag_id' in item) {
                        const tagItem = item as ScheduleSidebarTag;
                        // When a tag is selected, sidebar returns tag with options array
                        // Each option represents a tag value that should be a separate row
                        if (tagItem.options && tagItem.options.length > 0) {
                            // Return array of groups, one for each option
                            return tagItem.options.map((option) => ({
                                group_id: option.value_code,
                                group_name: option.value_text,
                                dates: {},
                            }));
                        }
                        // Fallback: if no options, use tag_id as group_id
                        const tagId = tagItem.tag_id?.toString();
                        if (!tagId) return [];

                        return [{
                            group_id: tagId,
                            group_name: tagItem.tag_name || tagItem.tag_code || tagId,
                            dates: {},
                        }];
                    }

                    return [];
                })
                .filter((group): group is { group_id: string; group_name: string; dates: any } => Boolean(group));

            // Map schedule groups to layout groups by matching group_id
            // Always use sidebar names (from layoutGroups) as the source of truth
            // Empty groups (with no schedule data) are preserved to show empty rows
            const mappedLayoutGroups = layoutGroups.map((layoutGroup) => {
                const existingGroup = scheduleGroupsMap.get(layoutGroup.group_id);

                if (existingGroup) {
                    // Use sidebar name as source of truth, merge with schedule data
                    return {
                        ...existingGroup,
                        group_name: layoutGroup.group_name, // Always use sidebar name
                    };
                }

                // No schedule data - return layout group with empty dates object
                // This ensures empty rows are shown for sidebar items with no schedule data
                return {
                    ...layoutGroup,
                    dates: layoutGroup.dates || {}, // Ensure dates is at least an empty object
                };
            });

            // Check for ungrouped data groups (split by type: timeoff, unavailability, holiday)
            const ungroupedTimeoffGroup = groups.find(g => g.group_id === 'ungrouped_dept_timeoff');
            const ungroupedUnavailGroup = groups.find(g => g.group_id === 'ungrouped_dept_unavailability');
            const ungroupedHolidayGroup = groups.find(g => g.group_id === 'ungrouped_dept_holiday');
            const ungroupedOpenShiftsGroup =
                groups.find(g => g.group_id === 'ungrouped_dept_open_shifts') ||
                groups.find(g => g.group_id === 'ungrouped_emp_open_shifts');
            const openShiftsGroup = groups.find(g => g.group_id === 'open_shifts');
            
            // Check for ungrouped_site when grouping by site
            const ungroupedSiteGroup = groupingType === 'site' ? groups.find(g => g.group_id === 'ungrouped_site') : null;
            
            // Check for ungrouped_tags when grouping by tags
            const ungroupedTagsGroup = groupingType === 'tags' ? groups.find(g => g.group_id === 'ungrouped_tags') : null;
            
            console.log('Sidebar layout - checking for ungrouped groups:', {
                hasTimeoff: !!ungroupedTimeoffGroup,
                hasUnavailability: !!ungroupedUnavailGroup,
                hasHoliday: !!ungroupedHolidayGroup,
                hasOpenShifts: !!ungroupedOpenShiftsGroup,
                hasOpenShiftsStandalone: !!openShiftsGroup,
                hasUngroupedSite: !!ungroupedSiteGroup,
                hasUngroupedTags: !!ungroupedTagsGroup,
                groupingType,
                allGroupIds: groups.map(g => g.group_id)
            });
            
            // Add ungrouped groups at the end (each as a separate row)
            const ungroupedGroups: ScheduleV3Group[] = [];
            
            if (ungroupedTimeoffGroup) {
                ungroupedGroups.push(ungroupedTimeoffGroup);
            }
            
            if (ungroupedUnavailGroup) {
                ungroupedGroups.push(ungroupedUnavailGroup);
            }
            
            if (ungroupedHolidayGroup) {
                ungroupedGroups.push(ungroupedHolidayGroup);
            }

            if (ungroupedOpenShiftsGroup) {
                ungroupedGroups.push({
                    ...ungroupedOpenShiftsGroup,
                    group_name: "Open Shifts",
                });
            }

            if (openShiftsGroup) {
                ungroupedGroups.push({
                    ...openShiftsGroup,
                    group_name: "Open Shifts",
                });
            }
            
            // Add ungrouped_site when grouping by site
            if (ungroupedSiteGroup) {
                ungroupedGroups.push({
                    ...ungroupedSiteGroup,
                    group_name: "Unassigned Sites"
                });
            }
            
            // Add ungrouped_tags when grouping by tags
            if (ungroupedTagsGroup) {
                ungroupedGroups.push({
                    ...ungroupedTagsGroup,
                    group_name: "Unassigned Tags"
                });
            }
            
            console.log('Sidebar layout - returning with ungrouped groups:', {
                mappedCount: mappedLayoutGroups.length,
                ungroupedCount: ungroupedGroups.length,
                total: mappedLayoutGroups.length + ungroupedGroups.length,
                ungroupedGroupIds: ungroupedGroups.map(g => g.group_id)
            });
            
            // For tags grouping, collect timeoff from groups that don't match sidebar tag values
            // Timeoff doesn't have tag information, so it needs a separate "Timeoff" row
            if (groupingType === 'tags') {
                const timeoffGroup: ScheduleV3Group = {
                    group_id: 'timeoff',
                    group_name: 'Timeoff',
                    dates: {}
                };
                
                // Get all tag value codes from sidebar (for matching)
                const sidebarTagValueCodes = new Set(
                    mappedLayoutGroups.map(g => g.group_id)
                );
                
                groups.forEach(group => {
                    // Skip groups that match sidebar tag values, holidays, and ungrouped groups
                    if (group.group_id === "global_holiday" ||
                        group.group_id.startsWith('ungrouped_') ||
                        sidebarTagValueCodes.has(group.group_id)) {
                        return;
                    }
                    
                    // Collect timeoff from this group
                    Object.entries(group.dates || {}).forEach(([dateStr, dateData]) => {
                        if (dateData?.timeoff && dateData.timeoff.length > 0) {
                            if (!timeoffGroup.dates[dateStr]) {
                                timeoffGroup.dates[dateStr] = {
                                    schedules: [],
                                    timeoff: [],
                                    unavailability: [],
                                    holiday: [],
                                    open_shifts: []
                                };
                            }
                            timeoffGroup.dates[dateStr].timeoff.push(...dateData.timeoff);
                        }
                    });
                });
                
                // Add timeoff group if it has data
                if (Object.keys(timeoffGroup.dates).length > 0) {
                    ungroupedGroups.push(timeoffGroup);
                }
            }
            
            // For site grouping, collect timeoff from groups that don't match sidebar sites
            // Timeoff doesn't have site information, so it needs a separate "Timeoff" row
            if (groupingType === 'site') {
                const timeoffGroup: ScheduleV3Group = {
                    group_id: 'timeoff',
                    group_name: 'Timeoff',
                    dates: {}
                };
                
                // Get all site codes from sidebar (for matching)
                // Schedules API uses site_code as group_id, not site_id
                const sidebarSiteCodes = new Set(
                    sidebarItems
                        .filter((item): item is ScheduleSidebarSite => 'site_id' in item)
                        .map((item) => {
                            const siteItem = item as ScheduleSidebarSite;
                            return siteItem.site_code || siteItem.site_id?.toString();
                        })
                        .filter((code): code is string => Boolean(code))
                );
                
                console.log('Sidebar layout - site grouping - collecting timeoff:', {
                    allGroupIds: groups.map(g => g.group_id),
                    sidebarSiteCodes: Array.from(sidebarSiteCodes),
                    groupsToCheck: groups.filter(g => 
                        g.group_id !== "global_holiday" &&
                        g.group_id !== 'ungrouped_site' &&
                        !g.group_id.startsWith('ungrouped_') &&
                        !sidebarSiteCodes.has(g.group_id)
                    ).map(g => g.group_id)
                });
                
                groups.forEach(group => {
                    // Skip groups that match sidebar sites, holidays, and ungrouped groups
                    if (group.group_id === "global_holiday" ||
                        group.group_id === 'ungrouped_site' ||
                        group.group_id.startsWith('ungrouped_') ||
                        sidebarSiteCodes.has(group.group_id)) {
                        return;
                    }
                    
                    // Collect timeoff from this group
                    Object.entries(group.dates || {}).forEach(([dateStr, dateData]) => {
                        if (dateData?.timeoff && dateData.timeoff.length > 0) {
                            if (!timeoffGroup.dates[dateStr]) {
                                timeoffGroup.dates[dateStr] = {
                                    schedules: [],
                                    timeoff: [],
                                    unavailability: [],
                                    holiday: [],
                                    open_shifts: []
                                };
                            }
                            timeoffGroup.dates[dateStr].timeoff.push(...dateData.timeoff);
                            console.log(`Sidebar layout - site grouping - collected ${dateData.timeoff.length} timeoff items from group ${group.group_id} for date ${dateStr}`);
                        }
                    });
                });
                
                console.log('Sidebar layout - site grouping - timeoff group created:', {
                    hasTimeoff: Object.keys(timeoffGroup.dates).length > 0,
                    datesWithTimeoff: Object.keys(timeoffGroup.dates),
                    totalTimeoffItems: Object.values(timeoffGroup.dates).reduce((sum, d) => sum + (d.timeoff?.length || 0), 0)
                });
                
                // Add timeoff group if it has data
                if (Object.keys(timeoffGroup.dates).length > 0) {
                    ungroupedGroups.push(timeoffGroup);
                    console.log('Sidebar layout - site grouping - added timeoff group to ungroupedGroups');
                } else {
                    console.log('Sidebar layout - site grouping - timeoff group has no data, not adding');
                }
            }
            
            // For department grouping, collect timeoff from groups that don't match sidebar departments
            // Timeoff doesn't have department information, so it needs a separate "Timeoff" row
            if (groupingType === 'department') {
                const timeoffGroup: ScheduleV3Group = {
                    group_id: 'timeoff',
                    group_name: 'Timeoff',
                    dates: {}
                };
                
                // Get all department codes from sidebar (for matching)
                const sidebarDepartmentCodes = new Set(
                    mappedLayoutGroups.map(g => g.group_id)
                );
                
                groups.forEach(group => {
                    // Skip groups that match sidebar departments, holidays, and ungrouped groups
                    if (group.group_id === "global_holiday" ||
                        group.group_id.startsWith('ungrouped_') ||
                        sidebarDepartmentCodes.has(group.group_id)) {
                        return;
                    }
                    
                    // Collect timeoff from this group
                    Object.entries(group.dates || {}).forEach(([dateStr, dateData]) => {
                        if (dateData?.timeoff && dateData.timeoff.length > 0) {
                            if (!timeoffGroup.dates[dateStr]) {
                                timeoffGroup.dates[dateStr] = {
                                    schedules: [],
                                    timeoff: [],
                                    unavailability: [],
                                    holiday: [],
                                    open_shifts: []
                                };
                            }
                            timeoffGroup.dates[dateStr].timeoff.push(...dateData.timeoff);
                        }
                    });
                });
                
                // Add timeoff group if it has data
                if (Object.keys(timeoffGroup.dates).length > 0) {
                    ungroupedGroups.push(timeoffGroup);
                }
            }
            
            // For employee grouping, collect timeoff from groups that don't match sidebar employees
            // Timeoff doesn't have employee information, so it needs a separate "Timeoff" row
            if (groupingType === 'employee') {
                const timeoffGroup: ScheduleV3Group = {
                    group_id: 'timeoff',
                    group_name: 'Timeoff',
                    dates: {}
                };
                
                // Get all employee IDs from sidebar (for matching)
                const sidebarEmployeeIds = new Set(
                    mappedLayoutGroups.map(g => g.group_id)
                );
                
                groups.forEach(group => {
                    // Skip groups that match sidebar employees, holidays, and ungrouped groups
                    if (group.group_id === "global_holiday" ||
                        group.group_id.startsWith('ungrouped_') ||
                        sidebarEmployeeIds.has(group.group_id)) {
                        return;
                    }
                    
                    // Collect timeoff from this group
                    Object.entries(group.dates || {}).forEach(([dateStr, dateData]) => {
                        if (dateData?.timeoff && dateData.timeoff.length > 0) {
                            if (!timeoffGroup.dates[dateStr]) {
                                timeoffGroup.dates[dateStr] = {
                                    schedules: [],
                                    timeoff: [],
                                    unavailability: [],
                                    holiday: [],
                                    open_shifts: []
                                };
                            }
                            timeoffGroup.dates[dateStr].timeoff.push(...dateData.timeoff);
                        }
                    });
                });
                
                // Add timeoff group if it has data
                if (Object.keys(timeoffGroup.dates).length > 0) {
                    ungroupedGroups.push(timeoffGroup);
                }
            }
            
            // Always include global_holiday group if it exists (at the beginning)
            if (holidaysGroup) {
                return [holidaysGroup, ...mappedLayoutGroups, ...ungroupedGroups];
            }
            return [...mappedLayoutGroups, ...ungroupedGroups];
        }

        // Check if any filter is applied (use appliedFilters, not filterValues)
        const hasAnyFilter = 
            appliedFilters.employeeIds.length > 0 ||
            appliedFilters.departmentCodes.length > 0 ||
            appliedFilters.siteIds.length > 0 ||
            appliedFilters.shiftIds.length > 0 ||
            appliedFilters.groupCodes.length > 0 ||
            appliedFilters.scheduleStartDate !== null ||
            appliedFilters.scheduleEndDate !== null;

        // Handle employee grouping
        if (groupingType === 'employee') {
            // If employee filter is applied (specific employees selected), just show those employees
            if (appliedFilters.employeeIds.length > 0) {
                // Map employee names for display
                if (employeeList && employeeList.length > 0) {
                    const employeeMap = new Map(
                        employeeList.map(emp => [
                            emp.employee_id?.toString() || '',
                            emp.full_name || `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || emp.employee_id?.toString() || ''
                        ])
                    );
                    
                    // Create a map of existing groups by employee ID
                    const existingGroupsMap = new Map(
                        groups.map(group => [group.group_id, group])
                    );
                    
                    // Create groups for selected employees, merging with schedule data if available
                    const selectedEmployeeGroups = appliedFilters.employeeIds.map(employeeId => {
                        const existingGroup = existingGroupsMap.get(employeeId);
                        const employeeName = employeeMap.get(employeeId) || employeeId;
                        
                        if (existingGroup) {
                            return {
                                ...existingGroup,
                                group_name: employeeName
                            };
                        }
                        
                        return {
                            group_id: employeeId,
                            group_name: employeeName,
                            dates: {}
                        };
                    });
                    
                    // Always include holidays group if it exists
                    if (holidaysGroup) {
                        return [holidaysGroup, ...selectedEmployeeGroups];
                    }
                    return selectedEmployeeGroups;
                }
                // Always include holidays group if it exists
                if (holidaysGroup) {
                    return [holidaysGroup, ...groups];
                }
                return groups;
            }
            
            // If other filters are applied (department, site, etc.), merge with employee list but filter by applicable criteria
            if (hasAnyFilter) {
                // If no employees loaded yet, return the schedule groups as-is
                if (!employeeList || employeeList.length === 0) {
                    // Always include holidays group if it exists
                    if (holidaysGroup) {
                        const otherGroups = groups.filter(g => g.group_id !== "global_holiday");
                        return [holidaysGroup, ...otherGroups];
                    }
                    return groups;
                }
                
                // Filter employees based on applicable filters (department, site, etc.)
                let filteredEmployeeList = employeeList;
                
                // Filter by department if department filter is applied
                if (appliedFilters.departmentCodes.length > 0) {
                    filteredEmployeeList = filteredEmployeeList.filter(employee => {
                        const employeeDeptCode = employee.department?.department_code?.toString() || '';
                        return appliedFilters.departmentCodes.includes(employeeDeptCode);
                    });
                    console.log('Filtered employees by department:', {
                        departmentCodes: appliedFilters.departmentCodes,
                        totalEmployees: employeeList.length,
                        filteredEmployees: filteredEmployeeList.length
                    });
                }
                
                // Filter by site if site filter is applied
                // Note: Employee type may not have site information, so we check if it exists
                if (appliedFilters.siteIds.length > 0) {
                    filteredEmployeeList = filteredEmployeeList.filter(employee => {
                        // Check if employee has site information (may not be available in Employee type)
                        const employeeSiteId = (employee as any).default_site_id?.toString() || 
                                             (employee as any).site_id?.toString() || 
                                             (employee as any).site?.site_id?.toString() || '';
                        // If no site info available, skip this filter for this employee
                        if (!employeeSiteId) {
                            return false; // Exclude employees without site info when site filter is applied
                        }
                        return appliedFilters.siteIds.includes(employeeSiteId);
                    });
                    console.log('Filtered employees by site:', {
                        siteIds: appliedFilters.siteIds,
                        totalEmployees: employeeList.length,
                        filteredEmployees: filteredEmployeeList.length
                    });
                }
                
                // Filter by includeDept setting
                if (includeDept === IncludeDeptType.MY_SCHEDULE && user?.os_emp_account_id) {
                    filteredEmployeeList = filteredEmployeeList.filter(employee => 
                        employee.employee_id === user.os_emp_account_id?.toString()
                    );
                }
                
                // Create a map of existing groups by employee ID
                const existingGroupsMap = new Map(
                    groups.map(group => [group.group_id, group])
                );
                
                // Create groups for all filtered employees, merging with schedule data if available
                const employeeMap = new Map(
                    filteredEmployeeList.map(emp => [
                        emp.employee_id?.toString() || '',
                        emp.full_name || `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || emp.employee_id?.toString() || ''
                    ])
                );
                
                const allEmployeeGroups = filteredEmployeeList.map(employee => {
                    const employeeId = employee.employee_id.toString();
                    const existingGroup = existingGroupsMap.get(employeeId);
                    const employeeName = employeeMap.get(employeeId) || employeeId;
                    
                    if (existingGroup) {
                        return {
                            ...existingGroup,
                            group_name: employeeName
                        };
                    }
                    
                    return {
                        group_id: employeeId,
                        group_name: employeeName,
                        dates: {}
                    };
                });
                
                    console.log('Merged groups with filters applied:', {
                        totalGroups: allEmployeeGroups.length,
                        groupsWithSchedules: groups.length,
                        groupsWithoutSchedules: allEmployeeGroups.length - groups.length,
                        filters: {
                            departments: appliedFilters.departmentCodes,
                            sites: appliedFilters.siteIds
                        }
                    });
                
                // Always include holidays group if it exists
                if (holidaysGroup) {
                    return [holidaysGroup, ...allEmployeeGroups];
                }
                return allEmployeeGroups;
            }

            // If no employees loaded yet, return the schedule groups as-is (with holidays)
            if (!employeeList || employeeList.length === 0) {
                // Always include holidays group if it exists
                if (holidaysGroup) {
                    const otherGroups = groups.filter(g => g.group_id !== "global_holiday");
                    return [holidaysGroup, ...otherGroups];
                }
                return groups;
            }

            console.log('Merging employee list with schedule groups', {
                totalEmployees: employeeList.length,
                scheduleGroups: groups.length,
                includeDept,
                userDepartment: user?.department_code
            });

            // Filter employees based on includeDept setting
            let filteredEmployeeList = employeeList;
            
            if (includeDept === IncludeDeptType.MY_SCHEDULE && user?.os_emp_account_id) {
                // Show only the current user
                filteredEmployeeList = employeeList.filter(employee => 
                    employee.employee_id === user.os_emp_account_id?.toString()
                );
                console.log('Filtered employees to current user only:', {
                    userId: user.os_emp_account_id,
                    totalEmployees: employeeList.length,
                    filteredEmployees: filteredEmployeeList.length
                });
            }
            // For MY_DEPARTMENT: API already returns only department employees (co_worker: true)
            // For ALL: API returns all employees
            // No additional filtering needed for these cases

            // Create a map of existing groups by employee ID
            const existingGroupsMap = new Map(
                groups.map(group => [group.group_id, group])
            );

            // Create groups for all employees (or filtered employees)
            const allEmployeeGroups = filteredEmployeeList.map(employee => {
                const employeeId = employee.employee_id.toString();
                const existingGroup = existingGroupsMap.get(employeeId);

                // If employee has schedule data, use the existing group
                if (existingGroup) {
                    return existingGroup;
                }

                // Otherwise, create a placeholder group for employee without schedules
                return {
                    group_id: employeeId,
                    group_name: employee.full_name || `${employee.first_name} ${employee.last_name}`,
                    dates: {}
                };
            });

            console.log('Merged groups created:', {
                totalGroups: allEmployeeGroups.length,
                groupsWithSchedules: groups.length,
                groupsWithoutSchedules: allEmployeeGroups.length - groups.length
            });

            // Always include holidays group if it exists
            if (holidaysGroup) {
                return [holidaysGroup, ...allEmployeeGroups];
            }
            return allEmployeeGroups;
        }

        // Handle department grouping
        if (groupingType === 'department') {
            // If no departments loaded yet, return the schedule groups as-is
            if (!departments || departments.length === 0) {
                return groups;
            }

            // Filter departments based on applicable filters (department, site, etc.)
            let filteredDepartmentList = departments;

            // Filter by department if department filter is applied
            if (appliedFilters.departmentCodes.length > 0) {
                filteredDepartmentList = filteredDepartmentList.filter(dept => {
                    const deptCode = (dept.department_code || dept.code || '').toString();
                    return appliedFilters.departmentCodes.includes(deptCode);
                });
                console.log('Filtered departments by department filter:', {
                    departmentCodes: appliedFilters.departmentCodes,
                    totalDepartments: departments.length,
                    filteredDepartments: filteredDepartmentList.length
                });
            }

            // Filter by includeDept setting
            if (includeDept === IncludeDeptType.MY_SCHEDULE && user?.department_code) {
                // Show only the current user's department (only my schedules)
                filteredDepartmentList = filteredDepartmentList.filter(dept => 
                    (dept.department_code || dept.code) === user.department_code
                );
                console.log('Filtered departments to user department only (MY_SCHEDULE):', {
                    userDepartment: user.department_code,
                    totalDepartments: departments.length,
                    filteredDepartments: filteredDepartmentList.length
                });
            } else if (includeDept === IncludeDeptType.MY_DEPARTMENT && user?.department_code) {
                // Show only the current user's department (all schedules in my department)
                filteredDepartmentList = filteredDepartmentList.filter(dept => 
                    (dept.department_code || dept.code) === user.department_code
                );
                console.log('Filtered departments to user department only (MY_DEPARTMENT):', {
                    userDepartment: user.department_code,
                    totalDepartments: departments.length,
                    filteredDepartments: filteredDepartmentList.length
                });
            }

            // Create a map of existing groups by department code
            const existingGroupsMap = new Map(
                groups.map(group => [group.group_id, group])
            );

            // Create groups for all filtered departments, merging with schedule data if available
            const departmentMap = new Map(
                filteredDepartmentList.map(dept => [
                    (dept.department_code || dept.code || '').toString(),
                    dept.department_name || dept.name || ''
                ])
            );

            const allDepartmentGroups = filteredDepartmentList.map(dept => {
                const departmentCode = (dept.department_code || dept.code || '').toString();
                const departmentName = departmentMap.get(departmentCode) || departmentCode || 'Unknown Department';
                const existingGroup = existingGroupsMap.get(departmentCode);

                // If department has schedule data, ensure the group displays the department name
                if (existingGroup) {
                    return {
                        ...existingGroup,
                        group_name: departmentName
                    };
                }

                // Otherwise, create a placeholder group for department without schedules
                return {
                    group_id: departmentCode,
                    group_name: departmentName,
                    dates: {}
                };
            });

            console.log('Merged department groups created:', {
                totalGroups: allDepartmentGroups.length,
                groupsWithSchedules: groups.length,
                groupsWithoutSchedules: allDepartmentGroups.length - groups.length,
                filters: {
                    departments: appliedFilters.departmentCodes
                }
            });

            // Check for ungrouped data groups (split by type: timeoff, unavailability, holiday)
            const ungroupedTimeoffGroup = groups.find(g => g.group_id === 'ungrouped_dept_timeoff');
            const ungroupedUnavailGroup = groups.find(g => g.group_id === 'ungrouped_dept_unavailability');
            const ungroupedHolidayGroup = groups.find(g => g.group_id === 'ungrouped_dept_holiday');
            
            console.log('Department grouping - checking for ungrouped groups:', {
                hasTimeoff: !!ungroupedTimeoffGroup,
                hasUnavailability: !!ungroupedUnavailGroup,
                hasHoliday: !!ungroupedHolidayGroup,
                allGroupIds: groups.map(g => g.group_id)
            });
            
            // Add ungrouped groups at the end (each as a separate row)
            if (ungroupedTimeoffGroup) {
                allDepartmentGroups.push(ungroupedTimeoffGroup);
            }
            
            if (ungroupedUnavailGroup) {
                allDepartmentGroups.push(ungroupedUnavailGroup);
            }
            
            if (ungroupedHolidayGroup) {
                allDepartmentGroups.push(ungroupedHolidayGroup);
            }

            // Always include global_holiday group if it exists (at the beginning)
            if (holidaysGroup) {
                return [holidaysGroup, ...allDepartmentGroups];
            }
            return allDepartmentGroups;
        }

        // Handle site grouping - use sidebar API
        if (groupingType === 'site') {
            // Always find ungrouped_site group first (regardless of sidebar)
            const ungroupedSiteGroup = groups.find(group => group.group_id === 'ungrouped_site');
            
            console.log('Site grouping - initial check:', {
                groupingType,
                viewType,
                useLegacySchMapping,
                sidebarLayoutAvailable: !!sidebarLayout,
                sidebarGroupBy: sidebarLayout?.groupBy,
                allGroupIds: groups.map(g => g.group_id),
                foundUngroupedSite: !!ungroupedSiteGroup
            });
            
            // Use sidebar API for site grouping
            if (
                !useLegacySchMapping &&
                viewType === 'weekly' &&
                sidebarLayout?.groupBy === 'site'
            ) {
                const sidebarItems = sidebarLayout?.items || [];
                const scheduleGroupsMap = new Map(groups.map((group) => [group.group_id, group]));

                // Map sidebar items to groups
                const layoutGroups = sidebarItems
                    .map((item) => {
                        if ('site_id' in item) {
                            const siteItem = item as ScheduleSidebarSite;
                            // Schedules API uses site_code as group_id, not site_id
                            const groupId = siteItem.site_code || siteItem.site_id?.toString();
                            if (!groupId) return null;

                            const existingGroup = scheduleGroupsMap.get(groupId);
                            return {
                                group_id: groupId,
                                group_name: siteItem.site_name || groupId,
                                dates: existingGroup?.dates || {},
                            };
                        }
                        return null;
                    })
                    .filter((group): group is ScheduleV3Group => Boolean(group));

                // Find unassigned sites (sites in groups but not in sidebar)
                // Use site_code for matching since schedules API uses site_code as group_id
                const sidebarSiteCodes = new Set(
                    sidebarItems
                        .filter((item): item is ScheduleSidebarSite => 'site_id' in item)
                        .map((item) => item.site_code || item.site_id?.toString())
                        .filter((code): code is string => Boolean(code))
                );

                console.log('Site grouping - checking for ungrouped_site (sidebar path):', {
                    allGroupIds: groups.map(g => g.group_id),
                    foundUngroupedSite: !!ungroupedSiteGroup,
                    ungroupedSiteGroup: ungroupedSiteGroup ? {
                        group_id: ungroupedSiteGroup.group_id,
                        group_name: ungroupedSiteGroup.group_name,
                        datesCount: Object.keys(ungroupedSiteGroup.dates || {}).length
                    } : null
                });
                
                // Collect timeoff from groups that don't match sidebar sites
                // Timeoff doesn't have site information, so it needs a separate "Timeoff" row
                const timeoffGroup: ScheduleV3Group = {
                    group_id: 'timeoff',
                    group_name: 'Timeoff',
                    dates: {}
                };
                
                console.log('Site grouping - collecting timeoff from non-matching groups:', {
                    allGroupIds: groups.map(g => g.group_id),
                    sidebarSiteCodes: Array.from(sidebarSiteCodes),
                    groupsToCheck: groups.filter(g => 
                        g.group_id !== "global_holiday" &&
                        g.group_id !== 'ungrouped_site' &&
                        !g.group_id.startsWith('ungrouped_') &&
                        !sidebarSiteCodes.has(g.group_id)
                    ).map(g => g.group_id)
                });
                
                groups.forEach(group => {
                    // Skip groups that match sidebar sites, holidays, and ungrouped groups
                    if (group.group_id === "global_holiday" ||
                        group.group_id === 'ungrouped_site' ||
                        group.group_id.startsWith('ungrouped_') ||
                        sidebarSiteCodes.has(group.group_id)) {
                        return;
                    }
                    
                    // Collect timeoff from this group
                    Object.entries(group.dates || {}).forEach(([dateStr, dateData]) => {
                        if (dateData?.timeoff && dateData.timeoff.length > 0) {
                            if (!timeoffGroup.dates[dateStr]) {
                                timeoffGroup.dates[dateStr] = {
                                    schedules: [],
                                    timeoff: [],
                                    unavailability: [],
                                    holiday: [],
                                    open_shifts: []
                                };
                            }
                            timeoffGroup.dates[dateStr].timeoff.push(...dateData.timeoff);
                            console.log(`Site grouping - collected ${dateData.timeoff.length} timeoff items from group ${group.group_id} for date ${dateStr}`);
                        }
                    });
                });
                
                console.log('Site grouping - timeoff group created:', {
                    hasTimeoff: Object.keys(timeoffGroup.dates).length > 0,
                    datesWithTimeoff: Object.keys(timeoffGroup.dates),
                    totalTimeoffItems: Object.values(timeoffGroup.dates).reduce((sum, d) => sum + (d.timeoff?.length || 0), 0)
                });
                
                const unassignedSiteGroups = groups
                    .filter(group => {
                        // Exclude holidays and ungrouped groups (ungrouped_site is handled separately)
                        if (group.group_id === "global_holiday" ||
                            group.group_id === 'ungrouped_site' ||
                            group.group_id.startsWith('ungrouped_') ||
                            sidebarSiteCodes.has(group.group_id)) {
                            return false;
                        }
                        
                        // Exclude groups that only have timeoff (no schedules, unavailability, holidays, or open_shifts)
                        // These groups' timeoff is already collected in timeoffGroup
                        const hasSchedules = Object.values(group.dates || {}).some(d => 
                            d.schedules && d.schedules.length > 0
                        );
                        const hasUnavailability = Object.values(group.dates || {}).some(d => 
                            d.unavailability && d.unavailability.length > 0
                        );
                        const hasHolidays = Object.values(group.dates || {}).some(d => 
                            d.holiday && d.holiday.length > 0
                        );
                        const hasOpenShifts = Object.values(group.dates || {}).some(d => 
                            d.open_shifts && d.open_shifts.length > 0
                        );
                        
                        // Only include if it has schedules, unavailability, holidays, or open_shifts
                        // (timeoff-only groups are handled separately)
                        return hasSchedules || hasUnavailability || hasHolidays || hasOpenShifts;
                    })
                    .map(group => ({
                        ...group,
                        group_name: `Unassigned - ${group.group_name}`
                    }));

                // Prepare final groups array: sidebar groups, then unassigned groups, then timeoff, then ungrouped_site
                const finalGroups = [...layoutGroups, ...unassignedSiteGroups];
                
                // Add timeoff group if it has data
                if (Object.keys(timeoffGroup.dates).length > 0) {
                    finalGroups.push(timeoffGroup);
                    console.log('Site grouping - added timeoff group to finalGroups:', {
                        group_id: timeoffGroup.group_id,
                        group_name: timeoffGroup.group_name,
                        datesCount: Object.keys(timeoffGroup.dates).length,
                        finalGroupsLength: finalGroups.length
                    });
                } else {
                    console.log('Site grouping - timeoff group has no data, not adding to finalGroups');
                }
                
                // Add ungrouped_site as "Unassigned Sites" - append to sidebar groups (like unassigned)
                if (ungroupedSiteGroup) {
                    finalGroups.push({
                        ...ungroupedSiteGroup,
                        group_name: "Unassigned Sites"
                    });
                    console.log('Added ungrouped_site to finalGroups:', {
                        group_id: ungroupedSiteGroup.group_id,
                        group_name: "Unassigned Sites",
                        datesCount: Object.keys(ungroupedSiteGroup.dates || {}).length,
                        finalGroupsLength: finalGroups.length
                    });
                } else {
                    console.log('ungrouped_site group not found in groups array');
                }
                
                // Verify it's actually in finalGroups before returning
                const ungroupedInFinal = finalGroups.find(g => g.group_id === 'ungrouped_site');
                console.log('Site grouping - verification before return:', {
                    ungroupedInFinal: !!ungroupedInFinal,
                    finalGroupsLength: finalGroups.length,
                    finalGroupIds: finalGroups.map(g => g.group_id)
                });

                // Always include global_holiday group if it exists (at the beginning)
                const resultGroups = holidaysGroup ? [holidaysGroup, ...finalGroups] : finalGroups;
                
                // Final verification before returning
                const ungroupedInResult = resultGroups.find(g => g.group_id === 'ungrouped_site');
                const timeoffInResult = resultGroups.find(g => g.group_id === 'timeoff');
                console.log('Site grouping - final result before return:', {
                    timeoffInResult: !!timeoffInResult,
                    timeoffGroupData: timeoffInResult ? {
                        datesCount: Object.keys(timeoffInResult.dates || {}).length,
                        totalTimeoff: Object.values(timeoffInResult.dates || {}).reduce((sum, d) => sum + (d.timeoff?.length || 0), 0)
                    } : null,
                    sidebarGroups: layoutGroups.length,
                    unassignedGroups: unassignedSiteGroups.length,
                    hasUngroupedSite: !!ungroupedSiteGroup,
                    totalGroups: resultGroups.length,
                    finalGroupIds: resultGroups.map(g => g.group_id),
                    ungroupedInResult: !!ungroupedInResult,
                    ungroupedIndex: ungroupedInResult ? resultGroups.indexOf(ungroupedInResult) : -1
                });
                
                return resultGroups;
            }

            // Fallback: if sidebar not available, return groups as-is
            // But still handle ungrouped_site specially
            // Use the ungroupedSiteGroup found at the beginning of the site grouping block
            const otherGroups = groups.filter(g => 
                g.group_id !== "global_holiday" && 
                g.group_id !== 'ungrouped_site'
            );
            
            const finalGroups = [...otherGroups];
            if (ungroupedSiteGroup) {
                finalGroups.push({
                    ...ungroupedSiteGroup,
                    group_name: "Unassigned Sites"
                });
                console.log('Added ungrouped_site to finalGroups (fallback path)');
            } else {
                console.log('ungrouped_site not found in fallback path, groups:', groups.map(g => g.group_id));
            }
            
            console.log('Site grouping - fallback result:', {
                totalGroups: finalGroups.length,
                groupIds: finalGroups.map(g => g.group_id)
            });
            
            if (holidaysGroup) {
                return [holidaysGroup, ...finalGroups];
            }
            return finalGroups;
        }

        // Handle tags grouping - use sidebar API
        if (groupingType === 'tags') {
            // Debug: Log state for tags grouping
            console.log('Tags grouping - entry point:', {
                groupingType,
                useLegacySchMapping,
                viewType,
                sidebarGroupBy: sidebarLayout?.groupBy,
                selectedTagCode,
                attendanceTagsCount: attendanceTags?.length || 0,
                attendanceTagsCodes: attendanceTags?.map((t: any) => t.field_code) || []
            });
            
            // Use sidebar API for tags grouping
            if (
                !useLegacySchMapping &&
                viewType === 'weekly' &&
                sidebarLayout?.groupBy === 'tags'
            ) {
                const sidebarItems = sidebarLayout?.items || [];
                
                // Check if a specific tag is selected - if so, show rows for each tag value
                console.log('Tags grouping - checking for tag value rows:', {
                    selectedTagCode,
                    hasAttendanceTags: attendanceTags && attendanceTags.length > 0,
                    attendanceTagsAll: attendanceTags,
                    selectedTagFromList: attendanceTags?.find((t: any) => t.field_code === selectedTagCode)
                });
                
                if (selectedTagCode && attendanceTags && attendanceTags.length > 0) {
                    // Find the selected tag definition from attendanceTags
                    const selectedTag = attendanceTags.find(
                        (tag: any) => tag.field_code === selectedTagCode
                    );
                    
                    console.log('Tags grouping - selectedTag details:', {
                        selectedTagCode,
                        selectedTag,
                        field_json: selectedTag?.field_json,
                        field_type: selectedTag?.field_type,
                        field_values: selectedTag?.field_values
                    });
                    
                    // Normalize field_json to always be an array
                    // API may return single object or array
                    // Handle both property name formats: value_code/value_text OR field_value_code/field_value_text
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const rawFieldJson: any = selectedTag?.field_json;
                    const rawArray = rawFieldJson 
                        ? (Array.isArray(rawFieldJson) ? rawFieldJson : [rawFieldJson])
                        : [];
                    
                    // Normalize property names - API might use value_code or field_value_code
                    const tagFieldJson: Array<{ value_code: string; value_text: string; value_rate?: string }> = 
                        rawArray.map((item: any) => ({
                            value_code: item.value_code || item.field_value_code || '',
                            value_text: item.value_text || item.field_value_text || '',
                            value_rate: item.value_rate
                        })).filter((item: any) => item.value_code); // Filter out items without value_code
                    
                    console.log('Tags grouping - tagFieldJson:', {
                        rawFieldJson,
                        rawArray,
                        tagFieldJson,
                        tagFieldJsonLength: tagFieldJson.length
                    });
                    
                    // Only create value-based rows if the tag has dropdown values (field_json)
                    if (selectedTag && tagFieldJson.length > 0) {
                        console.log('Tags grouping - showing rows for tag values:', {
                            selectedTagCode,
                            tagName: selectedTag.field_name,
                            values: tagFieldJson
                        });
                        
                        // Group schedules by tag value (value_code)
                        // schedulesByTagValue: value_code -> date -> schedules[]
                        const schedulesByTagValue = new Map<string, Map<string, any[]>>();
                        
                        // Initialize maps for each value in the selected tag
                        tagFieldJson.forEach((tagValue) => {
                            const valueCode = tagValue.value_code;
                            if (valueCode) {
                                schedulesByTagValue.set(valueCode, new Map());
                            }
                        });
                        
                        // Extract schedules and group by tag value
                        groups.forEach((group) => {
                            if (group.group_id === "global_holiday") return;
                            
                            Object.entries(group.dates || {}).forEach(([dateStr, dateData]) => {
                                const schedules = dateData?.schedules || [];
                                
                                schedules.forEach((schedule: any) => {
                                    const scheduleTags = schedule.tags || [];
                                    
                                    // Find the tag matching our selected tag
                                    const matchingTag = scheduleTags.find(
                                        (tag: any) => tag.field_code === selectedTagCode
                                    );
                                    
                                    if (matchingTag && matchingTag.field_values && matchingTag.field_values.length > 0) {
                                        // Schedule has values for this tag - add to each matching value group
                                        matchingTag.field_values.forEach((tagValue: any) => {
                                            const valueCode = tagValue.value_code;
                                            if (valueCode && schedulesByTagValue.has(valueCode)) {
                                                const valueDateMap = schedulesByTagValue.get(valueCode)!;
                                                if (!valueDateMap.has(dateStr)) {
                                                    valueDateMap.set(dateStr, []);
                                                }
                                                valueDateMap.get(dateStr)!.push(schedule);
                                            }
                                        });
                                    }
                                });
                            });
                        });
                        
                        // Build groups from tag values
                        const valueGroups = tagFieldJson.map((tagValue) => {
                            const valueCode = tagValue.value_code;
                            const valueText = tagValue.value_text || valueCode;
                            const valueRate = tagValue.value_rate;
                            
                            const valueSchedules = schedulesByTagValue.get(valueCode);
                            const dates: Record<string, any> = {};
                            
                            if (valueSchedules) {
                                valueSchedules.forEach((schedules, dateStr) => {
                                    dates[dateStr] = {
                                        schedules: schedules,
                                        timeoff: [],
                                        unavailability: [],
                                        holiday: [],
                                        open_shifts: []
                                    };
                                });
                            }
                            
                            // Include empty dates from all groups
                            groups.forEach((group) => {
                                if (group.group_id === "global_holiday") return;
                                
                                Object.entries(group.dates || {}).forEach(([dateStr]) => {
                                    if (!dates[dateStr]) {
                                        dates[dateStr] = {
                                            schedules: [],
                                            timeoff: [],
                                            unavailability: [],
                                            holiday: [],
                                            open_shifts: []
                                        };
                                    }
                                });
                            });
                            
                            // Build display name with rate if available
                            const displayName = valueRate 
                                ? `${valueText} (Rate: ${valueRate})`
                                : valueText;
                            
                            return {
                                group_id: `${selectedTagCode}_${valueCode}`,
                                group_name: displayName,
                                dates: dates,
                            };
                        });
                        
                        console.log('Tags grouping - value-based groups:', {
                            selectedTag: selectedTag.field_name,
                            valueGroups: valueGroups.length,
                            valueNames: valueGroups.map((g) => g.group_name)
                        });
                        
                        // Return groups for each tag value
                        if (holidaysGroup) {
                            return [holidaysGroup, ...valueGroups];
                        }
                        return valueGroups;
                    }
                }
                
                // Default behavior: show rows for each tag (when no specific tag is selected or tag has no dropdown values)
                // Create a map of tag_code to sidebar item for quick lookup
                const tagCodeToSidebarItem = new Map<string, any>();
                sidebarItems.forEach((item) => {
                    if ('tag_code' in item) {
                        tagCodeToSidebarItem.set(item.tag_code, item);
                    }
                });

                // Extract all schedules from all groups and group them by tag field_code
                const schedulesByTagCode = new Map<string, Map<string, any[]>>(); // tag_code -> date -> schedules[]
                
                groups.forEach((group) => {
                    // Skip holidays group
                    if (group.group_id === "global_holiday") return;
                    
                    Object.entries(group.dates || {}).forEach(([dateStr, dateData]) => {
                        const schedules = dateData?.schedules || [];
                        
                        schedules.forEach((schedule: any) => {
                            // Each schedule can have multiple tags
                            const scheduleTags = schedule.tags || [];
                            
                            scheduleTags.forEach((tag: any) => {
                                const tagFieldCode = tag.field_code;
                                if (!tagFieldCode) return;
                                
                                // Initialize if needed
                                if (!schedulesByTagCode.has(tagFieldCode)) {
                                    schedulesByTagCode.set(tagFieldCode, new Map());
                                }
                                
                                const tagDateMap = schedulesByTagCode.get(tagFieldCode)!;
                                if (!tagDateMap.has(dateStr)) {
                                    tagDateMap.set(dateStr, []);
                                }
                                
                                // Add schedule to this tag's date
                                tagDateMap.get(dateStr)!.push(schedule);
                            });
                        });
                    });
                });

                // Build groups from sidebar items
                const layoutGroups = sidebarItems
                    .map((item) => {
                        if ('tag_code' in item) {
                            const tagCode = item.tag_code;
                            const tagSchedules = schedulesByTagCode.get(tagCode);
                            
                            // Build dates object from schedules
                            const dates: Record<string, any> = {};
                            if (tagSchedules) {
                                tagSchedules.forEach((schedules, dateStr) => {
                                    dates[dateStr] = {
                                        schedules: schedules,
                                        timeoff: [],
                                        unavailability: [],
                                        holiday: [],
                                        open_shifts: []
                                    };
                                });
                            }
                            
                            // Also include dates from all groups that don't have schedules for this tag
                            groups.forEach((group) => {
                                if (group.group_id === "global_holiday") return;
                                
                                Object.entries(group.dates || {}).forEach(([dateStr, dateData]) => {
                                    if (!dates[dateStr]) {
                                        dates[dateStr] = {
                                            schedules: [],
                                            timeoff: dateData?.timeoff || [],
                                            unavailability: dateData?.unavailability || [],
                                            holiday: dateData?.holiday || [],
                                            open_shifts: dateData?.open_shifts || []
                                        };
                                    }
                                });
                            });
                            
                            return {
                                group_id: item.tag_id || tagCode,
                                group_name: item.tag_name || item.tag_code || tagCode,
                                dates: dates,
                            };
                        }
                        return null;
                    })
                    .filter((group): group is ScheduleV3Group => Boolean(group));

                // Find tags that appear in schedules but not in sidebar (unassigned)
                const sidebarTagCodes = new Set(
                    sidebarItems
                        .filter((item): item is any => 'tag_code' in item)
                        .map((item: any) => item.tag_code)
                );
                
                const unassignedTagCodes = Array.from(schedulesByTagCode.keys()).filter(
                    tagCode => !sidebarTagCodes.has(tagCode)
                );
                
                const unassignedTagGroups = unassignedTagCodes.map((tagCode) => {
                    const tagSchedules = schedulesByTagCode.get(tagCode)!;
                    const dates: Record<string, any> = {};
                    
                    tagSchedules.forEach((schedules, dateStr) => {
                        dates[dateStr] = {
                            schedules: schedules,
                            timeoff: [],
                            unavailability: [],
                            holiday: [],
                            open_shifts: []
                        };
                    });
                    
                    return {
                        group_id: tagCode,
                        group_name: `Unassigned - ${tagCode}`,
                        dates: dates,
                    };
                });

                console.log('Tags grouping - sidebar groups:', {
                    sidebarGroups: layoutGroups.length,
                    unassignedGroups: unassignedTagGroups.length,
                    totalGroups: layoutGroups.length + unassignedTagGroups.length,
                    schedulesByTagCode: Array.from(schedulesByTagCode.keys())
                });

                // Always include global_holiday group if it exists (at the beginning)
                if (holidaysGroup) {
                    return [holidaysGroup, ...layoutGroups, ...unassignedTagGroups];
                }
                return [...layoutGroups, ...unassignedTagGroups];
            }

            // Fallback: if sidebar not available, return groups as-is
            if (holidaysGroup) {
                const otherGroups = groups.filter(g => g.group_id !== "global_holiday");
                return [holidaysGroup, ...otherGroups];
            }
            return groups;
        }

        // For other grouping types (job_code), return groups as-is
        // Groups already include any ungrouped data from the API
        // Note: holidays group should already be in groups, but ensure it's first
        if (holidaysGroup) {
            const otherGroups = groups.filter(g => g.group_id !== "global_holiday");
            return [holidaysGroup, ...otherGroups];
        }
        return groups;
    }, [groups, employeeList, departments, sitesV2, groupingType, appliedFilters, includeDept, user?.os_emp_account_id, user?.department_code, viewType, sidebarLayout, useLegacySchMapping, selectedTagCode, attendanceTags]);

    // Debug: Log final mergedGroups
    useEffect(() => {
        console.log('Final mergedGroups passed to grid:', {
            count: mergedGroups.length,
            groupIds: mergedGroups.map((g: ScheduleV3Group) => ({ id: g.group_id, name: g.group_name })),
            hasUngroupedTimeoff: mergedGroups.some((g: ScheduleV3Group) => g.group_id === 'ungrouped_dept_timeoff'),
            hasUngroupedUnavail: mergedGroups.some((g: ScheduleV3Group) => g.group_id === 'ungrouped_dept_unavailability'),
            hasUngroupedHoliday: mergedGroups.some((g: ScheduleV3Group) => g.group_id === 'ungrouped_dept_holiday')
        });
    }, [mergedGroups]);

    // Function to get date range based on view type
    const getDateRange = (viewType: SchedulingViewType, currentDate: Date) => {
        let startDate: Date;
        let endDate: Date;

        switch (viewType) {
            case 'daily':
                startDate = startOfDay(currentDate);
                endDate = endOfDay(currentDate);
                break;
            case 'weekly':
                startDate = SchedulingToolUtils.getWeekStart(currentDate, schedulingConfig);
                endDate = SchedulingToolUtils.getWeekEnd(currentDate, schedulingConfig);
                break;
            case 'monthly':
            default:
                const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
                const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0, 23, 59, 59);
                startDate = new Date(startOfMonth);
                startDate.setDate(startOfMonth.getDate() - 7);
                endDate = new Date(endOfMonth);
                endDate.setDate(endOfMonth.getDate() + 7);
                break;
        }

        return { startDate, endDate };
    };

    // Count of shifts that have one or more conflicts within the current view + applied filters.
    // Computed from the same `mergedGroups` the grid renders, so it always matches the on-screen
    // amber/red ring indicators. Reuses buildConflictIndex (no extra API calls). For the monthly
    // view, mergedGroups carries a ±7 day padding, so we narrow to the visible range here.
    const conflictCount = useMemo(() => {
        if (!mergedGroups?.length) return 0;
        const currentDate = viewType === 'daily' ? currentDay : viewType === 'weekly' ? currentWeek : currentMonth;
        const { startDate, endDate } = getDateRange(viewType, currentDate);
        // Date keys in group.dates are 'yyyy-MM-dd' strings — lexical comparison is range-correct.
        const startKey = format(startOfDay(startDate), 'yyyy-MM-dd');
        const endKey = format(endOfDay(endDate), 'yyyy-MM-dd');

        const conflictIndex = buildConflictIndex(mergedGroups); // Map<scheduleId, Conflict[]>
        let count = 0;
        for (const group of mergedGroups) {
            if (!group.dates) continue;
            for (const dateStr of Object.keys(group.dates)) {
                if (dateStr < startKey || dateStr > endKey) continue;
                for (const schedule of group.dates[dateStr]?.schedules ?? []) {
                    if (schedule.os_emp_schedules_id != null && conflictIndex.has(schedule.os_emp_schedules_id)) {
                        count++;
                    }
                }
            }
        }
        return count;
    }, [mergedGroups, viewType, currentDay, currentWeek, currentMonth]);

    // Rich, per-shift conflict rows for the Conflicts details modal. Mirrors conflictCount's
    // scoping exactly so the list always matches the badge number.
    const conflictDetails = useMemo(() => {
        if (!mergedGroups?.length) return [];
        const currentDate = viewType === 'daily' ? currentDay : viewType === 'weekly' ? currentWeek : currentMonth;
        const { startDate, endDate } = getDateRange(viewType, currentDate);
        const startKey = format(startOfDay(startDate), 'yyyy-MM-dd');
        const endKey = format(endOfDay(endDate), 'yyyy-MM-dd');

        const formatShiftTimeRange = (startTimeRaw?: string | null, endTimeRaw?: string | null): string => {
            if (!startTimeRaw || !endTimeRaw) return '';
            const toParsable = (t: string) => {
                if (t.includes('T') || t.includes(' ')) return t;
                const withSeconds = t.length === 5 && t.includes(':') ? `${t}:00` : t;
                return `2000-01-01 ${withSeconds}`;
            };
            const formatted = DateTimeUtil.formatElegantTimeRange(toParsable(startTimeRaw), toParsable(endTimeRaw));
            if (formatted && formatted !== 'N/A') return formatted;
            return `${startTimeRaw}-${endTimeRaw}`;
        };

        const conflictIndex = buildConflictIndex(mergedGroups);
        const rows: Array<{
            scheduleId: number;
            employeeName: string;
            groupName: string;
            date: string;
            shiftName: string;
            timeRange: string;
            severity: 'red' | 'amber';
            messages: string[];
        }> = [];
        for (const group of mergedGroups) {
            if (!group.dates) continue;
            for (const dateStr of Object.keys(group.dates)) {
                if (dateStr < startKey || dateStr > endKey) continue;
                for (const schedule of group.dates[dateStr]?.schedules ?? []) {
                    const id = schedule.os_emp_schedules_id;
                    if (id == null) continue;
                    const conflicts = conflictIndex.get(id);
                    if (!conflicts?.length) continue;
                    const severity = getConflictSeverity(conflicts);
                    rows.push({
                        scheduleId: id,
                        employeeName: schedule.employee?.name || 'Unknown',
                        groupName: group.group_name || '',
                        date: dateStr,
                        shiftName: schedule.shift?.shift_name || 'Shift',
                        timeRange: formatShiftTimeRange(schedule.shift?.start_time, schedule.shift?.end_time),
                        severity: severity?.color ?? 'amber',
                        messages: conflicts.map(c => c.message),
                    });
                }
            }
        }
        rows.sort((a, b) => a.date.localeCompare(b.date) || a.employeeName.localeCompare(b.employeeName));
        return rows;
    }, [mergedGroups, viewType, currentDay, currentWeek, currentMonth]);

    const auditLogDefaultDates = useMemo(() => {
        const currentDate = viewType === 'daily' ? currentDay : viewType === 'weekly' ? currentWeek : currentMonth;
        let startDate: Date;
        let endDate: Date;
        let label: string;

        if (viewType === 'daily') {
            startDate = startOfDay(currentDate);
            endDate = endOfDay(currentDate);
            label = format(currentDate, 'MMM d, yyyy');
        } else if (viewType === 'weekly') {
            startDate = SchedulingToolUtils.getWeekStart(currentDate, schedulingConfig);
            endDate = SchedulingToolUtils.getWeekEnd(currentDate, schedulingConfig);
            label = `${format(startDate, 'MMM d')} - ${format(endDate, 'MMM d, yyyy')}`;
        } else {
            startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
            endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
            label = format(currentDate, 'MMMM yyyy');
        }

        return {
            startDate: format(startDate, 'yyyy-MM-dd'),
            endDate: format(endDate, 'yyyy-MM-dd'),
            label,
        };
    }, [viewType, currentDay, currentWeek, currentMonth, schedulingConfig]);

    // Function to fetch schedules only (main data)
    const fetchSchedules = (viewType: SchedulingViewType, currentDate: Date, filterOverrides?: typeof appliedFilters) => {
        const { startDate, endDate } = getDateRange(viewType, currentDate);
        const activeFilters = filterOverrides || appliedFilters;
        
        console.log('fetchSchedules called:', {
            viewType,
            groupingType,
            shouldUseSidebarLayout,
            useLegacySchMapping,
            selectedTagCode
        });

        // Note: Sidebar layout API is now called in fetchDataForView, not here
        // This function only fetches schedule data
        const runFetch = async () => {
            // Build filters object
            const filtersToSend: any = {
                department_codes: activeFilters.departmentCodes.length > 0 ? activeFilters.departmentCodes : filters.departmentCodes,
                site_ids: activeFilters.siteIds.length > 0 ? activeFilters.siteIds : filters.siteIds,
                employee_ids: activeFilters.employeeIds.length > 0 ? activeFilters.employeeIds : filters.employeeIds,
                group_code: activeFilters.groupCodes.length > 0 ? activeFilters.groupCodes : [],
            };

            // Add tag_codes filter when grouping by tags and a tag is selected (not in monthly view)
            if (groupingType === 'tags' && selectedTagCode && viewType !== 'monthly') {
                filtersToSend.tag_codes = [selectedTagCode];
            } else {
                filtersToSend.tag_codes = [];
            }

            dispatch(
                getSchedulesV3({
                    start_date: format(startDate, 'yyyy-MM-dd'),
                    end_date: format(endDate, 'yyyy-MM-dd'),
                    group_by: viewType === 'monthly' ? '' as SchedulesV3GroupByType : groupingType,
                    filters: filtersToSend,
                    include: {
                        schedules: true,
                        timeoff: false,
                        holidays: false,
                        unavailability: false,
                        open_shifts: false,
                    },
                    include_mydept: includeDept,
                })
            );
        };

        runFetch();
    };

    // Function to fetch timeoff data
    const fetchTimeoff = (viewType: SchedulingViewType, currentDate: Date, filterOverrides?: typeof appliedFilters) => {
        const { startDate, endDate } = getDateRange(viewType, currentDate);
        const activeFilters = filterOverrides || appliedFilters;

        dispatch(
            getSchedulesV3({
                start_date: format(startDate, 'yyyy-MM-dd'),
                end_date: format(endDate, 'yyyy-MM-dd'),
                group_by: viewType === 'monthly' ? '' as SchedulesV3GroupByType : groupingType,
                filters: {
                    department_codes: activeFilters.departmentCodes.length > 0 ? activeFilters.departmentCodes : filters.departmentCodes,
                    site_ids: activeFilters.siteIds.length > 0 ? activeFilters.siteIds : filters.siteIds,
                    employee_ids: activeFilters.employeeIds.length > 0 ? activeFilters.employeeIds : filters.employeeIds,
                    group_code: activeFilters.groupCodes.length > 0 ? activeFilters.groupCodes : [],
                },
                include: {
                    schedules: false,
                    timeoff: true,
                    holidays: false,
                    unavailability: false,
                    open_shifts: false,
                },
                include_mydept: includeDept,
            })
        );
    };

    // Function to fetch holidays data
    const fetchHolidays = (viewType: SchedulingViewType, currentDate: Date, filterOverrides?: typeof appliedFilters) => {
        const { startDate, endDate } = getDateRange(viewType, currentDate);
        const activeFilters = filterOverrides || appliedFilters;

        dispatch(
            getSchedulesV3({
                start_date: format(startDate, 'yyyy-MM-dd'),
                end_date: format(endDate, 'yyyy-MM-dd'),
                group_by: viewType === 'monthly' ? '' as SchedulesV3GroupByType : groupingType,
                filters: {
                    department_codes: activeFilters.departmentCodes.length > 0 ? activeFilters.departmentCodes : filters.departmentCodes,
                    site_ids: activeFilters.siteIds.length > 0 ? activeFilters.siteIds : filters.siteIds,
                    employee_ids: activeFilters.employeeIds.length > 0 ? activeFilters.employeeIds : filters.employeeIds,
                    group_code: activeFilters.groupCodes.length > 0 ? activeFilters.groupCodes : [],
                },
                include: {
                    schedules: false,
                    timeoff: false,
                    holidays: true,
                    unavailability: false,
                    open_shifts: false,
                },
                include_mydept: includeDept,
            })
        );
    };

    // Function to fetch unavailability data
    const fetchUnavailability = (viewType: SchedulingViewType, currentDate: Date, filterOverrides?: typeof appliedFilters) => {
        const { startDate, endDate } = getDateRange(viewType, currentDate);
        const activeFilters = filterOverrides || appliedFilters;

        dispatch(
            getSchedulesV3({
                start_date: format(startDate, 'yyyy-MM-dd'),
                end_date: format(endDate, 'yyyy-MM-dd'),
                group_by: viewType === 'monthly' ? '' as SchedulesV3GroupByType : groupingType,
                filters: {
                    department_codes: activeFilters.departmentCodes.length > 0 ? activeFilters.departmentCodes : filters.departmentCodes,
                    site_ids: activeFilters.siteIds.length > 0 ? activeFilters.siteIds : filters.siteIds,
                    employee_ids: activeFilters.employeeIds.length > 0 ? activeFilters.employeeIds : filters.employeeIds,
                    group_code: activeFilters.groupCodes.length > 0 ? activeFilters.groupCodes : [],
                },
                include: {
                    schedules: false,
                    timeoff: false,
                    holidays: false,
                    unavailability: true,
                    open_shifts: false,
                },
                include_mydept: includeDept,
            })
        );
    };

    // Function to fetch open shifts data
    const fetchOpenShifts = (viewType: SchedulingViewType, currentDate: Date, filterOverrides?: typeof appliedFilters) => {
        const { startDate, endDate } = getDateRange(viewType, currentDate);
        const activeFilters = filterOverrides || appliedFilters;

        dispatch(
            getSchedulesV3({
                start_date: format(startDate, 'yyyy-MM-dd'),
                end_date: format(endDate, 'yyyy-MM-dd'),
                // Use actual groupingType even in monthly view so open_shifts land in expected group structure
                group_by: groupingType,
                filters: {
                    department_codes: activeFilters.departmentCodes.length > 0 ? activeFilters.departmentCodes : filters.departmentCodes,
                    site_ids: activeFilters.siteIds.length > 0 ? activeFilters.siteIds : filters.siteIds,
                    employee_ids: activeFilters.employeeIds.length > 0 ? activeFilters.employeeIds : filters.employeeIds,
                    group_code: activeFilters.groupCodes.length > 0 ? activeFilters.groupCodes : [],
                },
                include: {
                    schedules: false,
                    timeoff: false,
                    holidays: false,
                    unavailability: false,
                    open_shifts: true,
                },
                include_mydept: includeDept,
            })
        );
    };

    // Function to fetch all data in separate calls (lazy loading approach)
    const fetchDataForView = (viewType: SchedulingViewType, currentDate: Date, filterOverrides?: typeof appliedFilters) => {
        console.log('fetchDataForView called:', {
            viewType,
            showSchedule,
            showTimeoff,
            showHoliday,
            showUnavailability,
            showOpenShifts,
            shouldUseSidebarLayout
        });
        
        const { startDate, endDate } = getDateRange(viewType, currentDate);
        const activeFilters = filterOverrides || appliedFilters;

        // Clear existing data before fetching new data
        dispatch({ type: 'scheduleV3/clearScheduleV3Data' });

        // Always fetch Open Shift status counts for the current date range (for Open Shift menu badge)
        dispatch(
            getOpenShiftStatusCounts({
                start_date: format(startDate, "yyyy-MM-dd"),
                end_date: format(endDate, "yyyy-MM-dd"),
            })
        );
        
        // Fetch sidebar layout FIRST if using sidebar layout (this defines the rows, independent of data toggles)
        if (shouldUseSidebarLayout) {
            console.log('Fetching sidebar layout API from fetchDataForView:', {
                start_date: format(startDate, 'yyyy-MM-dd'),
                end_date: format(endDate, 'yyyy-MM-dd'),
                group_by: groupingType,
                include_mydept: includeDept,
                selectedTagCode: selectedTagCode
            });
            
            // Only call sidebar API for employee, department, site, and tags grouping
            if (groupingType === 'employee' || groupingType === 'department' || groupingType === 'site' || groupingType === 'tags') {
                const tagCodeToSend = selectedTagCode;
                console.log('Sending tag_code to API:', tagCodeToSend);
                dispatch(
                    getScheduleSidebarList({
                        start_date: format(startDate, 'yyyy-MM-dd'),
                        end_date: format(endDate, 'yyyy-MM-dd'),
                        group_by: groupingType as Extract<SchedulesV3GroupByType, 'employee' | 'department' | 'site' | 'tags'>,
                        sort_by: effectiveSidebarSortBy(),
                        filters: {
                            department_codes:
                                activeFilters.departmentCodes.length > 0 ? activeFilters.departmentCodes : filters.departmentCodes,
                            employee_ids:
                                activeFilters.employeeIds.length > 0 ? activeFilters.employeeIds : filters.employeeIds,
                            site_ids:
                                activeFilters.siteIds.length > 0 ? activeFilters.siteIds : filters.siteIds,
                            tag_code: tagCodeToSend, // For now, send first tag or null. API might need array support later
                            group_code: activeFilters.groupCodes.length > 0 ? activeFilters.groupCodes : [],
                        },
                        include_mydept: includeDept,
                    })
                ).catch(error => {
                    console.error('Scheduling Tool: sidebar layout fetch failed', error);
                });
            }
        }
        
        // Fetch schedules only if toggle is ON
        // For tags grouping, only fetch if a tag is selected
        if (showSchedule) {
            if (groupingType === 'tags' && !selectedTagCode) {
                console.log('Skipping fetchSchedules - tags grouping but no tag selected');
            } else {
                fetchSchedules(viewType, currentDate, filterOverrides);
            }
        } else {
            console.log('Skipping fetchSchedules - showSchedule is OFF');
        }

        // Then fetch other data types in sequence with slight delays for better performance
        // Only call API if toggle is ON
        if (showTimeoff) {
            setTimeout(() => fetchTimeoff(viewType, currentDate, filterOverrides), 100);
        }
        
        if (showHoliday) {
            setTimeout(() => fetchHolidays(viewType, currentDate, filterOverrides), 200);
        }
        
        if (showUnavailability) {
            setTimeout(() => fetchUnavailability(viewType, currentDate, filterOverrides), 300);
        }
        
        if (showOpenShifts) {
            setTimeout(() => fetchOpenShifts(viewType, currentDate, filterOverrides), 400);
        }
    };

    useEffect(() => {
        fetchDataForView(viewType, 
            viewType === 'daily' ? currentDay : 
            viewType === 'weekly' ? currentWeek : 
            currentMonth
        );
    }, [dispatch, groupingType, viewType, currentDay, currentWeek, currentMonth, includeDept, showSchedule, showTimeoff, showHoliday, showUnavailability, showOpenShifts, selectedTagCode, sidebarSortBy]);


    const handleMonthChange = (newMonth: Date) => {
        setCurrentMonth(newMonth);
    };

    const handleWeekChange = (newWeek: Date) => {
        setCurrentWeek(newWeek);
    };

    const handleDayChange = (newDay: Date) => {
        setCurrentDay(newDay);
    };

    const goToPrev = () => {
        if (viewType === 'daily') {
            const prev = addDays(currentDay, -1);
            handleDayChange(prev);
        } else if (viewType === 'weekly') {
            const prev = addDays(currentWeek, -7);
            handleWeekChange(prev);
        } else {
            const prev = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
            handleMonthChange(prev);
        }
    };

    const goToNext = () => {
        if (viewType === 'daily') {
            const next = addDays(currentDay, 1);
            handleDayChange(next);
        } else if (viewType === 'weekly') {
            const next = addDays(currentWeek, 7);
            handleWeekChange(next);
        } else {
            const next = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
            handleMonthChange(next);
        }
        };

        const goToToday = () => {
        const today = new Date();
        handleDayChange(today);
        handleWeekChange(today);
        handleMonthChange(today);
    };

    const openDailyDatePicker = (anchorElement: HTMLElement | null) => {
        if (viewType !== 'daily') return;
        if (!anchorElement) return;
        const rect = anchorElement.getBoundingClientRect();
        const calendarWidth = 320;
        const padding = 16;
        const left = Math.min(
            Math.max(rect.left, padding),
            window.innerWidth - calendarWidth - padding
        );
        const top = rect.bottom + 8;
        setDailyDatePickerPosition({ top, left });
        setDailyDatePickerMonth(currentDay);
        dailyDatePickerAnchorRef.current = anchorElement;
        setShowDailyDatePicker(true);
    };

    useEffect(() => {
        if (!showDailyDatePicker) return;

        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (
                datePickerContainerRef.current?.contains(target) ||
                (dailyDatePickerAnchorRef.current && dailyDatePickerAnchorRef.current.contains(target as Node))
            ) {
                return;
            }
            setShowDailyDatePicker(false);
        };

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setShowDailyDatePicker(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscape);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [showDailyDatePicker]);

    useEffect(() => {
        if (viewType !== 'daily') {
            setShowDailyDatePicker(false);
        }
    }, [viewType]);

    useEffect(() => {
        if (!showDailyDatePicker) {
            dailyDatePickerAnchorRef.current = null;
        }
    }, [showDailyDatePicker]);

    // Get current date range display
    const getDateRangeDisplay = () => {
        if (viewType === 'daily') {
            return format(currentDay, "EEEE, MMM d, yyyy");
        } else if (viewType === 'weekly') {
            const weekStart = SchedulingToolUtils.getWeekStart(currentWeek, schedulingConfig);
            const weekEnd = SchedulingToolUtils.getWeekEnd(currentWeek, schedulingConfig);
            return `${format(weekStart, "MMM d, yyyy")} - ${format(weekEnd, "MMM d, yyyy")}`;
        } else {
            return format(currentMonth, "MMMM yyyy");
        }
    };

    // Get grouping options
    const getGroupingOptions = () => {
        return [
            { value: 'department', label: 'Department', icon: '🏢' },
            { value: 'site', label: 'Site', icon: '🏢' },
            { value: 'employee', label: 'Employee', icon: '👥' },
            { value: 'tags', label: 'Tags', icon: '🏷️' },
            // { value: 'job_code', label: 'Job Code', icon: '💼' },
        ];
    };

    // Get sort options for sidebar list: employee view uses first/last name asc/desc; other views use asc/desc
    const getSortOptions = (): Array<{ value: ScheduleSidebarSortBy; label: string }> => {
        if (groupingType === 'employee') {
            return [
                { value: 'first_name_asc', label: 'First name A–Z' },
                { value: 'first_name_desc', label: 'First name Z–A' },
                { value: 'last_name_asc', label: 'Last name A–Z' },
                { value: 'last_name_desc', label: 'Last name Z–A' },
            ];
        }
        return [
            { value: 'asc', label: 'A–Z' },
            { value: 'desc', label: 'Z–A' },
        ];
    };

    const effectiveSidebarSortBy = (): ScheduleSidebarSortBy => {
        if (groupingType === 'employee') {
            return ['first_name_asc', 'first_name_desc', 'last_name_asc', 'last_name_desc'].includes(sidebarSortBy)
                ? sidebarSortBy
                : 'first_name_asc';
        }
        return sidebarSortBy === 'asc' || sidebarSortBy === 'desc' ? sidebarSortBy : 'asc';
    };

    // Handle publish schedule (from new Publish Schedule modal with full payload)
    const handlePublishScheduleFromModal = async (payload: PublishScheduleV3Request, conflictCount: number = 0) => {
        if (!hasScheduleToolPrivilege) {
            toast.error("You do not have permission to publish schedules");
            return;
        }
        setIsPublishing(true);
        setShowPublishConfirmation(false);
        try {
            const result = await dispatch(publishSchedule(payload));
            if (publishSchedule.fulfilled.match(result)) {
                const message = (result.payload as any)?.message || "Schedules published successfully!";
                setPublishMessage(message);
                setShowPublishSuccess(true);
                setPublishDateRange({ startDate: "", endDate: "" });
                if (conflictCount > 0) {
                    toast.warning(
                        `${conflictCount} published schedule${conflictCount !== 1 ? "s have" : " has"} conflicts — please review the calendar.`
                    );
                }
                fetchDataForView(
                    viewType,
                    viewType === "daily" ? currentDay : viewType === "weekly" ? currentWeek : currentMonth
                );
                setTimeout(() => setShowPublishSuccess(false), 3000);
            } else {
                const errorMessage =
                    typeof result.payload === "string"
                        ? result.payload
                        : (result.payload as any)?.message || "Unknown error";
                toast.error(cleanErrorMessage(errorMessage));
            }
        } catch (error: any) {
            console.error("Error publishing schedules:", error);
            toast.error(cleanErrorMessage(error.response?.data?.message || error.message || "Unknown error"));
        } finally {
            setIsPublishing(false);
        }
    };

    // Handle publish schedule (legacy - used only when modal was inline; kept for open shifts flow)
    const handlePublishSchedule = async () => {
        // Check privileges
        if (!hasScheduleToolPrivilege) {
            toast.error("You do not have permission to publish schedules");
            return;
        }

        // Use custom date range from state
        const startDate = new Date(publishDateRange.startDate);
        const endDate = new Date(publishDateRange.endDate);

        setIsPublishing(true);
        setShowPublishConfirmation(false);

        try {
            const result = await dispatch(publishSchedule({
                start_date: DateTimeUtil.formatDateToMySQL(startDate, true),
                end_date: DateTimeUtil.formatDateToMySQL(endDate, true),
                scope: 'ALL',
                send_notification: true,
                generate_ics: false,
            }));

            if (publishSchedule.fulfilled.match(result)) {
                const message = result.payload?.message || 'Schedules published successfully!';
                setPublishMessage(message);
                setShowPublishSuccess(true);

                // Reset date range
                setPublishDateRange({ startDate: '', endDate: '' });

                // Refresh schedules after successful publish
                fetchDataForView(viewType, 
                    viewType === 'daily' ? currentDay : 
                    viewType === 'weekly' ? currentWeek : 
                    currentMonth
                );

                // Hide success message after 3 seconds
                setTimeout(() => {
                    setShowPublishSuccess(false);
                }, 3000);
            } else {
                const errorMessage = typeof result.payload === 'string' 
                    ? result.payload 
                    : (result.payload as any)?.message || 'Unknown error';
                toast.error(cleanErrorMessage(errorMessage));
            }
        } catch (error: any) {
            console.error('Error publishing schedules:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
            toast.error(cleanErrorMessage(errorMessage));
        } finally {
            setIsPublishing(false);
        }
    };

    // Handle publish open shifts
    const handlePublishOpenShifts = async () => {
        // Check privileges
        if (!hasScheduleToolPrivilege) {
            toast.error("You do not have permission to publish open shifts");
            return;
        }

        // Use custom date range from state
        const startDate = new Date(publishDateRange.startDate);
        const endDate = new Date(publishDateRange.endDate);

        setIsPublishing(true);
        setShowPublishConfirmation(false);

        try {
            const result = await dispatch(publishOpenShift({
                start_date: DateTimeUtil.formatDateToMySQL(startDate, true),
                end_date: DateTimeUtil.formatDateToMySQL(endDate, true)
            }));

            if (publishOpenShift.fulfilled.match(result)) {
                const message = result.payload?.data?.message || result.payload?.message || 'Open shifts published successfully!';
                setPublishMessage(message);
                setShowPublishSuccess(true);

                // Reset date range
                setPublishDateRange({ startDate: '', endDate: '' });

                // Refresh schedules after successful publish
                fetchDataForView(viewType, 
                    viewType === 'daily' ? currentDay : 
                    viewType === 'weekly' ? currentWeek : 
                    currentMonth
                );

                // Hide success message after 3 seconds
                setTimeout(() => {
                    setShowPublishSuccess(false);
                }, 3000);
            } else {
                const errorMessage = typeof result.payload === 'string' 
                    ? result.payload 
                    : (result.payload as any)?.message || 'Unknown error';
                toast.error(cleanErrorMessage(errorMessage));
            }
        } catch (error: any) {
            console.error('Error publishing open shifts:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
            toast.error(cleanErrorMessage(errorMessage));
        } finally {
            setIsPublishing(false);
        }
    };

    // Handle publish based on type
    const handlePublish = () => {
        if (publishType === 'schedules') {
            handlePublishSchedule();
        } else {
            handlePublishOpenShifts();
        }
    };

    // Handle create open shifts
    const handleCreateOpenShifts = async (data: any) => {
        // Check privileges
        if (!hasScheduleToolPrivilege) {
            toast.error("You do not have permission to create open shifts");
            return;
        }

        console.log('Creating open shifts with data:', data);
        setIsCreatingOpenShifts(true);

        try {
            const result = await dispatch(createOpenShifts(data));

            if (createOpenShifts.fulfilled.match(result)) {
                const message = result.payload?.message || 'Open shifts created successfully!';
                toast.success(message);
                setShowCreateOpenShiftModal(false);

                // Refresh the current view after creating open shifts
                fetchDataForView(viewType, 
                    viewType === 'daily' ? currentDay : 
                    viewType === 'weekly' ? currentWeek : 
                    currentMonth
                );
            } else {
                const errorMessage = typeof result.payload === 'string' 
                    ? result.payload 
                    : (result.payload as any)?.message || 'Unknown error';
                toast.error(cleanErrorMessage(errorMessage));
            }
        } catch (error: any) {
            console.error('Error creating open shifts:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
            toast.error(cleanErrorMessage(errorMessage));
        } finally {
            setIsCreatingOpenShifts(false);
        }
    };

    // Handle opening assign shifts modal
    const handleOpenAssignShiftsModal = async () => {
        setShowAssignShiftsModal(true);
        setIsLoadingShifts(true);
        setPublishedShifts([]);
        setSelectedAssignments({});

        const { startDate, endDate } = getDateRange(viewType, 
            viewType === 'daily' ? currentDay : 
            viewType === 'weekly' ? currentWeek : 
            currentMonth
        );

        try {
            const result = await dispatch(getPublishedShiftsForAssign({
                start_date: DateTimeUtil.formatDateToMySQL(startDate, true),
                end_date: DateTimeUtil.formatDateToMySQL(endDate, true)
            }));

            if (getPublishedShiftsForAssign.fulfilled.match(result)) {
                setPublishedShifts(result.payload.data.published_shifts || []);
            } else {
                const errorMessage = typeof result.payload === 'string' 
                    ? result.payload 
                    : 'Failed to fetch published shifts';
                toast.error(cleanErrorMessage(errorMessage));
            }
        } catch (error: any) {
            console.error('Error fetching published shifts:', error);
            toast.error('Error fetching published shifts');
        } finally {
            setIsLoadingShifts(false);
        }
    };

    // Helper function to generate employee table data for PDF
    // Works for both employee view (group_name is employee) and department view (extract employee from schedule)
    const generateEmployeeTableData = (groups: ScheduleV3Group[], displayDays: Date[], isEmployeeGrouping: boolean) => {
        const tableData: any[][] = [];
        
        groups.forEach((group: ScheduleV3Group) => {
            displayDays.forEach(day => {
                const dateStr = format(day, 'yyyy-MM-dd');
                const dateData = group.dates[dateStr];
                
                if (!dateData || (!dateData.schedules?.length && !dateData.unavailability?.length && !dateData.timeoff?.length)) {
                    return;
                }

                // Schedules
                if (dateData.schedules && dateData.schedules.length > 0) {
                    dateData.schedules.forEach((schedule: any) => {
                        // For employee grouping, use group_name. For department grouping, extract from schedule
                        const employeeName = isEmployeeGrouping 
                            ? group.group_name 
                            : (schedule.employee?.name || 
                               schedule.employee?.full_name || 
                               `${schedule.employee?.first_name || ''} ${schedule.employee?.last_name || ''}`.trim() ||
                               'Unknown Employee');
                        
                        const row = [
                            employeeName,
                            format(day, 'MMM d, yyyy'),
                            'Schedule',
                            schedule.shift?.start_time && schedule.shift?.end_time 
                                ? `${schedule.shift.start_time} - ${schedule.shift.end_time}` 
                                : '',
                            schedule.shift?.shift_name || schedule.shift?.shift_code || '',
                            schedule.site?.site_name || '',
                            schedule.notes || ''
                        ];
                        tableData.push(row);
                    });
                }

                // Unavailability
                if (dateData.unavailability && dateData.unavailability.length > 0) {
                    dateData.unavailability.forEach((unavail: any) => {
                        const employeeName = isEmployeeGrouping 
                            ? group.group_name 
                            : (unavail.employee?.name || 
                               unavail.employee?.full_name || 
                               `${unavail.employee?.first_name || ''} ${unavail.employee?.last_name || ''}`.trim() ||
                               'Unknown Employee');
                        
                        const timeStr = unavail.all_day 
                            ? 'Full Day' 
                            : `${unavail.start_time || ''} - ${unavail.end_time || ''}`;
                        const row = [
                            employeeName,
                            format(day, 'MMM d, yyyy'),
                            'Unavailability',
                            timeStr,
                            '',
                            '',
                            unavail.note || ''
                        ];
                        tableData.push(row);
                    });
                }

                // Timeoff
                if (dateData.timeoff && dateData.timeoff.length > 0) {
                    dateData.timeoff.forEach((timeoff: any) => {
                        const employeeName = isEmployeeGrouping 
                            ? group.group_name 
                            : (timeoff.employee?.name || 
                               timeoff.employee?.full_name || 
                               `${timeoff.employee?.first_name || ''} ${timeoff.employee?.last_name || ''}`.trim() ||
                               'Unknown Employee');
                        
                        const startDate = timeoff.start_datetime ? format(new Date(timeoff.start_datetime), 'MMM d') : '';
                        const endDate = timeoff.end_datetime ? format(new Date(timeoff.end_datetime), 'MMM d') : '';
                        const dateRange = startDate && endDate ? `${startDate} - ${endDate}` : startDate || endDate || '';
                        const hoursStr = timeoff.hours ? `${timeoff.hours}h` : '';
                        const row = [
                            employeeName,
                            format(day, 'MMM d, yyyy'),
                            'Timeoff',
                            dateRange,
                            timeoff.earnings_code_name || '',
                            hoursStr,
                            timeoff.notes || ''
                        ];
                        tableData.push(row);
                    });
                }
            });
        });

        return tableData;
    };

    // Handle single employee PDF download
    const handleDownloadEmployeePDF = async (employeeId: string, month: Date) => {
        if (!employeeId) {
            toast.error('Please select an employee');
            return;
        }

        setIsDownloadingPDF(true);

        try {
            console.log('Generating PDF for employee:', employeeId, 'for month:', format(month, 'MMMM yyyy'));
            
            // Fetch data for the entire selected month (call API directly without updating Redux)
            const startOfMonth = new Date(month.getFullYear(), month.getMonth(), 1);
            const endOfMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0);
            
            const requestBody = buildScheduleV3Request({
                start_date: DateTimeUtil.formatDateToMySQL(startOfMonth, true),
                end_date: DateTimeUtil.formatDateToMySQL(endOfMonth, true),
                group_by: 'employee',
                filters: {
                    employee_ids: [employeeId],
                    site_ids: [],
                    department_codes: [],
                    group_code: [],
                },
                include: {
                    schedules: true,
                    timeoff: true,
                    holidays: true,
                    unavailability: true,
                    open_shifts: false,
                },
                include_mydept: IncludeDeptType.ALL,
            });
            
            const response = await phpClient.post<GetSchedulesV3Response>({
                url: "/Schedule/my_schedules_v4",
                data: requestBody,
            });
            
            // Check if fetch was successful
            if (!response.status) {
                toast.error('Failed to fetch employee data');
                setIsDownloadingPDF(false);
                return;
            }
            
            // Get the fetched employee data from the result
            const fetchedGroups = response.data || [];
            console.log('Fetched groups:', fetchedGroups);

            // Find employee data from fetched groups
            let employeeGroup = fetchedGroups.find((g: ScheduleV3Group) => g.group_id === employeeId);
            
            // If not found in fetched groups, get employee name from employeeList
            if (!employeeGroup) {
                const employee = employeeList.find(emp => emp.employee_id.toString() === employeeId);
                if (employee) {
                    // Create empty group with employee name
                    employeeGroup = {
                        group_id: employeeId,
                        group_name: employee.full_name || `${employee.first_name} ${employee.last_name}`,
                        dates: {}
                    };
                }
            }
            
            if (!employeeGroup) {
                toast.error('No data found for selected employee');
                setIsDownloadingPDF(false);
                return;
            }

            // Generate display days for the month (reuse startOfMonth and endOfMonth from above)
            const displayDays: Date[] = [];
            const daysInMonth = endOfMonth.getDate();
            for (let i = 1; i <= daysInMonth; i++) {
                displayDays.push(new Date(month.getFullYear(), month.getMonth(), i));
            }

            // Create PDF
            const pdf = new jsPDF('landscape', 'mm', 'a4');
            let currentY = 20;

            // Title
            pdf.setFontSize(18);
            pdf.text(`Employee Schedule Report`, 14, currentY);
            currentY += 7;

            // Employee Name
            pdf.setFontSize(14);
            pdf.text(`Employee: ${employeeGroup.group_name}`, 14, currentY);
            currentY += 6;

            // Month
            pdf.setFontSize(12);
            pdf.text(`Month: ${format(month, 'MMMM yyyy')}`, 14, currentY);
            currentY += 8;

            // Generate table data
            const tableData = generateEmployeeTableData([employeeGroup], displayDays, true);

            if (tableData.length > 0) {
                autoTable(pdf, {
                    head: [['Employee', 'Date', 'Type', 'Time/Range', 'Shift/Code', 'Site/Hours', 'Notes']],
                    body: tableData,
                    startY: currentY,
                    styles: {
                        fontSize: 7,
                        cellPadding: 1.5,
                        overflow: 'linebreak'
                    },
                    headStyles: {
                        fillColor: [66, 139, 202],
                        textColor: 255,
                        fontStyle: 'bold',
                        fontSize: 8
                    },
                    alternateRowStyles: { fillColor: [245, 245, 245] },
                    columnStyles: {
                        0: { cellWidth: 35, fontStyle: 'bold' },
                        1: { cellWidth: 25 },
                        2: { cellWidth: 25 },
                        3: { cellWidth: 35 },
                        4: { cellWidth: 30 },
                        5: { cellWidth: 25 },
                        6: { cellWidth: 'auto' },
                    },
                    margin: { left: 14, right: 14 },
                    showHead: 'everyPage',
                });
            } else {
                pdf.setFontSize(10);
                pdf.text('No schedule data available for this employee in the selected month', 14, currentY);
            }

            // Save PDF
            const timestamp = format(new Date(), 'dd_MM_yyyy_HH_mm');
            const employeeName = employeeGroup.group_name.replace(/\s+/g, '_');
            const monthStr = format(month, 'MMM_yyyy');
            pdf.save(`schedule_${employeeName}_${monthStr}_${timestamp}.pdf`);
            toast.success('PDF downloaded successfully');
            
            // Close modal after successful generation
            setShowEmployeeMonthPicker(false);
        } catch (error) {
            console.error('Error generating employee PDF:', error);
            toast.error('Error generating PDF');
        } finally {
            setIsDownloadingPDF(false);
        }
    };

    // Handle PDF download - simplified to use current view's grouping
    const handleDownloadPDF = (customMonth?: Date) => {
        console.log('=== PDF Generation Debug ===');
        console.log('Current groupingType:', groupingType);
        console.log('mergedGroups:', mergedGroups);
        console.log('mergedGroups length:', mergedGroups?.length);

        if (!mergedGroups || mergedGroups.length === 0) {
            toast.error('No schedule data available to download. Please ensure you have data loaded in the current view.');
            return;
        }

        setIsDownloadingPDF(true);

        try {
            // Use custom month if provided (for employee by month), otherwise use current view
            const useCustomMonth = !!customMonth;
            const pdfViewType = useCustomMonth ? 'monthly' : viewType;
            const pdfDate = useCustomMonth ? customMonth : (viewType === 'daily' ? currentDay : viewType === 'weekly' ? currentWeek : currentMonth);
            const actualGrouping = groupingType; // Always use current grouping
            
            const groupsToUse = mergedGroups;
            
            // Get display days based on view type
            const { startDate, endDate } = getDateRange(pdfViewType, pdfDate);
            const displayDays: Date[] = [];
            
            if (pdfViewType === 'daily') {
                displayDays.push(startDate);
            } else if (pdfViewType === 'weekly') {
                const weekStart = SchedulingToolUtils.getWeekStart(pdfDate, schedulingConfig);
                for (let i = 0; i < 7; i++) {
                    displayDays.push(addDays(weekStart, i));
                }
            } else {
                // Monthly
                const monthToUse = useCustomMonth ? customMonth : currentMonth;
                const startOfMonth = new Date(monthToUse.getFullYear(), monthToUse.getMonth(), 1);
                const endOfMonth = new Date(monthToUse.getFullYear(), monthToUse.getMonth() + 1, 0);
                const daysInMonth = endOfMonth.getDate();
                for (let i = 1; i <= daysInMonth; i++) {
                    displayDays.push(new Date(monthToUse.getFullYear(), monthToUse.getMonth(), i));
                }
            }
            
            console.log('Display days:', displayDays.map(d => format(d, 'yyyy-MM-dd')));

            // Create PDF in landscape mode for detailed tabular view
            const pdf = new jsPDF('landscape', 'mm', 'a4');
            let currentY = 20;

            // Title
            pdf.setFontSize(18);
            pdf.text('Schedule Detailed Report', 14, currentY);
            currentY += 7;
            
            // Date Range
            pdf.setFontSize(12);
            const dateRangeText = pdfViewType === 'daily' 
                ? format(pdfDate, 'MMM d, yyyy')
                : pdfViewType === 'weekly'
                ? `${format(SchedulingToolUtils.getWeekStart(pdfDate, schedulingConfig), 'MMM d')} - ${format(addDays(SchedulingToolUtils.getWeekStart(pdfDate, schedulingConfig), 6), 'MMM d, yyyy')}`
                : format(pdfDate, 'MMMM yyyy');
            pdf.text(dateRangeText, 14, currentY);
            currentY += 5;

            // View Type and Grouping
            pdf.setFontSize(10);
            pdf.text(`View: ${pdfViewType.charAt(0).toUpperCase() + pdfViewType.slice(1)} | Grouping: ${actualGrouping.charAt(0).toUpperCase() + actualGrouping.slice(1)}`, 14, currentY);
            currentY += 8;

            // Filters Applied
            pdf.setFontSize(9);
            const filtersArray: string[] = [];
            if (filterValues.employeeIds.length > 0) {
                filtersArray.push(`Employees: ${filterValues.employeeIds.length} selected`);
            }
            if (filterValues.departmentCodes.length > 0) {
                filtersArray.push(`Depts: ${filterValues.departmentCodes.join(', ')}`);
            }
            if (filterValues.siteIds.length > 0) {
                filtersArray.push(`Sites: ${filterValues.siteIds.length} selected`);
            }
            if (filterValues.shiftIds.length > 0) {
                filtersArray.push(`Shifts: ${filterValues.shiftIds.length} selected`);
            }
            if (filterValues.groupCodes.length > 0) {
                filtersArray.push(`Groups: ${filterValues.groupCodes.join(', ')}`);
            }
            if (filterValues.scheduleStartDate) {
                filtersArray.push(`Start: ${format(filterValues.scheduleStartDate, 'MMM d, yyyy')}`);
            }
            if (filterValues.scheduleEndDate) {
                filtersArray.push(`End: ${format(filterValues.scheduleEndDate, 'MMM d, yyyy')}`);
            }
            if (filtersArray.length > 0) {
                pdf.text(`Filters: ${filtersArray.join(' | ')}`, 14, currentY);
                currentY += 6;
            }
            
            if (actualGrouping === 'department') {
                // Department view: Show each department with its employees in a table
                console.log('Generating department view PDF');
                console.log('Total departments:', groupsToUse.length);
                
                groupsToUse.forEach((deptGroup: ScheduleV3Group, index: number) => {
                    console.log(`\n=== Processing department ${index + 1}: ${deptGroup.group_name} (${deptGroup.group_id}) ===`);
                    console.log('Department dates:', Object.keys(deptGroup.dates));
                    
                    // Log sample data from first date
                    const firstDate = Object.keys(deptGroup.dates)[0];
                    if (firstDate) {
                        const dateData = deptGroup.dates[firstDate];
                        console.log(`Sample date ${firstDate}:`, {
                            schedules: dateData.schedules?.length || 0,
                            timeoff: dateData.timeoff?.length || 0,
                            unavailability: dateData.unavailability?.length || 0
                        });
                        if (dateData.schedules && dateData.schedules.length > 0) {
                            console.log('Sample schedule:', dateData.schedules[0]);
                        }
                    }
                    // Add page break if needed (not for first department)
                    if (index > 0 && currentY > 180) {
                        pdf.addPage();
                        currentY = 20;
                    }

                    // Department Header
                    pdf.setFontSize(14);
                    pdf.setFillColor(66, 139, 202);
                    pdf.rect(14, currentY - 5, 270, 8, 'F');
                    pdf.setTextColor(255, 255, 255);
                    pdf.text(deptGroup.group_name, 16, currentY);
                    pdf.setTextColor(0, 0, 0);
                    currentY += 8;

                    // Use the same table generation function, passing false for isEmployeeGrouping
                    // This will extract employee names from each schedule/timeoff/unavailability object
                    const deptTableData = generateEmployeeTableData([deptGroup], displayDays, false);
                    
                    console.log(`Department ${deptGroup.group_name} table data rows:`, deptTableData.length);

                    if (deptTableData.length > 0) {
                        // Generate table for this department's employees
                        autoTable(pdf, {
                            head: [['Employee', 'Date', 'Type', 'Time', 'Shift/Code', 'Site/Hours', 'Notes']],
                            body: deptTableData,
                            startY: currentY,
                            styles: {
                                fontSize: 7,
                                cellPadding: 1.5,
                                overflow: 'linebreak'
                            },
                            headStyles: {
                                fillColor: [66, 139, 202],
                                textColor: 255,
                                fontStyle: 'bold',
                                fontSize: 8
                            },
                            alternateRowStyles: {
                                fillColor: [245, 245, 245],
                            },
                            columnStyles: {
                                0: { cellWidth: 35, fontStyle: 'bold' },
                                1: { cellWidth: 25 },
                                2: { cellWidth: 25 },
                                3: { cellWidth: 35 },
                                4: { cellWidth: 30 },
                                5: { cellWidth: 25 },
                                6: { cellWidth: 'auto' },
                            },
                            margin: { left: 14, right: 14 },
                        });

                        currentY = (pdf as any).lastAutoTable.finalY + 12;
                    } else {
                        pdf.setFontSize(9);
                        pdf.setTextColor(128, 128, 128);
                        pdf.text('No schedule data for employees in this department', 14, currentY);
                        pdf.setTextColor(0, 0, 0);
                        currentY += 12;
                    }
                });
            } else {
                // Employee view: Show all employees in one table
                const employeeTableData = generateEmployeeTableData(groupsToUse, displayDays, true);

                if (employeeTableData.length > 0) {
                    // Generate detailed table
                    autoTable(pdf, {
                        head: [['Employee', 'Date', 'Type', 'Time/Range', 'Shift/Code', 'Site/Hours', 'Notes']],
                        body: employeeTableData,
                        startY: currentY,
                        styles: { 
                            fontSize: 7, 
                            cellPadding: 1.5,
                            overflow: 'linebreak'
                        },
                        headStyles: { 
                            fillColor: [66, 139, 202], 
                            textColor: 255, 
                            fontStyle: 'bold',
                            fontSize: 8
                        },
                        alternateRowStyles: { fillColor: [245, 245, 245] },
                        columnStyles: {
                            0: { cellWidth: 35, fontStyle: 'bold' }, // Employee
                            1: { cellWidth: 25 }, // Date
                            2: { cellWidth: 25 }, // Type
                            3: { cellWidth: 35 }, // Time/Range
                            4: { cellWidth: 30 }, // Shift/Code
                            5: { cellWidth: 25 }, // Site/Hours
                            6: { cellWidth: 'auto' }, // Notes
                        },
                        margin: { left: 14, right: 14 },
                        showHead: 'everyPage',
                    });
                } else {
                    pdf.setFontSize(10);
                    pdf.text('No schedule data available for the selected period', 14, currentY);
                }
            }

            // Save PDF with formatted name
            const timestamp = format(new Date(), 'dd_MM_yyyy_HH_mm');
            pdf.save(`scheduling_${pdfViewType}_${actualGrouping}_${timestamp}.pdf`);
            toast.success('PDF downloaded successfully');
        } catch (error) {
            console.error('Error generating PDF:', error);
            toast.error('Error generating PDF');
        } finally {
            setIsDownloadingPDF(false);
        }
    };

    // Load employee options for filter
    const loadEmployeeOptionsForFilter = async (inputValue: string) => {
        if (inputValue.length < 3) {
            return [];
        }

        try {
            const response = await dispatch(searchEmployee({
                search: inputValue,
                departments: '',
                groups: '',
                site_id: '',
                page: 1,
                module_name: ModuleName.EMPLOYEE_PRIV_SCHEDULE_TOOL
            })).unwrap();

            return response.data.employees.map((employee: any) => ({
                value: employee.os_emp_account_id,
                label: employee.text,
                data: employee
            }));
        } catch (error) {
            console.error('Error searching employees:', error);
            return [];
        }
    };

    // Load employee options for search (for assign shifts modal)
    const loadEmployeeOptionsForShift = async (inputValue: string, shiftId: string) => {
        if (inputValue.length < 3) {
            return [];
        }

        try {
            const response = await dispatch(searchEmployee({
                search: inputValue,
                departments: '',
                groups: '',
                site_id: '',
                page: 1,
                module_name: ModuleName.EMPLOYEE_PRIV_SCHEDULE_TOOL
            })).unwrap();

            // Get the shift to filter by eligible employees
            const shift = publishedShifts.find(s => s.os_sch_open_shift_transactions_id === shiftId);
            const eligibleEmpIds = shift?.eligible_emps_json?.EMP_ID || [];

            return response.data.employees
                .filter((employee: any) => eligibleEmpIds.includes(employee.os_emp_account_id))
                .map((employee: any) => ({
                    value: employee.os_emp_account_id,
                    label: employee.text
                }));
        } catch (error) {
            console.error('Error searching employees:', error);
            return [];
        }
    };

    // Handle apply filters
    const handleApplyFilters = () => {
        // Create new filters object from filterValues
        const newFilters = {
            employeeIds: [...filterValues.employeeIds],
            departmentCodes: [...filterValues.departmentCodes],
            siteIds: [...filterValues.siteIds],
            shiftIds: [...filterValues.shiftIds],
            groupCodes: [...filterValues.groupCodes],
            scheduleStartDate: filterValues.scheduleStartDate ? new Date(filterValues.scheduleStartDate) : null,
            scheduleEndDate: filterValues.scheduleEndDate ? new Date(filterValues.scheduleEndDate) : null,
        };
        
        // Update applied filters state
        setAppliedFilters(newFilters);
        setShowFilterModal(false);
        
        // Refetch data with new filters immediately - pass filters directly to avoid stale closure
        fetchDataForView(viewType, 
            viewType === 'daily' ? currentDay : 
            viewType === 'weekly' ? currentWeek : 
            currentMonth,
            newFilters
        );
    };

    // Handle clear all filters
    const handleClearAllFilters = () => {
        const emptyFilters = {
            employeeIds: [],
            departmentCodes: [],
            siteIds: [],
            shiftIds: [],
            groupCodes: [],
            scheduleStartDate: null,
            scheduleEndDate: null,
        };
        setFilterValues(emptyFilters);
        setAppliedFilters(emptyFilters);
        setSelectedEmployees([]);
        // Refetch data after clearing filters - pass empty filters directly
        fetchDataForView(viewType, 
            viewType === 'daily' ? currentDay : 
            viewType === 'weekly' ? currentWeek : 
            currentMonth,
            emptyFilters
        );
    };

    // Handle assign shifts submission
    const handleAssignShifts = async () => {
        // Check privileges
        if (!hasScheduleToolPrivilege) {
            toast.error("You do not have permission to assign open shifts");
            return;
        }

        const assignments = Object.entries(selectedAssignments)
            .filter(([_, empId]) => empId !== '')
            .map(([shiftId, empId]) => ({
                selected_shifts: shiftId,
                assigned_employees: empId
            }));

        if (assignments.length === 0) {
            toast.warning('Please select at least one employee to assign');
            return;
        }

        setIsAssigningShifts(true);

        try {
            const result = await dispatch(assignOpenShiftToEmployee({
                shifts_data: assignments
            }));

            if (assignOpenShiftToEmployee.fulfilled.match(result)) {
                const message = result.payload?.message || 'Shifts assigned successfully!';
                toast.success(message);
                setShowAssignShiftsModal(false);

                // Refresh the current view after assigning shifts
                fetchDataForView(viewType, 
                    viewType === 'daily' ? currentDay : 
                    viewType === 'weekly' ? currentWeek : 
                    currentMonth
                );
            } else {
                const errorMessage = typeof result.payload === 'string' 
                    ? result.payload 
                    : (result.payload as any)?.message || 'Unknown error';
                toast.error(cleanErrorMessage(errorMessage));
            }
        } catch (error: any) {
            console.error('Error assigning shifts:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
            toast.error(cleanErrorMessage(errorMessage));
        } finally {
            setIsAssigningShifts(false);
        }
    };

    // Handle copy schedule
    const handleCopySchedule = async () => {
        // Check privileges
        if (!hasScheduleToolPrivilege) {
            toast.error("You do not have permission to copy schedules");
            return;
        }

        // Validation
        if (
            copyScheduleData.sourceType === 'date_range' &&
            (!copyScheduleData.sourceStartDate || !copyScheduleData.sourceEndDate)
        ) {
            toast.error('Please select source date range');
            return;
        }
        if (!copyScheduleData.destStartDate) {
            toast.error('Please select destination start date');
            return;
        }

        setIsCopyingSchedule(true);

        try {
            const currentViewAnchorDate =
                viewType === 'daily' ? currentDay :
                viewType === 'weekly' ? currentWeek :
                currentMonth;
            const { startDate: currentViewStartDate, endDate: currentViewEndDate } = getDateRange(viewType, currentViewAnchorDate);
             
            // const scope =
            //     includeDept === IncludeDeptType.ALL ? 'all' :
            //     includeDept === IncludeDeptType.MY_DEPARTMENT ? 'department' :
            //     'my';
            // For "Current schedule view" always copy within "my" scope.
            // For "Custom date range" always copy within "all" scope.
            const scope = copyScheduleData.sourceType === 'current_view' ? 'my' : 'all';

            const currentViewFilter = {
                ...(copyScheduleData.selectedDepartments.length > 0 ? { departments: copyScheduleData.selectedDepartments } : {}),
                ...(copyScheduleData.selectedGroups.length > 0 ? { groups: copyScheduleData.selectedGroups } : {}),
                ...(appliedFilters.siteIds.length > 0
                    ? {
                        sites: appliedFilters.siteIds
                            .map((id) => Number(id))
                            .filter((n) => !Number.isNaN(n)),
                    }
                    : {}),
                ...(appliedFilters.employeeIds.length > 0 ? { emp_ids: appliedFilters.employeeIds } : {}),
            };
            const hasCurrentViewFilter = Object.keys(currentViewFilter).length > 0;

            const request =
                copyScheduleData.sourceType === 'current_view'
                    ? {
                        source: {
                            source_type: 'current_view',
                            scope,
                            view_type: viewType,
                            start_date: format(currentViewStartDate, 'yyyy-MM-dd'),
                            end_date: format(currentViewEndDate, 'yyyy-MM-dd'),
                        },
                        ...(hasCurrentViewFilter ? { current_view_filter: currentViewFilter } : {}),
                        destination: {
                            start_date: copyScheduleData.destStartDate,
                        },
                        options: {
                            clear_existing: copyScheduleData.clearExisting,
                            exclude_employee_ids: [],
                            ...(copyScheduleData.selectedDepartments.length > 0
                                ? { include_departments: copyScheduleData.selectedDepartments }
                                : {}),
                        },
                    }
                    : {
                        source: {
                            source_type: 'date_range',
                            scope,
                            start_date: copyScheduleData.sourceStartDate,
                            end_date: copyScheduleData.sourceEndDate,
                        },
                        destination: {
                            start_date: copyScheduleData.destStartDate,
                        },
                        options: {
                            clear_existing: copyScheduleData.clearExisting,
                            exclude_employee_ids: [],
                            ...(copyScheduleData.selectedDepartments.length > 0
                                ? { include_departments: copyScheduleData.selectedDepartments }
                                : {}),
                        },
                    };

            const result = await dispatch(copySchedule(request as any));

            if (copySchedule.fulfilled.match(result)) {
                const payload = result.payload as any;
                const copiedCount = payload?.data?.copied_shifts || payload?.data?.shifts_count || payload?.shifts_count || 0;
                const employeeCount = payload?.data?.copied_employees || payload?.data?.employees_count || payload?.employees_count || 0;
                const message = payload?.message || `Successfully copied ${copiedCount} shifts for ${employeeCount} employees`;
                showScheduleResultToast(payload, message);
                setShowCopyScheduleModal(false);

                // Reset form
                                setCopyScheduleData({
                                    sourceType: 'current_view',
                                    sourceStartDate: '',
                                    sourceEndDate: '',
                                    destStartDate: '',
                                    clearExisting: false,
                                    selectedDepartments: [],
                                    selectedGroups: [],
                                });

                // Refresh the current view
                fetchDataForView(viewType, 
                    viewType === 'daily' ? currentDay : 
                    viewType === 'weekly' ? currentWeek : 
                    currentMonth
                );
            } else {
                const errorMessage = typeof result.payload === 'string' 
                    ? result.payload 
                    : (result.payload as any)?.message || 'Unknown error';
                toast.error(cleanErrorMessage(errorMessage));
            }
        } catch (error: any) {
            console.error('Error copying schedule:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
            toast.error(cleanErrorMessage(errorMessage));
        } finally {
            setIsCopyingSchedule(false);
        }
    };

    // Copy Schedule Modal
    const CopyScheduleModal = () => {
        if (!showCopyScheduleModal) return null;

        const currentViewAnchorDate =
            viewType === 'daily' ? currentDay :
            viewType === 'weekly' ? currentWeek :
            currentMonth;
        const { startDate: currentViewStartDate, endDate: currentViewEndDate } = getDateRange(viewType, currentViewAnchorDate);
        const formatLocalDate = (d: Date) =>
            new Intl.DateTimeFormat(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
        const formatLocalDateRange = (start: Date, end: Date) => `${formatLocalDate(start)} – ${formatLocalDate(end)}`;

        return (
            <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[1400] p-0 md:p-4 overflow-y-auto">
                <div className="bg-white rounded-none md:rounded-xl shadow-2xl w-full max-w-4xl h-full md:h-auto md:max-h-[90vh] flex flex-col my-0 md:my-4">
                    {/* Header */}
                    <div className="px-4 md:px-6 py-3 md:py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                        <h3 className="text-base md:text-lg lg:text-xl font-bold text-gray-900">Copy Schedule</h3>
                        <button
                            onClick={() => setShowCopyScheduleModal(false)}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <X size={20} className="md:w-6 md:h-6" />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto px-4 md:px-6 py-4 md:py-6 space-y-4 md:space-y-6">
                        {/* Source selector */}
                        <div className="border border-gray-200 rounded-lg p-4">
                            <h4 className="text-sm font-semibold text-gray-700 mb-3">Source</h4>

                            <div className="space-y-3">
                                <label className="flex items-start gap-3 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="copyScheduleSourceType"
                                        checked={copyScheduleData.sourceType === 'current_view'}
                                        onChange={() => setCopyScheduleData(prev => ({ ...prev, sourceType: 'current_view' }))}
                                        className="mt-1 w-4 h-4 text-purple-600 border-gray-300 focus:ring-purple-500"
                                    />
                                    <div>
                                        <div className="text-sm font-medium text-gray-900">Current schedule view</div>
                                    </div>
                                </label>

                                <label className="flex items-start gap-3 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="copyScheduleSourceType"
                                        checked={copyScheduleData.sourceType === 'date_range'}
                                        onChange={() =>
                                            setCopyScheduleData(prev => {
                                                const start = format(currentViewStartDate, 'yyyy-MM-dd');
                                                const end = format(currentViewEndDate, 'yyyy-MM-dd');
                                                return {
                                                    ...prev,
                                                    sourceType: 'date_range',
                                                    sourceStartDate: prev.sourceStartDate || start,
                                                    sourceEndDate: prev.sourceEndDate || end,
                                                };
                                            })
                                        }
                                        className="mt-1 w-4 h-4 text-purple-600 border-gray-300 focus:ring-purple-500"
                                    />
                                    <div className="text-sm font-medium text-gray-900">Custom date range</div>
                                </label>
                            </div>

                            {/* Schedule Details (merged into Source box) */}
                            <div className="mt-6 pt-6 border-t border-gray-200">
                                <h4 className="text-base font-semibold text-gray-900 mb-4">Schedule Details</h4>
                                
                                {copyScheduleData.sourceType === 'current_view' ? (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Copy shifts from
                                            </label>
                                            <div className="text-sm text-gray-700 mt-1">
                                                {formatLocalDateRange(currentViewStartDate, currentViewEndDate)}
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Paste starting on
                                            </label>
                                            <input
                                                type="date"
                                                value={copyScheduleData.destStartDate}
                                                onChange={(e) => setCopyScheduleData(prev => ({ ...prev, destStartDate: e.target.value }))}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                                required
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Copy shifts from</label>
                                            <label className="text-xs text-gray-500 mb-1 block">Start Date</label>
                                            <input
                                                type="date"
                                                value={copyScheduleData.sourceStartDate}
                                                onChange={(e) => setCopyScheduleData(prev => ({ ...prev, sourceStartDate: e.target.value }))}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2 opacity-0 select-none">
                                                Copy shifts from
                                            </label>
                                            <label className="text-xs text-gray-500 mb-1 block">End Date</label>
                                            <input
                                                type="date"
                                                value={copyScheduleData.sourceEndDate}
                                                onChange={(e) => setCopyScheduleData(prev => ({ ...prev, sourceEndDate: e.target.value }))}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Paste starting on</label>
                                            <label className="text-xs text-gray-500 mb-1 block">Start Date</label>
                                            <input
                                                type="date"
                                                value={copyScheduleData.destStartDate}
                                                onChange={(e) => setCopyScheduleData(prev => ({ ...prev, destStartDate: e.target.value }))}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                                required
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Clear Shifts Checkbox */}
                        <div className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                id="clearShifts"
                                checked={copyScheduleData.clearExisting}
                                onChange={(e) => setCopyScheduleData(prev => ({ ...prev, clearExisting: e.target.checked }))}
                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <label htmlFor="clearShifts" className="text-sm text-gray-700 flex items-center gap-2">
                                Replace existing shifts in destination
                                <span className="relative inline-flex items-center group">
                                    <Info size={14} className="text-gray-500" />
                                    <span className="pointer-events-none absolute left-1/2 top-full mt-2 w-80 -translate-x-1/2 rounded-lg bg-gray-800 px-3 py-2 text-xs text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
                                        If enabled, existing shifts in the destination dates will be removed before copying.
                                    </span>
                                </span>
                            </label>
                        </div>

                        {copyScheduleData.sourceType === 'date_range' && (
                            <>
                                {/* Filters Section */}
                                <div className="border border-gray-200 rounded-lg p-3 md:p-4">
                                    <h4 className="text-sm font-semibold text-gray-700 mb-4">Filters (optional)</h4>
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                                        {/* Department Filter */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Schedule Department</label>
                                            <Select
                                                isMulti
                                                value={copyScheduleData.selectedDepartments.map(code => ({
                                                    value: code,
                                                    label: departments.find(d => d.department_code === code)?.department_name || code
                                                }))}
                                                onChange={(selected: any) => {
                                                    setCopyScheduleData(prev => ({
                                                        ...prev,
                                                        selectedDepartments: (selected || []).map((item: any) => item.value)
                                                    }));
                                                }}
                                                options={departments
                                                    .filter(dept => dept.department_code && dept.department_name)
                                                    .map(dept => ({
                                                        value: dept.department_code!,
                                                        label: dept.department_name!
                                                    }))}
                                                className="text-sm"
                                                classNamePrefix="react-select"
                                                placeholder="Select Schedule Department"
                                                isClearable
                                                isSearchable
                                                menuPortalTarget={document.body}
                                                styles={{
                                                    menuPortal: base => ({ ...base, zIndex: 1600 }),
                                                    menu: base => ({ ...base, zIndex: 1600 })
                                                }}
                                            />
                                        </div>

                                        {/* Group Filter */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Group</label>
                                            <Select
                                                isMulti
                                                value={copyScheduleData.selectedGroups.map(group => ({
                                                    value: group,
                                                    label: group
                                                }))}
                                                onChange={(selected: any) => {
                                                    setCopyScheduleData(prev => ({
                                                        ...prev,
                                                        selectedGroups: (selected || []).map((item: any) => item.value)
                                                    }));
                                                }}
                                                options={[]}
                                                className="text-sm"
                                                classNamePrefix="react-select"
                                                placeholder="Select Group"
                                                isClearable
                                                isSearchable
                                                menuPortalTarget={document.body}
                                                styles={{
                                                    menuPortal: base => ({ ...base, zIndex: 1600 }),
                                                    menu: base => ({ ...base, zIndex: 1600 })
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        {/*
                        // Stats Footer (static for now)
                        <div className="flex items-center gap-6 text-sm text-gray-600">
                            <div className="flex items-center gap-2">
                                <Calendar size={16} />
                                <span>0 Shifts</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Users size={16} />
                                <span>0 Employees</span>
                            </div>
                        </div>
                        */}
                    </div>

                    {/* Footer */}
                    <div className="px-4 md:px-6 py-3 md:py-4 border-t border-gray-200 flex justify-end flex-shrink-0">
                        <button
                            onClick={handleCopySchedule}
                            disabled={isCopyingSchedule}
                            className="px-4 md:px-6 py-2 bg-transparent border border-blue-500 text-blue-700 rounded-lg hover:bg-blue-50 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm md:text-base"
                        >
                            {isCopyingSchedule ? (
                                <>
                                    <Loader2 className="animate-spin" size={16} />
                                    Copying...
                                </>
                            ) : (
                                'Copy Schedule'
                            )}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // Build tags payload in the format expected by the API (same as AddTimesheetEntry.tsx)
    const buildTagsPayload = (
        localSelectedMap: Record<string, { value_code: string; value_text: string }[]>,
        attendanceTagsOptions: any[]
    ): { field_code: string; field_values: { value_code: string; value_text: string }[] }[] => {
        // First identify root tags (those without parents or with null parent)
        const rootFields = attendanceTagsOptions.filter(
            (tag) => tag.parent_labor_dimension_id === null || tag.parent_labor_dimension_id === ""
        );

        const rootFieldCodes = rootFields.map((field) => field.field_code);

        // Filter out any local selections that aren't in our map or don't have values
        const validSelections = Object.entries(localSelectedMap).filter(([_, values]) => values && values.length > 0);

        // Split into root selections and child selections
        const rootSelections = validSelections.filter(([field_code]) => rootFieldCodes.includes(field_code));

        // Start with root tags
        const result: { field_code: string; field_values: { value_code: string; value_text: string }[] }[] =
            rootSelections.map(([field_code, values]) => ({
                field_code,
                field_values: values.map((v) => ({
                    value_code: v.value_code,
                    value_text: v.value_text,
                })),
            }));

        // If no root selections, return empty array
        if (result.length === 0) {
            return [];
        }

        // Store all non-root selections, organized by their level in the hierarchy
        const findFieldLevelAndPath = (fieldCode: string): { level: number; path: string[] } => {
            // Find the field by its code
            const field = attendanceTagsOptions.find((t) => t.field_code === fieldCode);
            if (!field) return { level: 0, path: [] };

            // If it's a root field, return level 1
            if (!field.parent_labor_dimension_id) {
                return { level: 1, path: [field.field_code] };
            }

            // Otherwise, find its parent and add 1 to the parent's level
            let current = field;
            const path = [fieldCode];
            let level = 1;

            // Traverse up the hierarchy until we reach a root field
            while (current.parent_labor_dimension_id) {
                const parentField = attendanceTagsOptions.find(
                    (t) => String(t.labor_dimensions_id) === current.parent_labor_dimension_id
                );

                if (!parentField) break;

                path.unshift(parentField.field_code);
                current = parentField;
                level++;
            }

            return { level, path };
        };

        // Group non-root selections by their hierarchy level and path
        const nonRootSelections = validSelections
            .filter(([field_code]) => !rootFieldCodes.includes(field_code))
            .map(([field_code, values]) => {
                const { level, path } = findFieldLevelAndPath(field_code);
                return {
                    field_code,
                    values,
                    level,
                    path,
                };
            })
            // Sort by level to process in order from higher levels to lower levels
            .sort((a, b) => a.level - b.level);

        // Add non-root selections as separate items in the result array
        // instead of nesting them under their parent tags
        if (nonRootSelections.length > 0) {
            nonRootSelections.forEach(({ field_code, values }) => {
                // Find the tag info for this field
                const tagInfo = attendanceTagsOptions.find((t) => t.field_code === field_code);

                if (tagInfo) {
                    // Add the child selection as a separate entry in the result
                    result.push({
                        field_code,
                        field_values: values.map((value) => ({
                            value_code: value.value_code,
                            value_text: value.value_text,
                        })),
                    });
                }
            });
        }

        return result;
    };

    // Handle attendance field changes for mass assignment
    const handleMassAssignmentAttendanceFieldChange = (fieldCode: string, value: any) => {
        setMassAssignmentData(prev => ({
            ...prev,
            attendanceTags: {
                ...prev.attendanceTags,
                [fieldCode]: value,
            }
        }));
    };

    // Handle mass assignment submission
    const handleMassAssignment = async () => {
        // Check privileges
        if (!hasScheduleToolPrivilege) {
            toast.error("You do not have permission to perform mass assignment");
            return;
        }

        // Validation
        if (massAssignmentData.selectedEmployees.length === 0 && !massAssignmentData.selectedGroup && !massAssignmentData.selectedDepartment) {
            toast.error('Please select at least one employee, group, or department');
            return;
        }

        if (!massAssignmentData.onlyNotes && !allowCustomShift && !massAssignmentData.selectedShift) {
            toast.error("Please select a shift template.");
            return;
        }
        
        if (!massAssignmentData.startDateTime) {
            toast.error(massAssignmentData.selectedShift ? 'Please select start date' : 'Please select start date time');
            return;
        }

        // End date/time is only required when: not "Only Notes" mode AND no shift selected (free-flow mode)
        // When shift is selected, end date/time is calculated automatically from start date + repeat count
        if (allowCustomShift && !massAssignmentData.onlyNotes && !massAssignmentData.selectedShift && !massAssignmentData.endDateTime) {
            toast.error('Please select end date time');
            return;
        }

        setIsMassAssigning(true);

        try {
            // Get selected shift details
            const selectedShift = allShifts.find(s => s.os_schedules_site_shifts_id === massAssignmentData.selectedShift);

            // Determine start_date and shift configuration
            let startDate: string;
            let freeFlowShift: { start_datetime: string; end_datetime: string } | undefined;

            if (massAssignmentData.selectedShift && selectedShift) {
                // Shift is selected - use template mode
                // Date format will be based on input type
                startDate = massAssignmentData.startDateTime.includes('T') 
                    ? format(new Date(massAssignmentData.startDateTime), 'yyyy-MM-dd')
                    : massAssignmentData.startDateTime;
            } else if (!allowCustomShift && !massAssignmentData.onlyNotes) {
                toast.error("Please select a shift template.");
                return;
            } else {
                // No shift selected - use free-flow mode
                startDate = format(new Date(massAssignmentData.startDateTime), 'yyyy-MM-dd');
                
                if (massAssignmentData.onlyNotes) {
                    // Only Notes mode - use start as both start and end
                    freeFlowShift = {
                        start_datetime: massAssignmentData.startDateTime,
                        end_datetime: massAssignmentData.startDateTime,
                    };
                } else {
                    // Normal free-flow mode - use both start and end
                    freeFlowShift = {
                        start_datetime: massAssignmentData.startDateTime,
                        end_datetime: massAssignmentData.endDateTime,
                    };
                }
            }

            // Build attendance tags payload
            const tagsPayload = buildTagsPayload(massAssignmentData.attendanceTags, attendanceTags || []);
            const isFreeFlowAssignment = allowCustomShift && !massAssignmentData.selectedShift;

            // Build the request
            let request: AddScheduleV2Request;
            
            if (massAssignmentData.onlyNotes) {
                // Only Notes mode - only pass notes
                request = {
                    assign_to_employee_ids: massAssignmentData.selectedEmployees.map(e => e.value),
                    assign_to_group: massAssignmentData.selectedGroup ? [massAssignmentData.selectedGroup] : undefined,
                    assign_to_department: massAssignmentData.selectedDepartment ? [massAssignmentData.selectedDepartment] : undefined,
                    
                    shift_type: 'notes' as any, // Notes-only mode (API accepts "notes")
                    repeat_type: massAssignmentData.repeatFor === 'Weeks' ? 'WEEK' : 'DAY',
                    repeat_count: massAssignmentData.numberOfDays && massAssignmentData.numberOfDays > 0 ? massAssignmentData.numberOfDays : 1,
                    start_date: startDate,
                    
                    shift_attributes: {
                        logs: massAssignmentData.notes || undefined,
                        attendance_tags: tagsPayload.length > 0
                            ? tagsPayload.map((tag) => {
                                  // If no values, send empty string
                                  if (tag.field_values.length === 0) {
                                      return {
                                          code: tag.field_code,
                                          value: "",
                                      };
                                  }
                                  // If single value, send as string
                                  if (tag.field_values.length === 1) {
                                      return {
                                          code: tag.field_code,
                                          value: tag.field_values[0].value_text || tag.field_values[0].value_code || "",
                                      };
                                  }
                                  // If multiple values, send as array
                                  return {
                                      code: tag.field_code,
                                      value: tag.field_values.map((fieldValue) => 
                                          fieldValue.value_text || fieldValue.value_code || ""
                                      ),
                                  };
                              })
                            : undefined,
                    },
                };
            } else {
                // Normal mode - include all fields
                request = {
                    assign_to_employee_ids: massAssignmentData.selectedEmployees.map(e => e.value),
                    assign_to_group: massAssignmentData.selectedGroup ? [massAssignmentData.selectedGroup] : undefined,
                    assign_to_department: massAssignmentData.selectedDepartment ? [massAssignmentData.selectedDepartment] : undefined,
                    
                    shift_type: isFreeFlowAssignment ? 'free_flow' : 'template',
                    shift_code: massAssignmentData.selectedShift && selectedShift ? selectedShift.os_shift_code : undefined,
                    free_flow_shift: isFreeFlowAssignment ? freeFlowShift : undefined,
                    
                    repeat_type: massAssignmentData.repeatFor === 'Weeks' ? 'WEEK' : 'DAY',
                    repeat_count: massAssignmentData.numberOfDays && massAssignmentData.numberOfDays > 0 ? massAssignmentData.numberOfDays : 1,
                    start_date: startDate,
                    
                    shift_attributes: {
                        site_code: massAssignmentData.site || undefined,
                        department_code: massAssignmentData.department || undefined,
                        work_code: massAssignmentData.jobCode ? jobCodes?.find(j => j.os_work_codes_id === massAssignmentData.jobCode)?.work_code_number || undefined : undefined,
                        logs: massAssignmentData.notes || undefined,
                        shift_color: massAssignmentData.shift_color || undefined,
                        attendance_tags: tagsPayload.length > 0
                            ? tagsPayload.map((tag) => {
                                  // If no values, send empty string
                                  if (tag.field_values.length === 0) {
                                      return {
                                          code: tag.field_code,
                                          value: "",
                                      };
                                  }
                                  // If single value, send as string
                                  if (tag.field_values.length === 1) {
                                      return {
                                          code: tag.field_code,
                                          value: tag.field_values[0].value_text || tag.field_values[0].value_code || "",
                                      };
                                  }
                                  // If multiple values, send as array
                                  return {
                                      code: tag.field_code,
                                      value: tag.field_values.map((fieldValue) => 
                                          fieldValue.value_text || fieldValue.value_code || ""
                                      ),
                                  };
                              })
                            : undefined,
                    },
                };
            }

            const result = await dispatch(addScheduleV2(request));

            if (addScheduleV2.fulfilled.match(result)) {
                const payload = result.payload as any;
                const assignedCount = payload?.data?.assigned_count || payload?.assigned_count;
                const message = payload?.message || `Schedule assigned successfully!${assignedCount ? ` (${assignedCount} assigned)` : ''}`;
                showScheduleResultToast(payload, message);
                setShowMassAssignmentModal(false);

                // Reset form
                setMassAssignmentData({
                    selectedEmployees: [],
                    selectedGroup: '',
                    selectedDepartment: '',
                    onlyNotes: false,
                    selectedShift: '',
                    startDateTime: '',
                    endDateTime: '',
                    repeatFor: 'Days',
                    numberOfDays: 1,
                    showOptionalFields: false,
                    site: '',
                    department: '',
                    jobCode: null,
                    notes: '',
                    shift_color: '',
                    attendanceTags: {},
                });

                // Refresh the current view
                fetchDataForView(viewType, 
                    viewType === 'daily' ? currentDay : 
                    viewType === 'weekly' ? currentWeek : 
                    currentMonth
                );
            } else {
                const errorMessage = typeof result.payload === 'string' 
                    ? result.payload 
                    : (result.payload as any)?.message || 'Unknown error';
                toast.error(cleanErrorMessage(errorMessage));
            }
        } catch (error: any) {
            console.error('Error assigning schedule:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
            toast.error(cleanErrorMessage(errorMessage));
        } finally {
            setIsMassAssigning(false);
        }
    };

    // Mass Assignment Modal - memoized to prevent scroll issues
    const massAssignmentModalContent = useMemo(() => {
        if (!showMassAssignmentModal) return null;
        const useTemplateDateInput = massAssignmentData.selectedShift || (!allowCustomShift && !massAssignmentData.onlyNotes);

        return (
            <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[1400] p-0 md:p-4 overflow-y-auto">
                <div className="bg-white rounded-none md:rounded-xl shadow-2xl w-full max-w-5xl h-full md:h-auto md:max-h-[90vh] flex flex-col my-0 md:my-4">
                    {/* Header */}
                    <div className="px-4 md:px-6 py-3 md:py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                        <h3 className="text-base md:text-lg lg:text-xl font-bold text-gray-900">Shift mass assignment</h3>
                        <button
                            onClick={() => setShowMassAssignmentModal(false)}
                            className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
                        >
                            <X size={20} className="md:w-6 md:h-6" />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto px-4 md:px-6 py-4 md:py-6 space-y-4 md:space-y-6">
                        {/* Assign to Section */}
                        <div className="border border-gray-200 rounded-lg p-3 md:p-4">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
                                <h4 className="text-base md:text-lg font-semibold text-gray-900">Assign to:</h4>
                                {/* Only Notes toggle temporarily disabled */}
                                {/* <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={massAssignmentData.onlyNotes}
                                        onChange={(e) => setMassAssignmentData(prev => ({ ...prev, onlyNotes: e.target.checked }))}
                                        className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                                    />
                                    <span className="text-sm text-gray-700">Only Notes</span>
                                </label> */}
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
                                {/* Employee Search */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Search for employees
                                    </label>
                                    <AsyncSelect
                                        value={null}
                                        onChange={(selected: any) => {
                                            if (selected) {
                                                setMassAssignmentData(prev => ({
                                                    ...prev,
                                                    selectedEmployees: [...prev.selectedEmployees, selected]
                                                }));
                                            }
                                        }}
                                        loadOptions={loadEmployeeOptionsForFilter}
                                        placeholder="Search for employees"
                                        noOptionsMessage={({ inputValue }) =>
                                            inputValue.length < 3 ? "Type at least 3 characters to search" : "No employees found"
                                        }
                                        className="text-sm"
                                        classNamePrefix="react-select"
                                        isClearable
                                        isSearchable
                                        menuPortalTarget={document.body}
                                        styles={{
                                            menuPortal: base => ({ ...base, zIndex: 1500 }),
                                            menu: base => ({ ...base, zIndex: 1500 })
                                        }}
                                    />
                                    {massAssignmentData.selectedEmployees.length > 0 && (
                                        <div className="mt-2 flex flex-wrap gap-2">
                                            {massAssignmentData.selectedEmployees.map((employee) => (
                                                <div
                                                    key={employee.value}
                                                    className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-indigo-100 text-indigo-800"
                                                >
                                                    <span>{employee.label}</span>
                                                    <button
                                                        onClick={() => {
                                                            setMassAssignmentData(prev => ({
                                                                ...prev,
                                                                selectedEmployees: prev.selectedEmployees.filter(e => e.value !== employee.value)
                                                            }));
                                                        }}
                                                        className="ml-1 text-indigo-600 hover:text-indigo-800"
                                                    >
                                                        <X size={12} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Group Dropdown */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Group
                                    </label>
                                    <Select
                                        value={massAssignmentData.selectedGroup ? { value: massAssignmentData.selectedGroup, label: massAssignmentData.selectedGroup } : null}
                                        onChange={(selected: any) => {
                                            setMassAssignmentData(prev => ({ ...prev, selectedGroup: selected?.value || '' }));
                                        }}
                                        options={[
                                            { value: 'AS Group', label: 'AS Group' },
                                            { value: 'test group', label: 'test group' },
                                            { value: 'test group name', label: 'test group name' },
                                            { value: 'test001', label: 'test001' },
                                            { value: 'VS Group', label: 'VS Group' },
                                        ]}
                                        className="text-sm"
                                        classNamePrefix="react-select"
                                        placeholder="Select Group"
                                        isClearable
                                        menuPortalTarget={document.body}
                                        styles={{
                                            menuPortal: base => ({ ...base, zIndex: 1500 }),
                                            menu: base => ({ ...base, zIndex: 1500 })
                                        }}
                                    />
                                </div>

                                {/* Department Dropdown */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Department
                                    </label>
                                    <Select
                                        value={massAssignmentData.selectedDepartment ? 
                                            departments.filter(d => d.department_code === massAssignmentData.selectedDepartment)
                                                .map(d => ({ value: d.department_code!, label: d.department_name! }))[0] 
                                            : null
                                        }
                                        onChange={(selected: any) => {
                                            setMassAssignmentData(prev => ({ ...prev, selectedDepartment: selected?.value || '' }));
                                        }}
                                        options={departments
                                            .filter(dept => dept.department_code && dept.department_name)
                                            .map(dept => ({
                                                value: dept.department_code!,
                                                label: dept.department_name!
                                            }))}
                                        className="text-sm"
                                        classNamePrefix="react-select"
                                        placeholder="--Select Department--"
                                        isClearable
                                        menuPortalTarget={document.body}
                                        styles={{
                                            menuPortal: base => ({ ...base, zIndex: 1500 }),
                                            menu: base => ({ ...base, zIndex: 1500 })
                                        }}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Shift Details Section */}
                        <div className="border border-gray-200 rounded-lg p-3 md:p-4">
                            <h4 className="text-base md:text-lg font-semibold text-gray-900 mb-4">Shift Details:</h4>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
                                {/* Shift */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Shift
                                        {!massAssignmentData.onlyNotes && !allowCustomShift && <span className="text-red-500 ml-1">*</span>}
                                    </label>
                                    <Select
                                        value={massAssignmentData.selectedShift ? 
                                            allShifts.filter(s => s.os_schedules_site_shifts_id === massAssignmentData.selectedShift)
                                                .map(s => ({ value: s.os_schedules_site_shifts_id!, label: s.os_shift_name! }))[0]
                                            : null
                                        }
                                        onChange={(selected: any) => {
                                            const shiftId = selected?.value || '';
                                            setMassAssignmentData(prev => {
                                                const today = format(new Date(), 'yyyy-MM-dd');
                                                const selectedShiftObj = allShifts.find(s => s.os_schedules_site_shifts_id === shiftId);
                                                
                                                let startDateTime = prev.startDateTime || today;
                                                let endDateTime = prev.endDateTime || today;
                                                
                                                // If shift is selected, append shift times to dates
                                                if (selectedShiftObj && selectedShiftObj.os_shift_start_time && selectedShiftObj.os_shift_end_time) {
                                                    const startTime = selectedShiftObj.os_shift_start_time.substring(0, 5); // Get HH:mm
                                                    const endTime = selectedShiftObj.os_shift_end_time.substring(0, 5);
                                                    const datePart = startDateTime.includes('T') ? startDateTime.split('T')[0] : startDateTime;
                                                    startDateTime = `${datePart}T${startTime}`;
                                                    // Calculate end date based on numberOfDays and repeatFor (default to 1 if invalid)
                                                    const startDate = new Date(datePart);
                                                    const numberOfDays = prev.numberOfDays && prev.numberOfDays > 0 ? prev.numberOfDays : 1;
                                                    const daysToAdd = prev.repeatFor === 'Weeks' ? numberOfDays * 7 : numberOfDays;
                                                    const calculatedEndDate = addDays(startDate, daysToAdd - 1); // -1 because start day is included
                                                    endDateTime = `${format(calculatedEndDate, 'yyyy-MM-dd')}T${endTime}`;
                                                }
                                                
                                                return { 
                                                    ...prev, 
                                                    selectedShift: shiftId,
                                                    startDateTime,
                                                    endDateTime,
                                                };
                                            });
                                        }}
                                        options={allShifts
                                            .filter(shift => shift.os_schedules_site_shifts_id && shift.os_shift_name)
                                            .map(shift => ({
                                                value: shift.os_schedules_site_shifts_id!,
                                                label: shift.os_shift_name!
                                            }))}
                                        className="text-sm"
                                        classNamePrefix="react-select"
                                        placeholder="--Select Shift--"
                                        isClearable={allowCustomShift || massAssignmentData.onlyNotes}
                                        isDisabled={massAssignmentData.onlyNotes}
                                        menuPortalTarget={document.body}
                                        styles={{
                                            menuPortal: base => ({ ...base, zIndex: 1500 }),
                                            menu: base => ({ ...base, zIndex: 1500 })
                                        }}
                                    />
                                </div>

                                {/* Start Date/DateTime */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {useTemplateDateInput ? 'Start Date' : 'Start Date Time'} <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        {useTemplateDateInput ? (
                                            // When shift is selected, show only date picker
                                            <input
                                                type="date"
                                                value={massAssignmentData.startDateTime.includes('T') 
                                                    ? massAssignmentData.startDateTime.split('T')[0] 
                                                    : massAssignmentData.startDateTime}
                                                onChange={(e) => {
                                                    const selectedDate = e.target.value;
                                                    setMassAssignmentData(prev => {
                                                        // When shift is selected, update startDateTime with shift start time
                                                        const selectedShiftObj = allShifts.find(s => s.os_schedules_site_shifts_id === prev.selectedShift);
                                                        if (selectedShiftObj && selectedShiftObj.os_shift_start_time && selectedShiftObj.os_shift_end_time) {
                                                            const startTime = selectedShiftObj.os_shift_start_time.substring(0, 5);
                                                            const endTime = selectedShiftObj.os_shift_end_time.substring(0, 5);
                                                            const newStartDateTime = `${selectedDate}T${startTime}`;
                                                            // Calculate end date based on numberOfDays and repeatFor
                                                            const startDate = new Date(selectedDate);
                                                            const numberOfDays = prev.numberOfDays && prev.numberOfDays > 0 ? prev.numberOfDays : 1;
                                                            const daysToAdd = prev.repeatFor === 'Weeks' ? numberOfDays * 7 : numberOfDays;
                                                            const calculatedEndDate = addDays(startDate, daysToAdd - 1);
                                                            const newEndDateTime = `${format(calculatedEndDate, 'yyyy-MM-dd')}T${endTime}`;
                                                            return { ...prev, startDateTime: newStartDateTime, endDateTime: newEndDateTime };
                                                        }
                                                        // Fallback if shift times not available
                                                        return { ...prev, startDateTime: selectedDate };
                                                    });
                                                }}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                            />
                                        ) : (
                                            // When no shift selected (free-flow), show datetime picker
                                            <input
                                                type="datetime-local"
                                                value={massAssignmentData.startDateTime}
                                                onChange={(e) => setMassAssignmentData(prev => ({ ...prev, startDateTime: e.target.value }))}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                            />
                                        )}
                                    </div>
                                </div>

                                {/* End Date/DateTime - Only shown when no shift selected and not in "Only Notes" mode */}
                                {allowCustomShift && !massAssignmentData.onlyNotes && !massAssignmentData.selectedShift && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            End Date Time <span className="text-red-500">*</span>
                                        </label>
                                        <div className="relative">
                                            <input
                                                type="datetime-local"
                                                value={massAssignmentData.endDateTime}
                                                onChange={(e) => setMassAssignmentData(prev => ({ ...prev, endDateTime: e.target.value }))}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Assignment Details Section */}
                        <div className="border border-gray-200 rounded-lg p-4">
                            <h4 className="text-lg font-semibold text-gray-900 mb-4">Assignment Details:</h4>
                            
                            <div className="grid grid-cols-2 gap-4">
                                {/* Repeat for */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Repeat for
                                    </label>
                                    <Select
                                        value={{ value: massAssignmentData.repeatFor, label: massAssignmentData.repeatFor }}
                                        onChange={(selected: any) => {
                                            setMassAssignmentData(prev => {
                                                const newRepeatFor = selected?.value || 'Days';
                                                // If shift is selected, update end datetime based on new repeatFor value
                                                if (prev.selectedShift) {
                                                    const selectedShiftObj = allShifts.find(s => s.os_schedules_site_shifts_id === prev.selectedShift);
                                                    if (selectedShiftObj && selectedShiftObj.os_shift_start_time && selectedShiftObj.os_shift_end_time) {
                                                        const startDateStr = prev.startDateTime.includes('T') 
                                                            ? prev.startDateTime.split('T')[0] 
                                                            : prev.startDateTime;
                                                        const startDate = new Date(startDateStr);
                                                        const numberOfDays = prev.numberOfDays && prev.numberOfDays > 0 ? prev.numberOfDays : 1;
                                                        const daysToAdd = newRepeatFor === 'Weeks' ? numberOfDays * 7 : numberOfDays;
                                                        const calculatedEndDate = addDays(startDate, daysToAdd - 1);
                                                        const endTime = selectedShiftObj.os_shift_end_time.substring(0, 5);
                                                        const newEndDateTime = `${format(calculatedEndDate, 'yyyy-MM-dd')}T${endTime}`;
                                                        return { ...prev, repeatFor: newRepeatFor, endDateTime: newEndDateTime };
                                                    }
                                                }
                                                return { ...prev, repeatFor: newRepeatFor };
                                            });
                                        }}
                                        options={[
                                            { value: 'Days', label: 'Days' },
                                            { value: 'Weeks', label: 'Weeks' },
                                        ]}
                                        className="text-sm"
                                        classNamePrefix="react-select"
                                        isDisabled={massAssignmentData.onlyNotes}
                                        menuPortalTarget={document.body}
                                        styles={{
                                            menuPortal: base => ({ ...base, zIndex: 1500 }),
                                            menu: base => ({ ...base, zIndex: 1500 })
                                        }}
                                    />
                                </div>

                                {/* Number of days */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Number of days
                                    </label>
                                    <input
                                        type="number"
                                        min="1"
                                        value={massAssignmentData.numberOfDays || ''}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            // Allow empty string during editing for better UX
                                            if (value === '' || value === '-') {
                                                // Temporarily store empty/null to allow clearing
                                                setMassAssignmentData(prev => ({ ...prev, numberOfDays: null as any }));
                                                return;
                                            }
                                            
                                            const numValue = parseInt(value, 10);
                                            if (!isNaN(numValue) && numValue > 0) {
                                                setMassAssignmentData(prev => {
                                                    // If shift is selected, update end datetime based on new numberOfDays
                                                    if (prev.selectedShift) {
                                                        const selectedShiftObj = allShifts.find(s => s.os_schedules_site_shifts_id === prev.selectedShift);
                                                        if (selectedShiftObj && selectedShiftObj.os_shift_start_time && selectedShiftObj.os_shift_end_time) {
                                                            const startDateStr = prev.startDateTime.includes('T') 
                                                                ? prev.startDateTime.split('T')[0] 
                                                                : prev.startDateTime;
                                                            const startDate = new Date(startDateStr);
                                                            const daysToAdd = prev.repeatFor === 'Weeks' ? numValue * 7 : numValue;
                                                            const calculatedEndDate = addDays(startDate, daysToAdd - 1);
                                                            const endTime = selectedShiftObj.os_shift_end_time.substring(0, 5);
                                                            const newEndDateTime = `${format(calculatedEndDate, 'yyyy-MM-dd')}T${endTime}`;
                                                            return { ...prev, numberOfDays: numValue, endDateTime: newEndDateTime };
                                                        }
                                                    }
                                                    return { ...prev, numberOfDays: numValue };
                                                });
                                            }
                                        }}
                                        onBlur={(e) => {
                                            // Ensure minimum value of 1 when field loses focus (if empty, null, or invalid)
                                            const currentValue = massAssignmentData.numberOfDays;
                                            if (!currentValue || currentValue < 1 || isNaN(currentValue as any)) {
                                                setMassAssignmentData(prev => {
                                                    const newValue = 1;
                                                    // If shift is selected, also update end datetime with the default value
                                                    if (prev.selectedShift) {
                                                        const selectedShiftObj = allShifts.find(s => s.os_schedules_site_shifts_id === prev.selectedShift);
                                                        if (selectedShiftObj && selectedShiftObj.os_shift_end_time) {
                                                            const startDateStr = prev.startDateTime.split('T')[0];
                                                            const startDate = new Date(startDateStr);
                                                            const daysToAdd = prev.repeatFor === 'Weeks' ? newValue * 7 : newValue;
                                                            const calculatedEndDate = addDays(startDate, daysToAdd - 1);
                                                            const endTime = selectedShiftObj.os_shift_end_time.substring(0, 5);
                                                            const newEndDateTime = `${format(calculatedEndDate, 'yyyy-MM-dd')}T${endTime}`;
                                                            return { ...prev, numberOfDays: newValue, endDateTime: newEndDateTime };
                                                        }
                                                    }
                                                    return { ...prev, numberOfDays: newValue };
                                                });
                                            }
                                        }}
                                        placeholder="1"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Optional Fields */}
                        <div>
                            <button
                                onClick={() => setMassAssignmentData(prev => ({ ...prev, showOptionalFields: !prev.showOptionalFields }))}
                                className="text-blue-600 hover:text-blue-700 font-medium text-sm flex items-center gap-1"
                            >
                                <Plus size={16} />
                                Optional fields
                            </button>
                            
                            {massAssignmentData.showOptionalFields && (
                                <div className="mt-4 border border-gray-200 rounded-lg p-3 md:p-4 space-y-3 md:space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                                        {/* Site */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Site
                                            </label>
                                            <Select
                                                value={massAssignmentData.site ? 
                                                    sitesV2?.filter((site: any) => {
                                                        const siteValue = site.us_site_code || site.us_user_site_profile_id?.toString() || '';
                                                        return siteValue === massAssignmentData.site;
                                                    }).map((site: any) => ({
                                                        value: site.us_site_code || site.us_user_site_profile_id?.toString() || '',
                                                        label: site.user_site_name || ''
                                                    }))[0]
                                                    : null
                                                }
                                                onChange={(selected: any) => {
                                                    setMassAssignmentData(prev => ({ ...prev, site: selected?.value || '' }));
                                                }}
                                                options={sitesV2?.map((site: any) => ({
                                                    value: site.us_site_code || site.us_user_site_profile_id?.toString() || '',
                                                    label: site.user_site_name || ''
                                                })) || []}
                                                className="text-sm"
                                                classNamePrefix="react-select"
                                                placeholder="Select Site"
                                                isClearable
                                                isSearchable
                                                menuPortalTarget={document.body}
                                                styles={{
                                                    menuPortal: base => ({ ...base, zIndex: 1500 }),
                                                    menu: base => ({ ...base, zIndex: 1500 })
                                                }}
                                            />
                                        </div>

                                        {/* Department */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Department
                                            </label>
                                            <Select
                                                value={massAssignmentData.department ? { value: massAssignmentData.department, label: massAssignmentData.department } : null}
                                                onChange={(selected: any) => {
                                                    setMassAssignmentData(prev => ({ ...prev, department: selected?.value || '' }));
                                                }}
                                                options={departments
                                                    .filter(dept => dept.department_code && dept.department_name)
                                                    .map(dept => ({
                                                        value: dept.department_code!,
                                                        label: dept.department_name!
                                                    }))}
                                                className="text-sm"
                                                classNamePrefix="react-select"
                                                placeholder="Select Department"
                                                isClearable
                                                isSearchable
                                                menuPortalTarget={document.body}
                                                styles={{
                                                    menuPortal: base => ({ ...base, zIndex: 1500 }),
                                                    menu: base => ({ ...base, zIndex: 1500 })
                                                }}
                                            />
                                        </div>

                                        {/* Job Code */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Job Code
                                            </label>
                                            <Select
                                                value={massAssignmentData.jobCode ? 
                                                    jobCodes?.filter(j => j.os_work_codes_id === massAssignmentData.jobCode)
                                                        .map(j => ({ value: j.os_work_codes_id!, label: `${j.work_code_name} (${j.work_code_number})` }))[0]
                                                    : null
                                                }
                                                onChange={(selected: any) => {
                                                    setMassAssignmentData(prev => ({ ...prev, jobCode: selected?.value || null }));
                                                }}
                                                options={jobCodes
                                                    ?.filter(jobCode => jobCode.os_work_codes_id && jobCode.work_code_name)
                                                    .map(jobCode => ({
                                                        value: jobCode.os_work_codes_id!,
                                                        label: `${jobCode.work_code_name} (${jobCode.work_code_number})`
                                                    }))}
                                                className="text-sm"
                                                classNamePrefix="react-select"
                                                placeholder="Select Job Code"
                                                isClearable
                                                isSearchable
                                                menuPortalTarget={document.body}
                                                styles={{
                                                    menuPortal: base => ({ ...base, zIndex: 1500 }),
                                                    menu: base => ({ ...base, zIndex: 1500 })
                                                }}
                                            />
                                        </div>

                                        {/* Notes */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Notes
                                            </label>
                                            <textarea
                                                value={(massAssignmentData.notes === null || massAssignmentData.notes === 'null' || massAssignmentData.notes === undefined) ? '' : (massAssignmentData.notes || '')}
                                                onChange={(e) => setMassAssignmentData(prev => ({ ...prev, notes: e.target.value }))}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                                placeholder="Add notes..."
                                                rows={3}
                                            />
                                        </div>

                                        {/* Shift Color */}
                                        <div>
                                            <ColorPickerWithPalette
                                                value={massAssignmentData.shift_color || getSchedulingToolShiftColor()}
                                                onChange={(color) => {
                                                    // Save color preference to shift_planner_settings
                                                    if (color) {
                                                        setSchedulingToolShiftColor(color);
                                                    }
                                                    setMassAssignmentData(prev => ({ ...prev, shift_color: color }));
                                                }}
                                                label="Shift Color (Optional)"
                                            />
                                        </div>
                                    </div>

                                    {/* Attendance Tags Section */}
                                    {massAssignmentData.showOptionalFields && (
                                        <div className="mt-4 space-y-4 border-t border-gray-200 pt-4">
                                            <h4 className="text-sm font-semibold text-gray-700">Attendance Tags:</h4>
                                            {isLoadingAttendanceTags ? (
                                                <div className="flex items-center justify-center py-8">
                                                    <Loader2 className="animate-spin text-blue-500" size={24} />
                                                    <span className="ml-2 text-sm text-gray-600">Loading attendance tags...</span>
                                                </div>
                                            ) : attendanceTags && attendanceTags.length > 0 ? (
                                                attendanceTags.map((tag) => {
                                                const fieldType = tag.field_type?.toLowerCase();
                                                const isMultiselect = tag.multiselect === "true";

                                                // Parse options from field_json
                                                const getOptions = (
                                                    f: typeof tag
                                                ): { value_code: string; value_text: string }[] => {
                                                    if (!f.field_json) return [];

                                                    if (Array.isArray(f.field_json)) {
                                                        return f.field_json.map((item: any) => ({
                                                            value_code:
                                                                item.field_value_code || item.value_code || item.code || "",
                                                            value_text:
                                                                item.field_value_text ||
                                                                item.value_text ||
                                                                item.text ||
                                                                item.name ||
                                                                "",
                                                        }));
                                                    }

                                                    if (typeof f.field_json === "string") {
                                                        try {
                                                            const arr = JSON.parse(f.field_json);
                                                            if (Array.isArray(arr)) {
                                                                return arr.map((item: any) => ({
                                                                    value_code:
                                                                        item.field_value_code ||
                                                                        item.value_code ||
                                                                        item.code ||
                                                                        "",
                                                                    value_text:
                                                                        item.field_value_text ||
                                                                        item.value_text ||
                                                                        item.text ||
                                                                        item.name ||
                                                                        "",
                                                                }));
                                                            }
                                                        } catch (error) {
                                                            return [];
                                                        }
                                                    }

                                                    return [];
                                                };

                                                const options = getOptions(tag);
                                                const isRequired = tag.required === "true";

                                                const fieldValue = massAssignmentData.attendanceTags[tag.field_code] || [];

                                                // Dropdown field
                                                if (fieldType === "dropdown" && options.length > 0) {
                                                    // Multi-select dropdown - use react-select with isMulti
                                                    if (isMultiselect) {
                                                        const selectOptions = options.map((opt) => ({
                                                            value: opt.value_code,
                                                            label: opt.value_text,
                                                        }));

                                                        const selectedValues = Array.isArray(fieldValue)
                                                            ? fieldValue.map((v: any) => v.value_code || v)
                                                            : [];

                                                        const selectedOptions = selectOptions.filter((opt) =>
                                                            selectedValues.includes(opt.value)
                                                        );

                                                        return (
                                                            <div key={tag.field_code}>
                                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                    {tag.field_name}
                                                                    {isRequired && <span className="text-red-500 ml-1">*</span>}
                                                                </label>
                                                                <Select
                                                                    isMulti
                                                                    value={selectedOptions}
                                                                    onChange={(selected) => {
                                                                        const selectedArray = selected as Array<{ value: string; label: string }> | null;
                                                                        if (selectedArray) {
                                                                            const newValues = selectedArray.map((item) => ({
                                                                                value_code: item.value,
                                                                                value_text: item.label,
                                                                            }));
                                                                            handleMassAssignmentAttendanceFieldChange(tag.field_code, newValues);
                                                                        } else {
                                                                            handleMassAssignmentAttendanceFieldChange(tag.field_code, []);
                                                                        }
                                                                    }}
                                                                    options={selectOptions}
                                                                    placeholder={`Select ${tag.field_name}...`}
                                                                    className="text-sm"
                                                                    classNamePrefix="react-select"
                                                                    styles={{
                                                                        control: (base) => ({
                                                                            ...base,
                                                                            minHeight: '38px',
                                                                            fontSize: '14px',
                                                                        }),
                                                                        menu: (base) => ({
                                                                            ...base,
                                                                            fontSize: '14px',
                                                                        }),
                                                                    }}
                                                                />
                                                                {tag.field_description && (
                                                                    <p className="text-xs text-gray-500 mt-1">
                                                                        {tag.field_description}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        );
                                                    }

                                                    // Single-select dropdown - use select element
                                                    const currentValue =
                                                        Array.isArray(fieldValue) && fieldValue.length > 0
                                                            ? fieldValue[0].value_code
                                                            : "";

                                                    return (
                                                        <div key={tag.field_code}>
                                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                {tag.field_name}
                                                                {isRequired && <span className="text-red-500 ml-1">*</span>}
                                                            </label>
                                                            <select
                                                                value={currentValue}
                                                                onChange={(e) => {
                                                                    const selectedOption = options.find(
                                                                        (opt) => opt.value_code === e.target.value
                                                                    );
                                                                    if (selectedOption) {
                                                                        // Store as array with single item
                                                                        handleMassAssignmentAttendanceFieldChange(tag.field_code, [
                                                                            {
                                                                                value_code: selectedOption.value_code,
                                                                                value_text: selectedOption.value_text,
                                                                            },
                                                                        ]);
                                                                    } else {
                                                                        handleMassAssignmentAttendanceFieldChange(tag.field_code, []);
                                                                    }
                                                                }}
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                            >
                                                                <option value="">Select {tag.field_name}...</option>
                                                                {options.map((option, index) => (
                                                                    <option key={index} value={option.value_code}>
                                                                        {option.value_text}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                            {tag.field_description && (
                                                                <p className="text-xs text-gray-500 mt-1">
                                                                    {tag.field_description}
                                                                </p>
                                                            )}
                                                        </div>
                                                    );
                                                }

                                                // Text field (default)
                                                const currentTextValue =
                                                    Array.isArray(fieldValue) && fieldValue.length > 0
                                                        ? fieldValue[0].value_text
                                                        : "";

                                                return (
                                                    <div key={tag.field_code}>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                                            {tag.field_name}
                                                            {isRequired && <span className="text-red-500 ml-1">*</span>}
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={currentTextValue}
                                                            onChange={(e) => {
                                                                const value = e.target.value;
                                                                // Store as array with single item, using field_code as value_code for text fields
                                                                handleMassAssignmentAttendanceFieldChange(
                                                                    tag.field_code,
                                                                    value
                                                                        ? [
                                                                              {
                                                                                  value_code: tag.field_code,
                                                                                  value_text: value,
                                                                              },
                                                                          ]
                                                                        : []
                                                                );
                                                            }}
                                                            placeholder={`Enter ${tag.field_name}`}
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                        />
                                                        {tag.field_description && (
                                                            <p className="text-xs text-gray-500 mt-1">
                                                                {tag.field_description}
                                                            </p>
                                                        )}
                                                    </div>
                                                );
                                            })) : (
                                                <div className="text-sm text-gray-500 py-4">No attendance tags available.</div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="px-4 md:px-6 py-3 md:py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl flex-shrink-0">
                        <div className="flex justify-end">
                            <button
                                onClick={handleMassAssignment}
                                disabled={isMassAssigning || isLoadingAttendanceTags}
                                className="px-4 md:px-6 py-2 bg-transparent border border-indigo-600 text-indigo-700 rounded-lg hover:bg-indigo-50 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm md:text-base"
                            >
                                {isMassAssigning ? (
                                    <>
                                        <Loader2 className="animate-spin" size={16} />
                                        Assigning...
                                    </>
                                ) : (
                                    'Assign Shift'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }, [
        showMassAssignmentModal,
        massAssignmentData,
        isMassAssigning,
        allowCustomShift,
        allShifts,
        departments,
        sitesV2,
        jobCodes,
        attendanceTags,
        isLoadingAttendanceTags,
        loadEmployeeOptionsForFilter,
        handleMassAssignment,
        handleMassAssignmentAttendanceFieldChange,
    ]);

    // Options for Publish Schedule Modal (department/site dropdowns)
    const publishModalDepartmentOptions = useMemo(
        () =>
            (departments || []).map((dept: any) => ({
                value: dept.department_code || dept.code || "",
                label: dept.department_name || dept.name || "",
            })),
        [departments]
    );
    const publishModalSiteOptions = useMemo(
        () =>
            (sitesV2 || []).map((site: any) => ({
                value: site.us_site_code || site.us_user_site_profile_id?.toString() || "",
                label: site.user_site_name || "",
            })),
        [sitesV2]
    );
    const publishModalDefaultDates = useMemo(() => {
        const { startDate, endDate } = getDateRange(
            viewType,
            viewType === "daily" ? currentDay : viewType === "weekly" ? currentWeek : currentMonth
        );
        return { startDate: format(startDate, "yyyy-MM-dd"), endDate: format(endDate, "yyyy-MM-dd") };
    }, [viewType, currentDay, currentWeek, currentMonth]);

    // Publish Confirmation Modal (Open Shifts only)
    const PublishConfirmationModal = () => {
        // Initialize date range when modal opens
        useEffect(() => {
            if (showPublishConfirmation) {
                console.log('PublishConfirmationModal: Modal opened, showPublishConfirmation:', showPublishConfirmation);
                // Get default date range from current view
                const { startDate: defaultStart, endDate: defaultEnd } = getDateRange(viewType, 
                    viewType === 'daily' ? currentDay : 
                    viewType === 'weekly' ? currentWeek : 
                    currentMonth
                );
                
                // Only set if not already set
                if (!publishDateRange.startDate) {
                    console.log('PublishConfirmationModal: Setting date range');
                    setPublishDateRange({
                        startDate: format(defaultStart, 'yyyy-MM-dd'),
                        endDate: format(defaultEnd, 'yyyy-MM-dd'),
                    });
                }
            }
        }, [showPublishConfirmation]);

        if (!showPublishConfirmation || publishType !== "open_shifts") return null;

        return (
            <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[1400] p-2 md:p-4">
                <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-2 md:mx-0">
                    {/* Header */}
                    <div className="px-4 md:px-6 py-3 md:py-4 border-b border-gray-200 flex items-center justify-between">
                        <div className="flex items-center gap-2 md:gap-3 min-w-0">
                            <div className="flex items-center justify-center w-8 h-8 md:w-10 md:h-10 bg-green-100 rounded-lg flex-shrink-0">
                                <Send className="w-4 h-4 md:w-5 md:h-5 text-green-600" />
                            </div>
                            <h3 className="text-sm md:text-lg font-bold text-gray-900 truncate">
                                Publish Open Shifts
                            </h3>
                        </div>
                        <button
                            onClick={() => {
                                setShowPublishConfirmation(false);
                                setPublishDateRange({ startDate: '', endDate: '' });
                            }}
                            className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
                        >
                            <X size={18} className="md:w-5 md:h-5" />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="px-4 md:px-6 py-3 md:py-4 space-y-3 md:space-y-4">
                        <p className="text-gray-600 text-sm">
                            Select the date range for publishing open shifts:
                        </p>

                        <div className="space-y-3">
                            {/* Start Date */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Start Date
                                </label>
                                <input
                                    type="date"
                                    value={publishDateRange.startDate}
                                    onChange={(e) => setPublishDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                />
                            </div>

                            {/* End Date */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    End Date
                                </label>
                                <input
                                    type="date"
                                    value={publishDateRange.endDate}
                                    onChange={(e) => setPublishDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                />
                            </div>
                        </div>

                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <p className="text-xs text-blue-800">
                                <strong>Note:</strong> Published open shifts will be available for employees to claim.
                            </p>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="px-4 md:px-6 py-3 md:py-4 bg-gray-50 border-t border-gray-200 rounded-b-xl flex gap-2 md:gap-3">
                        <button
                            onClick={() => {
                                setShowPublishConfirmation(false);
                                setPublishDateRange({ startDate: '', endDate: '' });
                            }}
                            className="flex-1 px-3 md:px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium text-sm md:text-base"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handlePublish}
                            disabled={isPublishing || !publishDateRange.startDate || !publishDateRange.endDate}
                            className="flex-1 px-3 md:px-4 py-2 bg-transparent border border-green-600 text-green-700 rounded-lg hover:bg-green-50 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm md:text-base"
                        >
                            {isPublishing ? (
                                <>
                                    <Loader2 className="animate-spin" size={16} />
                                    Publishing...
                                </>
                            ) : (
                                <>
                                    <Send size={16} />
                                    Publish
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // Success Notification
    const PublishSuccessNotification = () => {
        if (!showPublishSuccess) return null;

        return (
            <div className="fixed top-4 right-4 z-[1500] animate-in slide-in-from-top duration-300">
                <div className="bg-white rounded-lg shadow-2xl border-l-4 border-green-500 p-4 flex items-center gap-3 min-w-[320px]">
                    <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                        <CheckCircle className="w-6 h-6 text-green-600" />
                    </div>
                    <div className="flex-1">
                        <h4 className="font-semibold text-gray-900 mb-0.5">Published!</h4>
                        <p className="text-sm text-gray-600">{publishMessage}</p>
                    </div>
                    <button
                        onClick={() => setShowPublishSuccess(false)}
                        className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <X size={18} />
                    </button>
                </div>
            </div>
        );
    };

    // Assign Published Shifts Modal
    const AssignPublishedShiftsModal = () => {
        if (!showAssignShiftsModal) return null;

        return (
            <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[1400] p-0 md:p-4 overflow-y-auto">
                <div className="bg-white rounded-none md:rounded-xl shadow-2xl w-full max-w-4xl h-full md:h-auto md:max-h-[90vh] flex flex-col my-0 md:my-4">
                    {/* Header */}
                    <div className="px-4 md:px-6 py-3 md:py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                        <div className="flex items-center gap-2 md:gap-3">
                            <div className="w-8 h-8 md:w-10 md:h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                <Users className="w-4 h-4 md:w-5 md:h-5 text-purple-600" />
                            </div>
                            <div className="min-w-0">
                                <h3 className="text-base md:text-lg font-bold text-gray-900 truncate">Assign Published Open Shifts</h3>
                                <p className="text-xs md:text-sm text-gray-500 hidden sm:block truncate">Select employees for available open shifts</p>
                            </div>
                        </div>
                        <button
                            onClick={() => setShowAssignShiftsModal(false)}
                            className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
                        >
                            <X size={24} />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto px-6 py-4">
                        {isLoadingShifts ? (
                            <div className="flex flex-col items-center justify-center py-12">
                                <Loader2 className="animate-spin text-purple-500 w-8 h-8 mb-3" />
                                <p className="text-gray-600">Loading published shifts...</p>
                            </div>
                        ) : publishedShifts.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-12">
                                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                                    <Calendar className="w-8 h-8 text-gray-400" />
                                </div>
                                <p className="text-gray-600 font-medium">No published open shifts found</p>
                                <p className="text-sm text-gray-500 mt-1">Try publishing open shifts first</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {publishedShifts.map((shift) => (
                                    <div
                                        key={shift.os_sch_open_shift_transactions_id}
                                        className="border border-gray-200 rounded-lg p-4 hover:border-purple-300 transition-colors"
                                    >
                                        <div className="flex items-start justify-between mb-3">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <h4 className="font-semibold text-gray-900">
                                                        {shift.shift_name}
                                                    </h4>
                                                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                                                        {shift.shift_code}
                                                    </span>
                                                </div>
                                                <p className="text-sm text-gray-600">
                                                    {shift.formatted_date} • {shift.other_details_json.shift_start_time} - {shift.other_details_json.shift_end_time}
                                                </p>
                                                <p className="text-xs text-gray-500 mt-1">
                                                    Position {shift.other_details_json.position_number} of {shift.other_details_json.position_count}
                                                </p>
                                            </div>
                                        </div>

                                        {/* Employee Selection */}
                                        <div className="mt-3">
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Assign to Employee
                                            </label>
                                            {shift.eligible_employees.length === 0 ? (
                                                <div className="text-sm text-gray-500 italic">
                                                    No eligible employees available
                                                </div>
                                            ) : (
                                                <AsyncSelect
                                                    value={
                                                        selectedAssignments[shift.os_sch_open_shift_transactions_id]
                                                            ? {
                                                                value: selectedAssignments[shift.os_sch_open_shift_transactions_id],
                                                                label: shift.eligible_employees.find(
                                                                    e => e.emp_id === selectedAssignments[shift.os_sch_open_shift_transactions_id]
                                                                )?.emp_name || ''
                                                            }
                                                            : null
                                                    }
                                                    onChange={(selected: any) => {
                                                        setSelectedAssignments(prev => ({
                                                            ...prev,
                                                            [shift.os_sch_open_shift_transactions_id]: selected?.value || ''
                                                        }));
                                                    }}
                                                    loadOptions={(inputValue) => loadEmployeeOptionsForShift(inputValue, shift.os_sch_open_shift_transactions_id)}
                                                    placeholder="Search employee (min 3 characters)..."
                                                    noOptionsMessage={({ inputValue }) => 
                                                        inputValue.length < 3 ? "Type at least 3 characters to search" : "No eligible employees found"
                                                    }
                                                    className="text-sm"
                                                    classNamePrefix="react-select"
                                                    isClearable
                                                    isSearchable
                                                    defaultOptions={shift.eligible_employees.map(emp => ({
                                                        value: emp.emp_id,
                                                        label: emp.emp_name
                                                    }))}
                                                    menuPortalTarget={document.body}
                                                    menuPosition="fixed"
                                                    styles={{
                                                        control: (base) => ({
                                                            ...base,
                                                            minHeight: '42px',
                                                            borderRadius: '0.5rem',
                                                            borderColor: '#d1d5db',
                                                            '&:hover': {
                                                                borderColor: '#9ca3af'
                                                            }
                                                        }),
                                                        menuPortal: (base) => ({
                                                            ...base,
                                                            zIndex: 1500
                                                        }),
                                                        menu: (base) => ({
                                                            ...base,
                                                            zIndex: 1500
                                                        })
                                                    }}
                                                />
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    {!isLoadingShifts && publishedShifts.length > 0 && (
                        <div className="px-4 md:px-6 py-3 md:py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl flex-shrink-0">
                            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                                <div className="text-xs md:text-sm text-gray-600">
                                    {Object.values(selectedAssignments).filter(v => v !== '').length} of {publishedShifts.length} shifts assigned
                                </div>
                                <div className="flex gap-2 md:gap-3 w-full sm:w-auto">
                                    <button
                                        onClick={() => setShowAssignShiftsModal(false)}
                                        className="flex-1 sm:flex-none px-3 md:px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium text-sm md:text-base"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleAssignShifts}
                                        disabled={isAssigningShifts || Object.values(selectedAssignments).filter(v => v !== '').length === 0}
                                        className="flex-1 sm:flex-none px-3 md:px-4 py-2 bg-transparent border border-purple-600 text-purple-700 rounded-lg hover:bg-purple-50 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm md:text-base"
                                    >
                                        {isAssigningShifts ? (
                                            <>
                                                <Loader2 className="animate-spin" size={16} />
                                                Assigning...
                                            </>
                                        ) : (
                                            <>
                                                <CheckCircle size={16} />
                                                Assign Shifts
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // Filter Modal Component
    const FilterModal = () => {
        if (!showFilterModal) return null;

        // Format options for dropdowns
        const departmentOptions = departments.map((dept: any) => ({
            value: dept.department_code || dept.code || '',
            label: dept.department_name || dept.name || ''
        }));

        const siteOptions = sitesV2?.map((site: any) => ({
            value: site.us_site_code || site.us_user_site_profile_id?.toString() || '',
            label: site.user_site_name || ''
        })) || [];

        const shiftOptions = allShifts
            .filter((shift: any) => shift.os_schedules_site_shifts_id && shift.os_shift_name)
            .map((shift: any) => ({
                value: shift.os_schedules_site_shifts_id?.toString() || '',
                label: shift.os_shift_name || ''
            }));

        const groupOptions = groupsList.map((group: any) => ({
            value: group.group_code || '',
            label: group.group_name || group.group_code || ''
        }));

        return (
            <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[1400] p-0 md:p-4 overflow-y-auto">
                <div className="bg-white rounded-none md:rounded-xl shadow-2xl w-full max-w-4xl h-full md:h-auto md:max-h-[90vh] flex flex-col my-0 md:my-4">
                    {/* Header */}
                    <div className="px-4 md:px-6 py-3 md:py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                        <div className="flex items-center gap-2 md:gap-3">
                            <div className="w-8 h-8 md:w-10 md:h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                <Filter className="w-4 h-4 md:w-5 md:h-5 text-blue-600" />
                            </div>
                            <div className="min-w-0">
                                <h3 className="text-base md:text-lg font-bold text-gray-900 truncate">Filter Schedules</h3>
                                <p className="text-xs md:text-sm text-gray-500 hidden sm:block">Refine your schedule view</p>
                            </div>
                        </div>
                        <button
                            onClick={() => setShowFilterModal(false)}
                            className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
                        >
                            <X size={20} className="md:w-6 md:h-6" />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto px-4 md:px-6 py-3 md:py-4 space-y-3 md:space-y-4">
                        {/* Employee Search */}
                        <div>
                            <div className="flex justify-between items-center mb-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Employees
                                </label>
                                {selectedEmployees.length > 0 && (
                                    <button
                                        className="text-xs text-blue-500 hover:text-blue-700"
                                        onClick={() => {
                                            setSelectedEmployees([]);
                                            setFilterValues(prev => ({ ...prev, employeeIds: [] }));
                                        }}
                                    >
                                        Clear
                                    </button>
                                )}
                            </div>
                            {selectedEmployees.length > 0 && (
                                <div className="mb-3 flex flex-wrap gap-2">
                                    {selectedEmployees.map((employee) => (
                                        <div
                                            key={employee.value}
                                            className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                                        >
                                            <span>{employee.label}</span>
                                            <button
                                                onClick={() => {
                                                    const updated = selectedEmployees.filter(e => e.value !== employee.value);
                                                    setSelectedEmployees(updated);
                                                    setFilterValues(prev => ({
                                                        ...prev,
                                                        employeeIds: updated.map(e => e.value)
                                                    }));
                                                }}
                                                className="ml-2 text-blue-600 hover:text-blue-800"
                                            >
                                                <X size={14} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <AsyncSelect
                                value={null}
                                onChange={(selected: any) => {
                                    if (selected) {
                                        const updated = [...selectedEmployees, selected];
                                        setSelectedEmployees(updated);
                                        setFilterValues(prev => ({
                                            ...prev,
                                            employeeIds: updated.map(e => e.value)
                                        }));
                                    }
                                }}
                                loadOptions={loadEmployeeOptionsForFilter}
                                placeholder="Search employees (min 3 characters)..."
                                noOptionsMessage={({ inputValue }) =>
                                    inputValue.length < 3 ? "Type at least 3 characters to search" : "No employees found"
                                }
                                className="text-sm"
                                classNamePrefix="react-select"
                                isClearable
                                isSearchable
                                menuPortalTarget={document.body}
                                styles={{
                                    menuPortal: (base) => ({
                                        ...base,
                                        zIndex: 1500
                                    }),
                                    menu: (base) => ({
                                        ...base,
                                        zIndex: 1500
                                    })
                                }}
                            />
                        </div>

                        {/* Departments */}
                        <div>
                            <div className="flex justify-between items-center mb-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Departments
                                </label>
                                {filterValues.departmentCodes.length > 0 && (
                                    <button
                                        className="text-xs text-blue-500 hover:text-blue-700"
                                        onClick={() => setFilterValues(prev => ({ ...prev, departmentCodes: [] }))}
                                    >
                                        Clear
                                    </button>
                                )}
                            </div>
                            <Select
                                isMulti
                                value={departmentOptions.filter(opt => filterValues.departmentCodes.includes(opt.value))}
                                onChange={(selected) => {
                                    setFilterValues(prev => ({
                                        ...prev,
                                        departmentCodes: (selected || []).map((s: any) => s.value)
                                    }));
                                }}
                                options={departmentOptions}
                                className="text-sm"
                                classNamePrefix="react-select"
                                placeholder="Select departments..."
                                menuPortalTarget={document.body}
                                styles={{
                                    menuPortal: (base) => ({
                                        ...base,
                                        zIndex: 1500
                                    }),
                                    menu: (base) => ({
                                        ...base,
                                        zIndex: 1500
                                    })
                                }}
                            />
                        </div>

                        {/* Sites */}
                        <div>
                            <div className="flex justify-between items-center mb-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Sites
                                </label>
                                {filterValues.siteIds.length > 0 && (
                                    <button
                                        className="text-xs text-blue-500 hover:text-blue-700"
                                        onClick={() => setFilterValues(prev => ({ ...prev, siteIds: [] }))}
                                    >
                                        Clear
                                    </button>
                                )}
                            </div>
                            <Select
                                isMulti
                                value={siteOptions.filter(opt => filterValues.siteIds.includes(opt.value))}
                                onChange={(selected) => {
                                    setFilterValues(prev => ({
                                        ...prev,
                                        siteIds: (selected || []).map((s: any) => s.value)
                                    }));
                                }}
                                options={siteOptions}
                                className="text-sm"
                                classNamePrefix="react-select"
                                placeholder="Select sites..."
                                menuPortalTarget={document.body}
                                styles={{
                                    menuPortal: (base) => ({
                                        ...base,
                                        zIndex: 1500
                                    }),
                                    menu: (base) => ({
                                        ...base,
                                        zIndex: 1500
                                    })
                                }}
                            />
                        </div>

                        {/* Shifts */}
                        <div>
                            <div className="flex justify-between items-center mb-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Shifts
                                </label>
                                {filterValues.shiftIds.length > 0 && (
                                    <button
                                        className="text-xs text-blue-500 hover:text-blue-700"
                                        onClick={() => setFilterValues(prev => ({ ...prev, shiftIds: [] }))}
                                    >
                                        Clear
                                    </button>
                                )}
                            </div>
                            <Select
                                isMulti
                                value={shiftOptions.filter(opt => filterValues.shiftIds.includes(opt.value))}
                                onChange={(selected) => {
                                    setFilterValues(prev => ({
                                        ...prev,
                                        shiftIds: (selected || []).map((s: any) => s.value)
                                    }));
                                }}
                                options={shiftOptions}
                                className="text-sm"
                                classNamePrefix="react-select"
                                placeholder="Select shifts..."
                                menuPortalTarget={document.body}
                                styles={{
                                    menuPortal: (base) => ({
                                        ...base,
                                        zIndex: 1500
                                    }),
                                    menu: (base) => ({
                                        ...base,
                                        zIndex: 1500
                                    })
                                }}
                            />
                        </div>

                        {/* Groups */}
                        <div>
                            <div className="flex justify-between items-center mb-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Groups
                                </label>
                                {filterValues.groupCodes.length > 0 && (
                                    <button
                                        className="text-xs text-blue-500 hover:text-blue-700"
                                        onClick={() => setFilterValues(prev => ({ ...prev, groupCodes: [] }))}
                                    >
                                        Clear
                                    </button>
                                )}
                            </div>
                            <Select
                                isMulti
                                value={groupOptions.filter(opt => filterValues.groupCodes.includes(opt.value))}
                                onChange={(selected) => {
                                    setFilterValues(prev => ({
                                        ...prev,
                                        groupCodes: (selected || []).map((s: any) => s.value)
                                    }));
                                }}
                                options={groupOptions}
                                className="text-sm"
                                classNamePrefix="react-select"
                                placeholder="Select groups..."
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                menuShouldScrollIntoView={false}
                                styles={{
                                    menuPortal: (base) => ({
                                        ...base,
                                        zIndex: 1500
                                    }),
                                    menu: (base) => ({
                                        ...base,
                                        zIndex: 1500,
                                        maxHeight: '250px',
                                        overflow: 'hidden'
                                    }),
                                    menuList: (base) => ({
                                        ...base,
                                        maxHeight: '250px',
                                        overflowY: 'auto',
                                        overflowX: 'hidden'
                                    })
                                }}
                            />
                        </div>

                        {/* Schedule Date Range */}
                        {/* <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Schedule Start Date
                                </label>
                                <input
                                    type="date"
                                    value={filterValues.scheduleStartDate ? format(filterValues.scheduleStartDate, 'yyyy-MM-dd') : ''}
                                    onChange={(e) => {
                                        setFilterValues(prev => ({
                                            ...prev,
                                            scheduleStartDate: e.target.value ? new Date(e.target.value) : null
                                        }));
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Schedule End Date
                                </label>
                                <input
                                    type="date"
                                    value={filterValues.scheduleEndDate ? format(filterValues.scheduleEndDate, 'yyyy-MM-dd') : ''}
                                    onChange={(e) => {
                                        setFilterValues(prev => ({
                                            ...prev,
                                            scheduleEndDate: e.target.value ? new Date(e.target.value) : null
                                        }));
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                />
                            </div>
                        </div> */}
                    </div>

                    {/* Footer */}
                    <div className="px-4 md:px-6 py-3 md:py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl flex-shrink-0">
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2">
                            <button
                                onClick={handleClearAllFilters}
                                className="px-3 md:px-4 py-2 text-xs md:text-sm text-gray-700 hover:text-gray-900 font-medium text-left sm:text-center"
                            >
                                Clear All Filters
                            </button>
                            <div className="flex gap-2 md:gap-3">
                                <button
                                    onClick={() => setShowFilterModal(false)}
                                    className="flex-1 sm:flex-none px-3 md:px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium text-sm md:text-base"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleApplyFilters}
                                    className="flex-1 sm:flex-none px-3 md:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center justify-center gap-2 text-sm md:text-base"
                                >
                                    <CheckCircle size={16} />
                                    Apply Filters
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Options Menu Component (Hamburger Menu)
    const OptionsMenu = () => {
        return (
            <div className="relative flex-shrink-0">
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsOptionsOpen(!isOptionsOpen);
                    }}
                    className="text-gray-600 p-2 rounded-lg hover:bg-gray-100 transition-colors relative"
                >
                    <Menu size={20} />
                    {(includeDept !== IncludeDeptType.MY_SCHEDULE || (viewType !== 'monthly' && groupingType !== 'department')) && (
                        <div className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 rounded-full border-2 border-white"></div>
                    )}
                </button>
                {isOptionsOpen && (
                    <div 
                        data-desktop-options-menu
                        className="hidden md:block absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-[9999] min-w-[220px]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="px-4 py-2 border-b border-gray-100">
                            <div className="text-xs font-medium text-gray-500 mb-2">VIEW TYPE</div>
                            <div className="space-y-2">
                                <label 
                                    className="flex items-center cursor-pointer"
                                    onClick={() => setViewTypeWithPersistence('daily')}
                                >
                                    <input
                                        type="radio"
                                        name="viewType"
                                        checked={viewType === 'daily'}
                                        onChange={() => setViewTypeWithPersistence('daily')}
                                        className="w-4 h-4 text-purple-600 border-gray-300 focus:ring-purple-500"
                                    />
                                    <span className="ml-2 text-sm text-gray-700">Daily</span>
                                </label>
                                <label 
                                    className="flex items-center cursor-pointer"
                                    onClick={() => setViewTypeWithPersistence('weekly')}
                                >
                                    <input
                                        type="radio"
                                        name="viewType"
                                        checked={viewType === 'weekly'}
                                        onChange={() => setViewTypeWithPersistence('weekly')}
                                        className="w-4 h-4 text-purple-600 border-gray-300 focus:ring-purple-500"
                                    />
                                    <span className="ml-2 text-sm text-gray-700">Weekly</span>
                                </label>
                                <label 
                                    className="flex items-center cursor-pointer"
                                    onClick={() => setViewTypeWithPersistence('monthly')}
                                >
                                    <input
                                        type="radio"
                                        name="viewType"
                                        checked={viewType === 'monthly'}
                                        onChange={() => setViewTypeWithPersistence('monthly')}
                                        className="w-4 h-4 text-purple-600 border-gray-300 focus:ring-purple-500"
                                    />
                                    <span className="ml-2 text-sm text-gray-700">Monthly</span>
                                </label>
                            </div>
                        </div>
                        <div className="px-4 py-2 border-b border-gray-100">
                            <div className="text-xs font-medium text-gray-500 mb-2">VIEW SCHEDULES</div>
                            <div className="space-y-2">
                                <label 
                                    className="flex items-center cursor-pointer"
                                    onClick={() => setIncludeDeptWithPersistence(IncludeDeptType.MY_SCHEDULE)}
                                >
                                    <input
                                        type="radio"
                                        name="includeDept"
                                        checked={includeDept === IncludeDeptType.MY_SCHEDULE}
                                        onChange={() => setIncludeDeptWithPersistence(IncludeDeptType.MY_SCHEDULE)}
                                        className="w-4 h-4 text-purple-600 border-gray-300 focus:ring-purple-500"
                                    />
                                    <span className="ml-2 text-sm text-gray-700">My schedules only</span>
                                </label>
                                <label 
                                    className="flex items-center cursor-pointer"
                                    onClick={() => setIncludeDeptWithPersistence(IncludeDeptType.MY_DEPARTMENT)}
                                >
                                    <input
                                        type="radio"
                                        name="includeDept"
                                        checked={includeDept === IncludeDeptType.MY_DEPARTMENT}
                                        onChange={() => setIncludeDeptWithPersistence(IncludeDeptType.MY_DEPARTMENT)}
                                        className="w-4 h-4 text-purple-600 border-gray-300 focus:ring-purple-500"
                                    />
                                    <span className="ml-2 text-sm text-gray-700">My department</span>
                                </label>
                                <label 
                                    className="flex items-center cursor-pointer"
                                    onClick={() => setIncludeDeptWithPersistence(IncludeDeptType.ALL)}
                                >
                                    <input
                                        type="radio"
                                        name="includeDept"
                                        checked={includeDept === IncludeDeptType.ALL}
                                        onChange={() => setIncludeDeptWithPersistence(IncludeDeptType.ALL)}
                                        className="w-4 h-4 text-purple-600 border-gray-300 focus:ring-purple-500"
                                    />
                                    <span className="ml-2 text-sm text-gray-700">All schedules</span>
                                </label>
                            </div>
                        </div>
                        <div className="px-4 py-2 border-b border-gray-100">
                            <div className="text-xs font-medium text-gray-500 mb-2">FILTER OPTIONS</div>
                            <div className="space-y-2">
                                <label className="flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={showSchedule}
                                        onChange={(e) => {
                                            const newValue = e.target.checked;
                                            setShowSchedule(newValue);
                                            localStorage.setItem("scheduling_tool_show_schedule", String(newValue));
                                        }}
                                        className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                                    />
                                    <span className="ml-2 text-sm text-gray-700">Schedule</span>
                                </label>
                                <label className="flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={showTimeoff}
                                        onChange={(e) => {
                                            const newValue = e.target.checked;
                                            setShowTimeoff(newValue);
                                            localStorage.setItem("scheduling_tool_show_timeoff", String(newValue));
                                        }}
                                        className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                                    />
                                    <span className="ml-2 text-sm text-gray-700">Timeoff</span>
                                </label>
                                <label className="flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={showHoliday}
                                        onChange={(e) => {
                                            const newValue = e.target.checked;
                                            setShowHoliday(newValue);
                                            localStorage.setItem("scheduling_tool_show_holiday", String(newValue));
                                        }}
                                        className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                                    />
                                    <span className="ml-2 text-sm text-gray-700">Holiday</span>
                                </label>
                                <label className="flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={showUnavailability}
                                        onChange={(e) => {
                                            const newValue = e.target.checked;
                                            setShowUnavailability(newValue);
                                            localStorage.setItem("scheduling_tool_show_unavailability", String(newValue));
                                        }}
                                        className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                                    />
                                    <span className="ml-2 text-sm text-gray-700">Unavailability</span>
                                </label>
                                <label className="flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={showOpenShifts}
                                        onChange={(e) => {
                                            const newValue = e.target.checked;
                                            setShowOpenShifts(newValue);
                                            localStorage.setItem("scheduling_tool_show_open_shifts", String(newValue));
                                        }}
                                        className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                                    />
                                    <span className="ml-2 text-sm text-gray-700">Open Shifts</span>
                                </label>
                            </div>
                        </div>
                        <button
                            onClick={() => {
                                setIsOptionsOpen(false);
                                setShowEventDisplayConfig(true);
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                            Configure Event Display
                        </button>
                        <button
                            onClick={() => {
                                setIsOptionsOpen(false);
                                navigate("/m-unavailability-form", {
                                    state: {
                                        fromCalendar: true,
                                    },
                                });
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-b-lg transition-colors"
                        >
                            Set Unavailability
                        </button>
                    </div>
                )}
            </div>
        );
    };

    // Event Display Configuration Modal
    const EventDisplayConfigModal = () => {
        const [tempConfig, setTempConfig] = useState<EventDisplayConfig>(eventDisplayConfig);

        // Reset temp config when modal opens
        useEffect(() => {
            if (showEventDisplayConfig) {
                setTempConfig(eventDisplayConfig);
            }
        }, [showEventDisplayConfig, eventDisplayConfig]);

        const handleFieldToggle = (
            eventType: keyof EventDisplayConfig,
            displayType: 'card' | 'hover',
            field: EventDisplayField,
            checked: boolean
        ) => {
            const newConfig = { ...tempConfig };
            const currentFields = [...newConfig[eventType][displayType]];
            
            if (checked) {
                if (!currentFields.includes(field)) {
                    currentFields.push(field);
                }
                newConfig[eventType][displayType] = currentFields;
            } else {
                // Don't allow unchecking if it's the last field
                if (currentFields.length > 1) {
                    newConfig[eventType][displayType] = currentFields.filter(f => f !== field);
                } else {
                    toast.error("At least one field must be selected");
                    return;
                }
            }
            
            setTempConfig(newConfig);
        };

        const handleSave = () => {
            saveEventDisplayConfig(tempConfig);
            setShowEventDisplayConfig(false);
            toast.success("Event display settings saved");
        };

        const eventTypeLabels: Record<keyof EventDisplayConfig, string> = {
            schedule: 'Schedule',
            timeoff: 'Time Off',
            unavailability: 'Unavailability',
            openShift: 'Open Shift',
        };

        return (
            <>
                {showEventDisplayConfig && (
                    <>
                        {/* Backdrop */}
                        <div 
                            className="fixed inset-0 bg-black bg-opacity-40 z-[1500]"
                            onClick={() => setShowEventDisplayConfig(false)}
                        />
                        
                        {/* Modal */}
                        <div 
                            className="fixed inset-0 z-[1600] flex items-center justify-center p-4"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div 
                                className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
                                onClick={(e) => e.stopPropagation()}
                            >
                                {/* Header */}
                                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                                    <h2 className="text-xl font-bold text-gray-900">Configure Event Display</h2>
                                    <button
                                        onClick={() => setShowEventDisplayConfig(false)}
                                        className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                                    >
                                        <X size={20} className="text-gray-500" />
                                    </button>
                                </div>

                                {/* Content */}
                                <div className="flex-1 overflow-y-auto px-6 py-4">
                                    <p className="text-sm text-gray-600 mb-6">
                                        Select which fields to display on event cards and hover tooltips. At least one field must be selected for each display type.
                                    </p>

                                    {(['schedule', 'timeoff', 'unavailability', 'openShift'] as Array<keyof EventDisplayConfig>).map((eventType) => (
                                        <div key={eventType} className="mb-8">
                                            <h3 className="text-lg font-semibold text-gray-900 mb-4">{eventTypeLabels[eventType]}</h3>
                                            
                                            {/* Card Display */}
                                            <div className="mb-6">
                                                <h4 className="text-sm font-medium text-gray-700 mb-3">Card Display</h4>
                                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                                                    {ALL_FIELDS.map((field) => {
                                                        const isChecked = tempConfig[eventType].card.includes(field.key);
                                                        return (
                                                            <label
                                                                key={field.key}
                                                                className="flex items-center cursor-pointer p-2 rounded hover:bg-gray-50"
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    checked={isChecked}
                                                                    onChange={(e) => handleFieldToggle(eventType, 'card', field.key, e.target.checked)}
                                                                    disabled={isChecked && tempConfig[eventType].card.length === 1}
                                                                    className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500 disabled:opacity-50"
                                                                />
                                                                <span className="ml-2 text-sm text-gray-700">{field.label}</span>
                                                            </label>
                                                        );
                                                    })}
                                                </div>
                                            </div>

                                            {/* Hover Display */}
                                            <div>
                                                <h4 className="text-sm font-medium text-gray-700 mb-3">Hover Tooltip Display</h4>
                                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                                                    {ALL_FIELDS.map((field) => {
                                                        const isChecked = tempConfig[eventType].hover.includes(field.key);
                                                        return (
                                                            <label
                                                                key={field.key}
                                                                className="flex items-center cursor-pointer p-2 rounded hover:bg-gray-50"
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    checked={isChecked}
                                                                    onChange={(e) => handleFieldToggle(eventType, 'hover', field.key, e.target.checked)}
                                                                    disabled={isChecked && tempConfig[eventType].hover.length === 1}
                                                                    className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500 disabled:opacity-50"
                                                                />
                                                                <span className="ml-2 text-sm text-gray-700">{field.label}</span>
                                                            </label>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Footer */}
                                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
                                    <button
                                        onClick={() => {
                                            setTempConfig(eventDisplayConfig);
                                            setShowEventDisplayConfig(false);
                                        }}
                                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleSave}
                                        className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors"
                                    >
                                        Save Settings
                                    </button>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </>
        );
    };

    return (
        <div className="w-full h-full flex flex-col bg-white">
            <CreateOpenShiftModal
                isOpen={showCreateOpenShiftModal}
                onClose={() => setShowCreateOpenShiftModal(false)}
                onSubmit={handleCreateOpenShifts}
                isSubmitting={isCreatingOpenShifts}
            />
            <CopyScheduleModal />
            {massAssignmentModalContent}
            <FilterModal />
            <AssignPublishedShiftsModal />
            <PublishConfirmationModal />
            {showPublishConfirmation && publishType === "schedules" && (
                <PublishScheduleModal
                    open={true}
                    onClose={() => {
                        setShowPublishConfirmation(false);
                        setPublishDateRange({ startDate: "", endDate: "" });
                    }}
                    defaultStartDate={publishModalDefaultDates.startDate}
                    defaultEndDate={publishModalDefaultDates.endDate}
                    groups={groups}
                    departmentOptions={publishModalDepartmentOptions}
                    siteOptions={publishModalSiteOptions}
                    loadEmployeeOptions={loadEmployeeOptionsForFilter}
                    onPublish={handlePublishScheduleFromModal}
                    isPublishing={isPublishing}
                />
            )}
            <PublishSuccessNotification />
            <EventDisplayConfigModal />
            <ConflictListModal
                open={showConflictModal}
                rows={conflictDetails}
                onClose={() => setShowConflictModal(false)}
            />
            <ScheduleAuditDrawer
                open={showAuditLogsModal}
                onClose={() => setShowAuditLogsModal(false)}
                defaultStartDate={auditLogDefaultDates.startDate}
                defaultEndDate={auditLogDefaultDates.endDate}
                dateRangeLabel={auditLogDefaultDates.label}
                loadEmployeeOptions={loadEmployeeOptionsForFilter}
            />
            {/* Top Header - Mobile Responsive */}
            <div className="border-b border-gray-200 bg-white">
                {/* Mobile: Compact Layout with Action Menu */}
                <div className="md:hidden">
                    {/* View-Only Badge */}
                    {!hasScheduleToolPrivilege && (
                        <div className="px-3 py-2 bg-yellow-50 border-b border-yellow-200">
                            <div className="flex items-center gap-2 text-yellow-800">
                                <Eye size={14} className="text-yellow-600" />
                                <span className="text-xs font-medium">View-Only Mode</span>
                            </div>
                        </div>
                    )}
                    
                    {/* Single Row: Date Navigation + Actions Menu */}
                    <div className="flex items-center justify-between px-3 py-2">
                        {/* Date Navigation */}
                    <div className="flex items-center gap-1">
                            <button
                                onClick={goToPrev}
                                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                                aria-label="Previous period"
                            >
                                <ChevronLeft size={16} />
                            </button>
                            
                        <div
                            className={`flex items-center gap-2 px-2 py-1.5 bg-gray-50 rounded-lg ${
                                viewType === 'daily' ? 'cursor-pointer' : ''
                            }`}
                            onClick={
                                viewType === 'daily'
                                    ? (e) => openDailyDatePicker(e.currentTarget as HTMLElement)
                                    : undefined
                            }
                            role={viewType === 'daily' ? 'button' : undefined}
                            tabIndex={viewType === 'daily' ? 0 : undefined}
                            onKeyDown={
                                viewType === 'daily'
                                    ? (e) => {
                                          if (e.key === 'Enter' || e.key === ' ') {
                                              e.preventDefault();
                                              openDailyDatePicker(e.currentTarget as HTMLElement);
                                          }
                                      }
                                    : undefined
                            }
                        >
                                <Calendar size={12} className="text-gray-500" />
                                <span className="text-xs font-medium text-gray-700">
                                    {getDateRangeDisplay()}
                                </span>
                            </div>
                            
                            <button
                                onClick={goToNext}
                                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                                aria-label="Next period"
                            >
                                <ChevronRight size={16} />
                            </button>
                        </div>

                        {/* Actions Menu Button */}
                        <div className="flex items-center gap-2">
                            {/* Conflict Counter */}
                            {conflictCount > 0 && (
                                <button
                                    type="button"
                                    onClick={() => setShowConflictModal(true)}
                                    className="relative p-2 rounded-lg border border-amber-300 bg-amber-50 hover:bg-amber-100 transition-colors cursor-pointer"
                                    title={`${conflictCount} ${conflictCount === 1 ? 'shift has a conflict' : 'shifts have conflicts'} in this view`}
                                >
                                    <AlertTriangle size={18} className="text-amber-600" />
                                    <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] bg-amber-500 text-white rounded-full text-[10px] font-bold">
                                        {conflictCount}
                                    </span>
                                </button>
                            )}

                            {/* Print Dropdown */}
                            <div className="relative">
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setIsPrintDropdownOpen(!isPrintDropdownOpen);
                                    }}
                                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 transition-colors"
                                    aria-label="Print options"
                                >
                                    <Printer size={18} className="text-gray-600" />
                                </button>
                                
                                {isPrintDropdownOpen && (
                                    <>
                                        <div 
                                            className="fixed inset-0 z-[998]"
                                            onClick={() => setIsPrintDropdownOpen(false)}
                                        />
                                        <div 
                                            className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-[999] min-w-[220px]"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <button
                                                onClick={() => {
                                                    setIsPrintDropdownOpen(false);
                                                    handleDownloadPDF();
                                                }}
                                                disabled={isDownloadingPDF}
                                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors border-b border-gray-100 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {isDownloadingPDF ? (
                                                    <>
                                                        <Loader2 size={14} className="animate-spin text-purple-600" />
                                                        <span>Preparing...</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Download size={14} className="text-purple-600" />
                                                        <span>Download Current View</span>
                                                    </>
                                                )}
                                            </button>
                                            
                                            <button
                                                onClick={() => {
                                                    setIsPrintDropdownOpen(false);
                                                    setShowEmployeeMonthPicker(true);
                                                }}
                                                disabled={isDownloadingPDF}
                                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-b-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <Users size={14} className="text-blue-600" />
                                                <span>Employee by Month</span>
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* Filter Badge */}
                            {activeFilters > 0 && (
                                <button 
                                    onClick={() => setShowFilterModal(true)}
                                    className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors"
                                    aria-label="Active filters"
                                >
                                    <Filter size={18} className="text-gray-600" />
                                    <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] bg-blue-600 text-white rounded-full text-[10px] font-bold">
                                        {activeFilters}
                                    </span>
                                </button>
                            )}
                            
                            {/* Actions Menu Trigger */}
                            <button
                                onClick={() => setShowMobileActionsMenu(true)}
                                className="p-2 rounded-lg border border-blue-500 text-blue-500 hover:bg-blue-50 transition-colors"
                                aria-label="Open actions menu"
                            >
                                <Menu size={20} />
                            </button>
                        </div>
                    </div>

                    {/* Mobile Actions Menu Modal (Bottom Sheet) */}
                    {showMobileActionsMenu && (
                        <>
                            {/* Backdrop */}
                            <div 
                                className="fixed inset-0 bg-black bg-opacity-40 z-[1300]"
                                onClick={() => setShowMobileActionsMenu(false)}
                            />
                            
                            {/* Bottom Sheet */}
                            <div 
                                className="fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-2xl z-[1400]"
                                style={{
                                    animation: 'slideUp 0.3s ease-out',
                                }}
                            >
                                <style>{`
                                    @keyframes slideUp {
                                        from {
                                            transform: translateY(100%);
                                            opacity: 0;
                                        }
                                        to {
                                            transform: translateY(0);
                                            opacity: 1;
                                        }
                                    }
                                `}</style>
                                {/* Handle */}
                                <div className="flex justify-center pt-3 pb-2">
                                    <div className="w-12 h-1 bg-gray-300 rounded-full"></div>
                                </div>

                                {/* Header */}
                                <div className="px-4 py-3 border-b border-gray-200">
                                    <div className="flex items-center justify-between">
                                        <h3 className="text-lg font-bold text-gray-900">Actions</h3>
                                        <button
                                            onClick={() => setShowMobileActionsMenu(false)}
                                            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                                        >
                                            <X size={20} className="text-gray-500" />
                                        </button>
                                    </div>
                                </div>

                                {/* Actions Grid */}
                                <div className="px-4 py-4 max-h-[70vh] overflow-y-auto">
                                    <div className="grid grid-cols-2 gap-3">
                                        {/* View open shifts - visible to all */}
                                        <button
                                            onClick={() => {
                                                setShowMobileActionsMenu(false);
                                                navigate("/open-shifts-master");
                                            }}
                                            className="flex flex-col items-center gap-2 p-4 bg-transparent border border-blue-500 text-blue-600 hover:bg-blue-50 rounded-xl transition-colors"
                                        >
                                            <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center">
                                                <Eye size={24} className="text-white" />
                                            </div>
                                            <span className="text-sm font-medium text-blue-700 text-center">View open shifts</span>
                                        </button>
                                        {/* Open Shift - Create */}
                                        {hasScheduleToolPrivilege && (
                                            <button
                                                onClick={() => {
                                                    setShowCreateOpenShiftModal(true);
                                                    setShowMobileActionsMenu(false);
                                                }}
                                                className="flex flex-col items-center gap-2 p-4 bg-transparent border border-blue-500 text-blue-600 hover:bg-blue-50 rounded-xl transition-colors"
                                            >
                                                <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center">
                                                    <Plus size={24} className="text-white" />
                                                </div>
                                                <span className="text-sm font-medium text-blue-700">Create Open Shift</span>
                                            </button>
                                        )}

                                        {/* Open Shift - Assign */}
                                        {hasScheduleToolPrivilege && (
                                            <button
                                                onClick={() => {
                                                    handleOpenAssignShiftsModal();
                                                    setShowMobileActionsMenu(false);
                                                }}
                                                className="flex flex-col items-center gap-2 p-4 bg-transparent border border-blue-500 text-blue-600 hover:bg-blue-50 rounded-xl transition-colors"
                                            >
                                                <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center">
                                                    <Users size={24} className="text-white" />
                                                </div>
                                                <span className="text-sm font-medium text-blue-700">Assign Open Shift</span>
                                            </button>
                                        )}

                                        {/* Copy Schedules */}
                                        {hasScheduleToolPrivilege && (
                                            <button
                                                onClick={() => {
                                                    setShowCopyScheduleModal(true);
                                                    setShowMobileActionsMenu(false);
                                                }}
                                                className="flex flex-col items-center gap-2 p-4 bg-transparent border border-blue-500 text-blue-600 hover:bg-blue-50 rounded-xl transition-colors"
                                            >
                                                <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center">
                                                    <Copy size={24} className="text-white" />
                                                </div>
                                                <span className="text-sm font-medium text-blue-700">Copy Schedules</span>
                                            </button>
                                        )}

                                        {/* Mass Assignment */}
                                        {hasScheduleToolPrivilege && (
                                            <button
                                                onClick={() => {
                                                    setShowMassAssignmentModal(true);
                                                    setShowMobileActionsMenu(false);
                                                }}
                                                className="flex flex-col items-center gap-2 p-4 bg-transparent border border-blue-500 text-blue-600 hover:bg-blue-50 rounded-xl transition-colors"
                                            >
                                                <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center">
                                                    <Users size={24} className="text-white" />
                                                </div>
                                                <span className="text-sm font-medium text-blue-700">Mass Assignment</span>
                                            </button>
                                        )}

                                        {/* Audit Logs */}
                                        {hasScheduleToolPrivilege && (
                                            <button
                                                onClick={() => {
                                                    setShowAuditLogsModal(true);
                                                    setShowMobileActionsMenu(false);
                                                }}
                                                className="flex flex-col items-center gap-2 p-4 bg-transparent border border-blue-500 text-blue-600 hover:bg-blue-50 rounded-xl transition-colors"
                                            >
                                                <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center">
                                                    <FileText size={24} className="text-white" />
                                                </div>
                                                <span className="text-sm font-medium text-blue-700">Audit Logs</span>
                                            </button>
                                        )}

                                        {/* Publish Schedules */}
                                        {hasScheduleToolPrivilege && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                    console.log('Mobile Publish Schedules clicked');
                                                    setPublishType('schedules');
                                                    setShowPublishConfirmation(true);
                                                    setShowMobileActionsMenu(false);
                                                }}
                                                disabled={isPublishing}
                                                className="flex flex-col items-center gap-2 p-4 bg-transparent border border-blue-500 text-blue-600 hover:bg-blue-50 rounded-xl transition-colors disabled:opacity-50"
                                            >
                                                <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center">
                                                    <Send size={24} className="text-white" />
                                                </div>
                                                <span className="text-sm font-medium text-blue-700">Publish Schedules</span>
                                            </button>
                                        )}

                                        {/* Publish Open Shifts */}
                                        {hasScheduleToolPrivilege && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                    console.log('Mobile Publish Open Shifts clicked');
                                                    setPublishType('open_shifts');
                                                    setShowPublishConfirmation(true);
                                                    setShowMobileActionsMenu(false);
                                                }}
                                                disabled={isPublishing}
                                                className="flex flex-col items-center gap-2 p-4 bg-transparent border border-blue-500 text-blue-600 hover:bg-blue-50 rounded-xl transition-colors disabled:opacity-50"
                                            >
                                                <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center">
                                                    <Send size={24} className="text-white" />
                                                </div>
                                                <span className="text-sm font-medium text-blue-700">Publish Open Shifts</span>
                                            </button>
                                        )}

                                        {/* Filters */}
                                        <button
                                            onClick={() => {
                                                setShowFilterModal(true);
                                                setShowMobileActionsMenu(false);
                                            }}
                                            className="flex flex-col items-center gap-2 p-4 bg-blue-50 hover:bg-blue-100 rounded-xl transition-colors relative"
                                        >
                                            <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                                                <Filter size={24} className="text-white" />
                                            </div>
                                            <span className="text-sm font-medium text-gray-900">Filters</span>
                                            {activeFilters > 0 && (
                                                <span className="absolute top-2 right-2 flex items-center justify-center min-w-[20px] h-5 bg-blue-600 text-white rounded-full text-xs font-bold px-1.5">
                                                    {activeFilters}
                                                </span>
                                            )}
                                        </button>

                                        {/* Options */}
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setIsOptionsOpen(true);
                                                setShowMobileActionsMenu(false);
                                            }}
                                            className="flex flex-col items-center gap-2 p-4 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors"
                                        >
                                            <div className="w-12 h-12 bg-gray-600 rounded-full flex items-center justify-center">
                                                <Menu size={24} className="text-white" />
                                            </div>
                                            <span className="text-sm font-medium text-gray-900">More Options</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    {/* Mobile Options Menu Modal (Bottom Sheet) */}
                    {isOptionsOpen && (
                        <>
                            {/* Backdrop */}
                            <div 
                                className="fixed inset-0 bg-black bg-opacity-40 z-[1300]"
                                onClick={() => setIsOptionsOpen(false)}
                            />
                            
                            {/* Bottom Sheet */}
                            <div 
                                data-mobile-options-menu
                                className="fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-2xl z-[1400]"
                                style={{
                                    animation: 'slideUp 0.3s ease-out',
                                }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <style>{`
                                    @keyframes slideUp {
                                        from {
                                            transform: translateY(100%);
                                            opacity: 0;
                                        }
                                        to {
                                            transform: translateY(0);
                                            opacity: 1;
                                        }
                                    }
                                `}</style>
                                {/* Handle */}
                                <div className="flex justify-center pt-3 pb-2">
                                    <div className="w-12 h-1 bg-gray-300 rounded-full"></div>
                                </div>

                                {/* Header */}
                                <div className="px-4 py-3 border-b border-gray-200">
                                    <div className="flex items-center justify-between">
                                        <h3 className="text-lg font-bold text-gray-900">Options</h3>
                                        <button
                                            onClick={() => setIsOptionsOpen(false)}
                                            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                                        >
                                            <X size={20} className="text-gray-500" />
                                        </button>
                                    </div>
                                </div>

                                {/* Options Content */}
                                <div className="px-4 py-4 max-h-[70vh] overflow-y-auto">
                                    {/* VIEW TYPE */}
                                    <div className="mb-6">
                                        <div className="text-xs font-medium text-gray-500 mb-3">VIEW TYPE</div>
                                        <div className="space-y-2">
                                            <label 
                                                className="flex items-center cursor-pointer py-2"
                                                onClick={() => {
                                                    setViewTypeWithPersistence('daily');
                                                }}
                                            >
                                                <input
                                                    type="radio"
                                                    name="viewType"
                                                    checked={viewType === 'daily'}
                                                    onChange={() => setViewTypeWithPersistence('daily')}
                                                    className="w-4 h-4 text-purple-600 border-gray-300 focus:ring-purple-500"
                                                />
                                                <span className="ml-3 text-sm text-gray-700">Daily</span>
                                            </label>
                                            <label 
                                                className="flex items-center cursor-pointer py-2"
                                                onClick={() => setViewTypeWithPersistence('weekly')}
                                            >
                                                <input
                                                    type="radio"
                                                    name="viewType"
                                                    checked={viewType === 'weekly'}
                                                    onChange={() => setViewTypeWithPersistence('weekly')}
                                                    className="w-4 h-4 text-purple-600 border-gray-300 focus:ring-purple-500"
                                                />
                                                <span className="ml-3 text-sm text-gray-700">Weekly</span>
                                            </label>
                                            <label 
                                                className="flex items-center cursor-pointer py-2"
                                                onClick={() => setViewTypeWithPersistence('monthly')}
                                            >
                                                <input
                                                    type="radio"
                                                    name="viewType"
                                                    checked={viewType === 'monthly'}
                                                    onChange={() => setViewTypeWithPersistence('monthly')}
                                                    className="w-4 h-4 text-purple-600 border-gray-300 focus:ring-purple-500"
                                                />
                                                <span className="ml-3 text-sm text-gray-700">Monthly</span>
                                            </label>
                                        </div>
                                    </div>

                                    {/* GROUP BY - Only show when not in monthly view */}
                                    {viewType !== 'monthly' && (
                                        <div className="mb-6">
                                            <div className="text-xs font-medium text-gray-500 mb-3">GROUP BY</div>
                                            <div className="space-y-2">
                                                {getGroupingOptions().map(option => (
                                                    <label 
                                                        key={option.value} 
                                                        className="flex items-center cursor-pointer py-2"
                                                        onClick={() => setGroupingTypeWithPersistence(option.value as SchedulesV3GroupByType)}
                                                    >
                                                        <input
                                                            type="radio"
                                                            name="groupingType"
                                                            checked={groupingType === option.value}
                                                            onChange={() => setGroupingTypeWithPersistence(option.value as SchedulesV3GroupByType)}
                                                            className="w-4 h-4 text-purple-600 border-gray-300 focus:ring-purple-500"
                                                        />
                                                        <span className="ml-3 text-sm text-gray-700">{option.label}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* SORT BY - Only show when not in monthly view */}
                                    {viewType !== 'monthly' && (
                                        <div className="mb-6">
                                            <div className="text-xs font-medium text-gray-500 mb-3">SORT BY</div>
                                            <div className="space-y-2">
                                                {getSortOptions().map(option => (
                                                    <label 
                                                        key={option.value} 
                                                        className="flex items-center cursor-pointer py-2"
                                                        onClick={() => setSidebarSortWithPersistence(option.value as ScheduleSidebarSortBy)}
                                                    >
                                                        <input
                                                            type="radio"
                                                            name="sidebarSortBy"
                                                            checked={sidebarSortBy === option.value}
                                                            onChange={() => setSidebarSortWithPersistence(option.value as ScheduleSidebarSortBy)}
                                                            className="w-4 h-4 text-purple-600 border-gray-300 focus:ring-purple-500"
                                                        />
                                                        <span className="ml-3 text-sm text-gray-700">{option.label}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* VIEW SCHEDULES */}
                                    <div className="mb-6">
                                        <div className="text-xs font-medium text-gray-500 mb-3">VIEW SCHEDULES</div>
                                        <div className="space-y-2">
                                            <label 
                                                className="flex items-center cursor-pointer py-2"
                                                onClick={() => setIncludeDeptWithPersistence(IncludeDeptType.MY_SCHEDULE)}
                                            >
                                                <input
                                                    type="radio"
                                                    name="includeDept"
                                                    checked={includeDept === IncludeDeptType.MY_SCHEDULE}
                                                    onChange={() => setIncludeDeptWithPersistence(IncludeDeptType.MY_SCHEDULE)}
                                                    className="w-4 h-4 text-purple-600 border-gray-300 focus:ring-purple-500"
                                                />
                                                <span className="ml-3 text-sm text-gray-700">My schedules only</span>
                                            </label>
                                            <label 
                                                className="flex items-center cursor-pointer py-2"
                                                onClick={() => setIncludeDeptWithPersistence(IncludeDeptType.MY_DEPARTMENT)}
                                            >
                                                <input
                                                    type="radio"
                                                    name="includeDept"
                                                    checked={includeDept === IncludeDeptType.MY_DEPARTMENT}
                                                    onChange={() => setIncludeDeptWithPersistence(IncludeDeptType.MY_DEPARTMENT)}
                                                    className="w-4 h-4 text-purple-600 border-gray-300 focus:ring-purple-500"
                                                />
                                                <span className="ml-3 text-sm text-gray-700">My department</span>
                                            </label>
                                            <label 
                                                className="flex items-center cursor-pointer py-2"
                                                onClick={() => setIncludeDeptWithPersistence(IncludeDeptType.ALL)}
                                            >
                                                <input
                                                    type="radio"
                                                    name="includeDept"
                                                    checked={includeDept === IncludeDeptType.ALL}
                                                    onChange={() => setIncludeDeptWithPersistence(IncludeDeptType.ALL)}
                                                    className="w-4 h-4 text-purple-600 border-gray-300 focus:ring-purple-500"
                                                />
                                                <span className="ml-3 text-sm text-gray-700">All schedules</span>
                                            </label>
                                        </div>
                                    </div>

                                    {/* FILTER OPTIONS */}
                                    <div className="mb-6">
                                        <div className="text-xs font-medium text-gray-500 mb-3">FILTER OPTIONS</div>
                                        <div className="space-y-2">
                                            <label className="flex items-center cursor-pointer py-2">
                                                <input
                                                    type="checkbox"
                                                    checked={showSchedule}
                                                    onChange={(e) => {
                                                        const newValue = e.target.checked;
                                                        setShowSchedule(newValue);
                                                        localStorage.setItem("scheduling_tool_show_schedule", String(newValue));
                                                    }}
                                                    className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                                                />
                                                <span className="ml-3 text-sm text-gray-700">Schedule</span>
                                            </label>
                                            <label className="flex items-center cursor-pointer py-2">
                                                <input
                                                    type="checkbox"
                                                    checked={showTimeoff}
                                                    onChange={(e) => {
                                                        const newValue = e.target.checked;
                                                        setShowTimeoff(newValue);
                                                        localStorage.setItem("scheduling_tool_show_timeoff", String(newValue));
                                                    }}
                                                    className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                                                />
                                                <span className="ml-3 text-sm text-gray-700">Timeoff</span>
                                            </label>
                                            <label className="flex items-center cursor-pointer py-2">
                                                <input
                                                    type="checkbox"
                                                    checked={showHoliday}
                                                    onChange={(e) => {
                                                        const newValue = e.target.checked;
                                                        setShowHoliday(newValue);
                                                        localStorage.setItem("scheduling_tool_show_holiday", String(newValue));
                                                    }}
                                                    className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                                                />
                                                <span className="ml-3 text-sm text-gray-700">Holiday</span>
                                            </label>
                                            <label className="flex items-center cursor-pointer py-2">
                                                <input
                                                    type="checkbox"
                                                    checked={showUnavailability}
                                                    onChange={(e) => {
                                                        const newValue = e.target.checked;
                                                        setShowUnavailability(newValue);
                                                        localStorage.setItem("scheduling_tool_show_unavailability", String(newValue));
                                                    }}
                                                    className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                                                />
                                                <span className="ml-3 text-sm text-gray-700">Unavailability</span>
                                            </label>
                                            <label className="flex items-center cursor-pointer py-2">
                                                <input
                                                    type="checkbox"
                                                    checked={showOpenShifts}
                                                    onChange={(e) => {
                                                        const newValue = e.target.checked;
                                                        setShowOpenShifts(newValue);
                                                        localStorage.setItem("scheduling_tool_show_open_shifts", String(newValue));
                                                    }}
                                                    className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                                                />
                                                <span className="ml-3 text-sm text-gray-700">Open Shifts</span>
                                            </label>
                                        </div>
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="space-y-2 pt-4 border-t border-gray-200">
                                        <button
                                            onClick={() => {
                                                setIsOptionsOpen(false);
                                                setShowEventDisplayConfig(true);
                                            }}
                                            className="w-full text-left px-4 py-3 text-sm text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                                        >
                                            Configure Event Display
                                        </button>
                                        <button
                                            onClick={() => {
                                                setIsOptionsOpen(false);
                                                navigate("/m-unavailability-form", {
                                                    state: {
                                                        fromCalendar: true,
                                                    },
                                                });
                                            }}
                                            className="w-full text-left px-4 py-3 text-sm text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                                        >
                                            Set Unavailability
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {/* Desktop: Original Layout */}
                <div className="hidden md:flex flex-col">
                    {/* View-Only Badge */}
                    {!hasScheduleToolPrivilege && (
                        <div className="px-4 lg:px-6 py-2 bg-yellow-50 border-b border-yellow-200">
                            <div className="flex items-center gap-2 text-yellow-800">
                                <Eye size={16} className="text-yellow-600" />
                                <span className="text-sm font-medium">View-Only Mode</span>
                                <span className="text-xs text-yellow-700 ml-1">(You can view schedules but cannot create, edit, delete, or publish)</span>
                            </div>
                        </div>
                    )}
                    
                    <div className="flex items-center justify-between px-4 lg:px-6 py-4">
                        {/* Left side - Navigation */}
                        <div className="flex items-center gap-2">
                            {/* Open Shift Dropdown - shown to all users; managers see full menu, view-only see pickup + view */}
                            <div className="relative">
                                <button
                                    data-open-shift-button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setIsOpenShiftDropdownOpen(!isOpenShiftDropdownOpen);
                                    }}
                                    className="px-3 py-2 bg-transparent border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium flex items-center gap-2 text-sm"
                                >
                                    <Plus size={14} />
                                    Open Shift
                                    {!isLoadingOpenShiftStatusCounts &&
                                        !!openShiftStatusCounts && (() => {
                                            const isManagerOrOwner = Boolean(
                                                (user as any)?.is_manager ||
                                                    (user as any)?.is_account_owner ||
                                                    (user as any)?.isManager ||
                                                    (user as any)?.isAccountOwner
                                            );
                                            const draftCount = openShiftStatusCounts?.counts?.draft ?? 0;
                                            const publishedCount = openShiftStatusCounts?.counts?.published ?? 0;
                                            const badgeValue = isManagerOrOwner
                                                ? openShiftStatusCounts?.total ?? 0
                                                : publishedCount;

                                            return isManagerOrOwner ? (
                                                <span className="relative group ml-1">
                                                    <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-semibold rounded-full bg-purple-100 text-purple-700">
                                                        {badgeValue}
                                                    </span>
                                                    <span className="hidden group-hover:block absolute left-1/2 -translate-x-1/2 top-full mt-1 whitespace-nowrap rounded-md bg-gray-900 text-white text-xs px-2 py-1 shadow-lg z-[1400]">
                                                        Draft: {draftCount} • Published: {publishedCount}
                                                    </span>
                                                </span>
                                            ) : (
                                                <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-semibold rounded-full bg-purple-100 text-purple-700">
                                                    {badgeValue}
                                                </span>
                                            );
                                        })()}
                                    <ChevronDown size={14} />
                                </button>
                                {isOpenShiftDropdownOpen && (
                                    <div data-open-shift-dropdown className="absolute left-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-[1200] min-w-[150px]">
                                        {hasScheduleToolPrivilege && (
                                            <>
                                                <button
                                                    onClick={() => {
                                                        setShowCreateOpenShiftModal(true);
                                                        setIsOpenShiftDropdownOpen(false);
                                                    }}
                                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors border-b border-gray-100 flex items-center gap-2"
                                                >
                                                    <Plus size={14} className="text-purple-600" />
                                                    Create
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        handleOpenAssignShiftsModal();
                                                        setIsOpenShiftDropdownOpen(false);
                                                    }}
                                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors border-b border-gray-100 flex items-center gap-2"
                                                >
                                                    <Users size={14} className="text-purple-600" />
                                                    Assign
                                                </button>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        e.preventDefault();
                                                        setPublishType('open_shifts');
                                                        setShowPublishConfirmation(true);
                                                        setIsOpenShiftDropdownOpen(false);
                                                    }}
                                                    disabled={isPublishing}
                                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors border-b border-gray-100 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    <Send size={14} className="text-green-600" />
                                                    Publish Open Shifts
                                                </button>
                                            </>
                                        )}
                                        {/* <button
                                            onClick={() => {
                                                setIsOpenShiftDropdownOpen(false);
                                                navigate("/open-shifts-master");
                                            }}
                                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-b-lg transition-colors flex items-center gap-2"
                                        >
                                            <Eye size={14} className="text-purple-600" />
                                            View open shifts
                                        </button> */}
                                    </div>
                                )}
                            </div>

                            {/* Manage Dropdown */}
                            {hasScheduleToolPrivilege && (
                                <div className="relative">
                                    <button
                                        data-manage-button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setIsManageDropdownOpen(!isManageDropdownOpen);
                                        }}
                                        className="px-3 py-2 bg-transparent border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium flex items-center gap-2 text-sm"
                                    >
                                        <Menu size={14} />
                                        Manage
                                        <ChevronDown size={14} />
                                    </button>
                                    
                                    {isManageDropdownOpen && (
                                        <div data-manage-dropdown className="absolute left-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-[1200] min-w-[180px]">
                                            <button
                                                onClick={() => {
                                                    setShowCopyScheduleModal(true);
                                                    setIsManageDropdownOpen(false);
                                                }}
                                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors border-b border-gray-100 flex items-center gap-2"
                                            >
                                                <Copy size={14} className="text-purple-600" />
                                                Copy Schedule
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setShowMassAssignmentModal(true);
                                                    setIsManageDropdownOpen(false);
                                                }}
                                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors border-b border-gray-100 flex items-center gap-2"
                                            >
                                                <Users size={14} className="text-purple-600" />
                                                Mass Assignment
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setShowAuditLogsModal(true);
                                                    setIsManageDropdownOpen(false);
                                                }}
                                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors border-b border-gray-100 flex items-center gap-2"
                                            >
                                                <FileText size={14} className="text-purple-600" />
                                                Audit Logs
                                            </button>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                    setPublishType('schedules');
                                                    setShowPublishConfirmation(true);
                                                    setIsManageDropdownOpen(false);
                                                }}
                                                disabled={isPublishing}
                                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-b-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <Send size={14} className="text-green-600" />
                                                Publish Schedules
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                            
                            <button
                                onClick={goToPrev}
                                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                            >
                                <ChevronLeft size={20} />
                            </button>
                            
                            <div
                                className={`flex items-center gap-2 px-4 py-2 bg-gray-50 rounded-lg min-w-[240px] lg:min-w-[280px] justify-center ${
                                    viewType === 'daily' ? 'cursor-pointer' : ''
                                }`}
                                onClick={
                                    viewType === 'daily'
                                        ? (e) => openDailyDatePicker(e.currentTarget as HTMLElement)
                                        : undefined
                                }
                                role={viewType === 'daily' ? 'button' : undefined}
                                tabIndex={viewType === 'daily' ? 0 : undefined}
                                onKeyDown={
                                    viewType === 'daily'
                                        ? (e) => {
                                              if (e.key === 'Enter' || e.key === ' ') {
                                                  e.preventDefault();
                                                  openDailyDatePicker(e.currentTarget as HTMLElement);
                                              }
                                          }
                                        : undefined
                                }
                            >
                                <Calendar size={14} className="text-gray-500" />
                                <span className="text-sm font-medium text-gray-700">
                                    {getDateRangeDisplay()}
                                </span>
                            </div>
                            
                            <button
                                onClick={goToNext}
                                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                            >
                                <ChevronRight size={20} />
                            </button>
                        </div>

                        {/* Right side - Controls */}
                        <div className="flex items-center gap-2 lg:gap-3">
                            {/* Print Dropdown */}
                            <div className="relative">
                                <button
                                    data-print-button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setIsPrintDropdownOpen(!isPrintDropdownOpen);
                                    }}
                                    className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 transition-colors"
                                >
                                    <Printer size={14} />
                                    <span className="hidden sm:inline">Print</span>
                                    <ChevronDown size={14} />
                                </button>
                                
                                {isPrintDropdownOpen && (
                                    <div 
                                        data-print-dropdown 
                                        className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-[9999] min-w-[220px]"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <button
                                            onClick={() => {
                                                setIsPrintDropdownOpen(false);
                                                handleDownloadPDF();
                                            }}
                                            disabled={isDownloadingPDF}
                                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors border-b border-gray-100 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {isDownloadingPDF ? (
                                                <>
                                                    <Loader2 size={14} className="animate-spin text-purple-600" />
                                                    <span>Preparing...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <Download size={14} className="text-purple-600" />
                                                    <span>Download Current View</span>
                                                </>
                                            )}
                                        </button>
                                        
                                        <button
                                            onClick={() => {
                                                setIsPrintDropdownOpen(false);
                                                setShowEmployeeMonthPicker(true);
                                            }}
                                            disabled={isDownloadingPDF}
                                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-b-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <Users size={14} className="text-blue-600" />
                                            <span>Employee by Month</span>
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Conflict Counter (scoped to current view + filters) */}
                            {conflictCount > 0 && (
                                <button
                                    type="button"
                                    onClick={() => setShowConflictModal(true)}
                                    className="flex items-center gap-1.5 px-3 py-2 border border-amber-300 bg-amber-50 hover:bg-amber-100 rounded-lg text-sm text-amber-700 transition-colors cursor-pointer"
                                    title={`${conflictCount} ${conflictCount === 1 ? 'shift has a conflict' : 'shifts have conflicts'} in this view`}
                                >
                                    <AlertTriangle size={14} />
                                    <span className="hidden sm:inline">Conflicts</span>
                                    <span className="flex items-center justify-center min-w-[20px] h-5 bg-amber-500 text-white rounded-full text-xs font-medium px-1.5">
                                        {conflictCount}
                                    </span>
                                </button>
                            )}

                            {/* Filter Button */}
                            <button
                                onClick={() => setShowFilterModal(true)}
                                className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 transition-colors relative"
                            >
                                <Filter size={14} />
                                <span className="hidden sm:inline">Filters</span>
                                {activeFilters > 0 && (
                                    <span className="flex items-center justify-center min-w-[20px] h-5 bg-blue-600 text-white rounded-full text-xs font-medium px-1.5">
                                        {activeFilters}
                                    </span>
                                )}
                            </button>

                            <OptionsMenu />
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content - Full Width Grid */}
            <div className="flex-1 flex flex-col overflow-hidden">
                {isSidebarLayoutBlocking ? (
                    <div className="flex flex-1 items-center justify-center bg-gray-50">
                        <div className="flex items-center gap-2 px-4 py-3 bg-white border border-gray-200 rounded-full shadow-sm text-sm font-medium text-gray-800">
                            <Loader2 className="animate-spin text-purple-600 w-5 h-5" />
                            <span>Preparing layout...</span>
                        </div>
                    </div>
                ) : isLoading ? (
                    <div className="flex flex-1 items-center justify-center bg-gray-50">
                        <div className="flex items-center gap-2 px-4 py-3 bg-white border border-gray-200 rounded-full shadow-sm text-sm font-medium text-gray-800">
                            <Loader2 className="animate-spin text-purple-600 w-5 h-5" />
                            <span>Loading schedules...</span>
                        </div>
                    </div>
                ) : (
                    <SchedulingToolWeeklyGrid
                        currentDate={viewType === 'daily' ? currentDay : viewType === 'weekly' ? currentWeek : currentMonth}
                        viewType={viewType}
                        groupingType={groupingType}
                        groups={mergedGroups}
                        schedulingConfig={schedulingConfig}
                        hasScheduleToolPrivilege={hasScheduleToolPrivilege}
                        includeDept={includeDept}
                        showHoliday={showHoliday}
                        onToggleHoliday={(show) => {
                            setShowHoliday(show);
                            localStorage.setItem("scheduling_tool_show_holiday", String(show));
                        }}
                        onGroupingTypeChange={setGroupingTypeWithPersistence}
                        getGroupingOptions={getGroupingOptions}
                        sidebarSortBy={sidebarSortBy}
                        getSortOptions={getSortOptions}
                        onSidebarSortChange={(value) => setSidebarSortWithPersistence(value as ScheduleSidebarSortBy)}
                        selectedTagCode={selectedTagCode}
                        onTagToggle={handleTagToggle}
                        attendanceTags={attendanceTags}
                        isLoadingAttendanceTags={isLoadingAttendanceTags}
                        isAttendanceTagsDropdownOpen={isAttendanceTagsDropdownOpen}
                        onAttendanceTagsDropdownToggle={setIsAttendanceTagsDropdownOpen}
                        onScheduleAdded={() => {
                            // Refresh the current view after adding a schedule
                            fetchDataForView(viewType, 
                                viewType === 'daily' ? currentDay : 
                                viewType === 'weekly' ? currentWeek : 
                                currentMonth
                            );
                        }}
                        onPublishOpenShift={() => {
                            setPublishType('open_shifts');
                            setShowPublishConfirmation(true);
                        }}
                        onCancelShiftSuccess={() => {
                            fetchDataForView(viewType,
                                viewType === 'daily' ? currentDay :
                                viewType === 'weekly' ? currentWeek :
                                currentMonth
                            );
                        }}
                        eventDisplayConfig={eventDisplayConfig}
                    />
                )}
            </div>
            
            {showDailyDatePicker &&
                createPortal(
                    <div
                        ref={datePickerContainerRef}
                        className="fixed z-[2000] bg-white border border-gray-200 rounded-xl shadow-xl p-3"
                        style={{ top: dailyDatePickerPosition.top, left: dailyDatePickerPosition.left }}
                    >
                        <DayPicker
                            mode="single"
                            selected={currentDay}
                            onSelect={(date) => {
                                if (!date) return;
                                handleDayChange(date);
                                setShowDailyDatePicker(false);
                            }}
                            month={dailyDatePickerMonth}
                            onMonthChange={(month) => setDailyDatePickerMonth(month)}
                        />
                    </div>,
                    document.body
                )}
            
            {/* Employee & Month Picker Modal for PDF */}
            {showEmployeeMonthPicker && (
                <div 
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
                    onClick={() => setShowEmployeeMonthPicker(false)}
                >
                    <div 
                        className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-900">Select Employee & Month</h3>
                            <button
                                onClick={() => setShowEmployeeMonthPicker(false)}
                                className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <X size={20} className="text-gray-500" />
                            </button>
                        </div>
                        
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Select Employee
                            </label>
                            <AsyncSelect
                                cacheOptions
                                defaultOptions
                                loadOptions={loadEmployeeOptionsForFilter}
                                onChange={(option: any) => {
                                    setSelectedEmployeeForPDF(option?.value || '');
                                }}
                                placeholder="Search employee (min 3 characters)..."
                                noOptionsMessage={({ inputValue }) =>
                                    inputValue.length < 3 ? "Type at least 3 characters to search" : "No employees found"
                                }
                                className="text-sm"
                                styles={{
                                    control: (base) => ({
                                        ...base,
                                        minHeight: '38px',
                                    }),
                                }}
                            />
                        </div>
                        
                        <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Select Month
                            </label>
                            <DayPicker
                                mode="single"
                                selected={selectedMonthForEmployeePDF}
                                onSelect={(date) => {
                                    if (date) {
                                        setSelectedMonthForEmployeePDF(date);
                                    }
                                }}
                                month={selectedMonthForEmployeePDF}
                                onMonthChange={(month) => setSelectedMonthForEmployeePDF(month)}
                            />
                        </div>
                        
                        <div className="flex gap-3">
                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setShowEmployeeMonthPicker(false);
                                }}
                                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleDownloadEmployeePDF(selectedEmployeeForPDF, selectedMonthForEmployeePDF);
                                }}
                                disabled={isDownloadingPDF || !selectedEmployeeForPDF}
                                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {isDownloadingPDF ? (
                                    <>
                                        <Loader2 size={16} className="animate-spin" />
                                        <span>Generating...</span>
                                    </>
                                ) : (
                                    <>
                                        <Download size={16} />
                                        <span>Download PDF</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default SchedulingToolMain;
